<template>
	<div class="checkout">
		<popover name="taxesAndFees" :width="300" event="hover">
			{{ 'checkout.summary.taxesAndFeesPopover' | trans }}
		</popover>

		<div class="summary-backdrop"></div>

		<!-- Checkout nav -->
		<nav class="checkout-nav d-none d-md-block" v-if="theme === 'default' && mode !== 'standalone'">
			<svg class="arrow-down" width="41px" height="10px" viewBox="0 0 41 10" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" :style="{ left: arrowDownPositionLeft + 'px' }">
				<g id="Trip-page---Draft" transform="translate(-452.000000, -134.000000)" fill="#1ABCB2">
					<path d="M452.415282,144 C463.963995,144 465.972467,134 472.5,134 C475.458429,134 477.488592,136.054111 479.899369,138.300393 C482.807777,141.01035 486.270148,144 492.584718,144 C504.133431,144 440.866569,144 452.415282,144 Z" id="nav-arrow-down" transform="translate(472.500000, 139.000000) scale(1, -1) translate(-472.500000, -139.000000) "></path>
				</g>
			</svg>

			<ul class="steps">
				<li v-for="(step, index) in steps" :class="{ 'is-completed': isStepCompleted(step), 'is-active': isStepActive(step) }">
					<i class="wa-icon-check" v-if="isStepCompleted(step)"></i>
					{{ index + 1 }}. {{ 'checkout.nav.steps.' + step | trans }}
				</li>
			</ul>
		</nav>

		<!-- Mobile checkout nav -->
		<nav class="checkout-nav mobile-checkout-nav d-md-none" v-if="theme === 'default'">
			<button type="button" class="btn btn-link btn-lg prev-button" @click="prevStep()">
				<i class="wa-icon-long-arrow-left"></i>
			</button>

			<svg class="white-loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" v-if="isSummaryLoading">
				<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
				<circle class="external-circle" cx="60" cy="60" r="50"></circle>
			</svg>

			<!-- Cart -->
			<button type="button" class="cart btn" @click.prevent="toggleMobileSummary">

				<span class="total" v-if="summary.total || summary.allow_zero_price">
					<span class="price">
						{{ summary.total | formatPrice }}
						&euro;
					</span>

					{{ 'checkout.summary.shortTotal' | trans }}
				</span>

				<i class="wa-icon-cart"></i>
			</button>

			<ul class="steps" v-if="theme === 'default' && mode !== 'standalone'">
				<li v-for="(step, index) in steps" :class="{ 'is-completed': isStepCompleted(step), 'is-active': isStepActive(step) }" v-show="isStepActive(step)">
					({{ index + 1 }}/{{ steps.length }})
				</li>
			</ul>
		</nav>

		<!-- Mobile next button -->
		<button type="button" class="btn btn-primary btn-lg next-button px-5 d-md-none mobile-next-button" v-if="validNext === true" @click="nextStep()">
			{{ 'checkout.nextButton' | trans }}
		</button>

		<div class="container-fluid">
			<slot name="breadcrumb"></slot>

			<div class="row">
				<div class="col-md-7">
					<!-- Voucher steps indicator -->
					<div class="inline-steps mb-4" v-if="theme === 'voucher'">
						<div class="steps d-flex">
							<div class="flex-fill" v-for="(step, index) in steps">
								<div class="step" :class="{ 'is-completed': isStepCompleted(step), 'is-active': isStepActive(step) }">
									<span class="count">
										{{ index + 1 }}
									</span>

									<span class="name">
										{{ 'checkout.nav.steps.' + step | trans }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Steps -->
					<div class="steps">
						<!-- Accommodation preselection -->
						<div class="accommodation_preselection" v-if="isStepActive('accommodation_preselection')">
							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="theme === 'voucher'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<!-- Select accommodation -->
							<h2>
								{{ 'checkout.steps.accommodationPreselection.selectAccommodation' | trans }}
								<svg v-if="isLoading === 'loadAccommodationPreselection'" class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
									<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
									<circle class="external-circle" cx="60" cy="60" r="50"></circle>
								</svg>
							</h2>

							<div class="list-group accommodation-list-group mt-3">
								<button type="button" class="list-group-item list-group-item-action" v-for="accommodation in accommodationPreselectionData" @click="setPreselectedAccommodation(accommodation)">
									<div class="d-flex w-100 justify-content-between align-items-center">
										<!-- Image -->
										<img class="rounded mr-3" :src="$cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=100&height=110'" :srcset="$cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=100&height=110 1x, ' + $cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=200&height=220 2x'" v-if="accommodation.image" />

										<div class="flex-grow-1">
											<!-- Recommended -->
											<span class="d-block" v-if="accommodation.is_recommended">
												<span class="badge badge-info">
													{{ 'checkout.steps.accommodation.recommended' | trans }}
												</span>
											</span>

											<!-- Name -->
											<strong class="d-block name">
												{{ accommodation.name }}
											</strong>

											<!-- Address -->
											<small class="text-muted d-block address">
												{{ accommodation.address }}
											</small>

											<!-- Stars -->
											<span class="stars" v-if="accommodation.stars > 0">
												<i class="fa fa-star" v-for="i in accommodation.stars / 1"></i>
												<i class="fa fa-star-half" v-if="accommodation.stars%1 > 0"></i>
											</span>
										</div>

										<i class="wa-icon-long-arrow-right ml-3" style="height: 16px;"></i>
									</div>
								</button>
							</div>
						</div>

						<!-- Product -->
						<div class="product" v-if="isStepActive('product')">
							<h1 v-if="theme === 'default'">
								{{ 'checkout.steps.product.title' | trans }}
							</h1>

							<!-- Quantity -->
							<h2 v-if="theme === 'default'">
								{{ 'checkout.steps.product.quantity' | trans }}
							</h2>

							<!-- Quantity btn group -->
							<div class="form-group mt-3 quantity-btn-group" v-if="theme === 'default'">
								<div class="btn-group btn-group-toggle">
									<label class="btn btn-case px-4" :for="'quantity' + quantity" :class="{ active: productQuantity == quantity }" v-for="quantity in 10">
										<input type="radio" :value="quantity" :id="'quantity' + quantity" autocomplete="off" v-model="productQuantity">
										{{ quantity }}
									</label>
								</div>
							</div>

							<div class="divider" v-if="theme === 'default'"></div>

							<!-- Select product -->
							<h2 v-if="theme === 'default'">
								{{ 'checkout.steps.product.selectProduct' | trans }}
								<svg v-if="isLoading === 'loadProducts'" class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
									<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
									<circle class="external-circle" cx="60" cy="60" r="50"></circle>
								</svg>
							</h2>
							<p class="info" v-if="theme === 'default'">
								{{ 'checkout.steps.product.selectProductInfo' | trans }}
							</p>

							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="theme === 'voucher'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<h2 v-if="theme === 'voucher'">
								Wähle die Anzahl der Übernachtungen:
							</h2>

							<div v-for="group in products">
								<h3 class="mt-4" v-if="theme === 'default'">
									{{ group.name }}
								</h3>

								<div class="list-group mt-3">
									<div class="form-check list-group-item" v-for="product in group.products" :class="{ active: productID == product.id }">
										<input type="radio" :value="product.id" v-model="productID" :id="'product' + product.id" />
										<label :for="'product' + product.id">
											<small class="d-block text-primary" v-if="product.id === 219">
												PREMIUM
											</small>

											{{ product.name }}
											<small class="subtitle" v-if="product.subtitle" v-html="product.subtitle">
												{{ product.subtitle }}
											</small>

											<small class="subtitle" v-if="product.id === 219">
												+ Gratis Sauna-Ratgeber<br />
												+ Gratis Geschenkbox<br />
												+ 1 Baum wird gepflanzt
											</small>

											<a href="/wellness-gutschein" class="btn btn-link pl-0" target="_blank" v-if="product.id === 219 && group.products.length > 1">
												Mehr erfahren
												<i class="fa fa-external-link ml-1" style="font-size: 14px;"></i>
											</a>

											<!-- Price -->
											<span class="price">
												{{ product.price | formatPrice }}
												&euro;
											</span>
										</label>
									</div>
								</div>

								<!-- Legal details -->
								<small class="d-block text-muted mt-3" v-if="product.legal_details" v-for="product in group.products">
									{{ product.legal_details }}
								</small>
							</div>

							<div v-if="theme === 'voucher' ">
								<h2 class="mt-5 mb-3">
									Wähle einen Anlass:
								</h2>

								<div class="custom-control custom-radio custom-control-inline" v-for="_design in ['general', 'birthday', 'wedding', 'christmas']">
									<input type="radio" :id="'design_' + _design" name="design" :value="_design" class="custom-control-input" v-model="design">
									<label class="custom-control-label" :for="'design_' + _design">
										<img class="rounded" style="height: 100px;" :src="$cdn.default + 'assets/images/voucher/occasions/' + _design + '.png'" />
										<strong class="d-block mt-2 text-center" v-if="_design === 'general'">
											Allgemein
										</strong>
										<strong class="d-block mt-2 text-center" v-if="_design === 'birthday'">
											Geburtstag
										</strong>
										<strong class="d-block mt-2 text-center" v-if="_design === 'wedding'">
											Hochzeit
										</strong>
										<strong class="d-block mt-2 text-center" v-if="_design === 'christmas'">
											Weihnachten
										</strong>
									</label>
								</div>

								<h2 class="mt-5 mb-3">
									Wie möchtest du den Gutschein erhalten
								</h2>

								<select class="custom-select" v-model="shipping">
									<option value="none">
										Gutschein per E-Mail
									</option>
									<option value="print_ticket">
										Gutschein per Post - 3,95 €
									</option>
									<option value="gift_box">
										Deluxe Gutscheinbox - 9,95 €
									</option>
								</select>

								<lightbox
								style="max-width: 200px;"
								class="mt-3"
								thumbnail="https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/postal-deluxe.png"
								:images="['https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/postal-deluxe.png']"
								v-if="shipping === 'gift_box'"
								></lightbox>

								<lightbox
								style="max-width: 200px;"
								class="mt-3"
								:thumbnail="'https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/postal-' + design + '.jpg'"
								:images="['https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/postal-' + design + '.png']"
								v-if="shipping === 'print_ticket'"
								></lightbox>

								<lightbox
								style="max-width: 200px;"
								class="mt-3"
								:thumbnail="'https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/voucher-mail-' + design + '.jpg'"
								:images="['https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/voucher-mail-' + design + '.png']"
								v-if="shipping === 'none'"
								></lightbox>
							</div>
						</div>

						<!-- Calendar -->
						<div class="calendar" v-if="isStepActive('calendar')">
							<slot name="cache-switch"></slot>

							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="theme === 'voucher' || steps[0] === 'accommodation_preselection'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<div class="mb-4" v-if="! isHidePersonSelection">
								<!-- Persons -->
								<h2>
									{{ 'checkout.steps.calendar.selectPersons' | trans }}
								</h2>

								<div class="row filters mt-3">
									<div class="col-md-6">
										<!-- Persons dropdown -->
										<div class="persons-dropdown dropdown" :class="{ open: showDropdown === 'persons' }">
											<button class="btn dropdown-toggle" type="button" id="dropdownMenuButton" aria-haspopup="true" aria-expanded="false" @click="togglePersonsDropdown('persons');">
												<i class="wa-icon-user" v-if="children.length === 0"></i>
												<i class="wa-icon-two-users" v-if="children.length > 0"></i>

												{{ adults }} {{ 'checkout.steps.calendar.adults' | choice(adults) }}
												<span v-if="children.length > 0">
													+ {{ children.length }} {{ 'checkout.steps.calendar.children' | choice(children.length) }}
												</span>
											</button>

											<div class="dropdown-menu" aria-labelledby="dropdownMenuButton" :class="{ show: showDropdown === 'persons' }">
												<!-- Adults -->
												<div class="adults">
													<label>
														<i class="wa-icon-user"></i>
														{{ 'nav.filterbar.adults' | trans }}
													</label>

													<div class="float-right">
														<button type="button" class="btn decrease" @click="adults = (adults > 1 ? adults - 1 : 1)">
															<i class="fa fa-minus"></i>
														</button>

														<span class="amount">{{ adults }}</span>

														<button type="button" class="btn increase" @click="adults++">
															<i class="fa fa-plus"></i>
														</button>
													</div>
												</div>

												<small class="d-block text-center text-muted px-4" v-if="!isChildren">
													{{ 'checkout.steps.calendar.noChildrenPossible' | trans }}
												</small>

												<div class="divider" v-if="isChildren"></div>

												<!-- Children -->
												<div class="children" v-if="isChildren">
													<label>
														<i class="wa-icon-two-users"></i>
														{{ 'nav.filterbar.children' | trans }}
													</label>

													<div class="float-right">
														<button type="button" class="btn decrease" @click="decreaseChildren()">
															<i class="fa fa-minus"></i>
														</button>

														<span class="amount">{{ children.length }}</span>

														<button type="button" class="btn increase" @click="increaseChildren()">
															<i class="fa fa-plus"></i>
														</button>
													</div>

													<!-- Children ages -->
													<div class="children-age" v-for="(child, index) in children" :class="{ first : index === 0 }" :key="child.id">
														<label>
															{{ 'checkout.steps.calendar.children' | choice(1) }} {{ index + 1 }}
															<small>
																{{ 'checkout.steps.calendar.childrenBirthdate' | trans }}
															</small>
														</label>

														<div class="birthdate">
															<div class="input">
																<input type="number" class="day" min="0" v-model="child.birthdate.day" :placeholder="'checkout.steps.calendar.childrenBirthdateDD' | trans" @keyup="jumpEnterBirthdate" @keydown="validateEnterBirthdate" @focus="clearBirthdateInput($event);child.birthdate.day = null;">
																<input type="number" class="month" min="0" v-model="child.birthdate.month" :placeholder="'checkout.steps.calendar.childrenBirthdateMM' | trans" @keyup="jumpEnterBirthdate" @keydown="validateEnterBirthdate" @focus="clearBirthdateInput($event);child.birthdate.month = null;">
																<input type="number" class="year" min="1940" v-model="child.birthdate.year" :placeholder="'checkout.steps.calendar.childrenBirthdateYYYY' | trans" @keyup="jumpEnterBirthdate" @keydown="validateEnterBirthdate" @focus="clearBirthdateInput($event);child.birthdate.year = null;">
															</div>

															<small class="age" v-if="(child.birthdate.day !== null && child.birthdate.day.length > 0) && (child.birthdate.month !== null && child.birthdate.month.length > 0) && (child.birthdate.year !== null && child.birthdate.year.length === 4)">
																<span v-if="calculateChildAge(child) < 18">
																	{{ calculateChildAge(child) }}
																	{{ 'checkout.steps.calendar.years' | choice(calculateChildAge(child)) }}
																</span>
																<span class="text-danger" v-if="calculateChildAge(child) >= 18">
																	{{ 'checkout.steps.calendar.childIsTooOld' | trans }}
																</span>
															</small>
														</div>
													</div>
												</div>

												<!-- Invalid persons -->
												<span class="invalid-persons d-block px-4 text-center text-danger" v-if="!isPersonsValid">
													{{ 'checkout.steps.calendar.personsNotValid' | trans }}
												</span>

												<div class="row apply-cancel">
													<div class="col-sm-6 text-center">
														<button type="button" class="btn btn-link text-dark btn-block" @click="togglePersonsDropdown('persons')">
															{{ 'checkout.steps.calendar.personsCancel' | trans }}
														</button>
													</div>

													<div class="col-sm-6 text-center">
														<button type="button" class="btn btn-link btn-block" @click="applyPersons('persons')">
															<svg v-if="isLoading === 'applyPersons'" class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
																<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
																<circle class="external-circle" cx="60" cy="60" r="50"></circle>
															</svg>
															<span v-if="!isLoading">{{ 'checkout.steps.calendar.personsApply' | trans }}</span>
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							<!-- Origin airports -->
							<div class="mb-4" v-if="steps.indexOf('flights') !== -1">
								<h2>
									<i class="far fa-plane-departure"></i>

									{{ 'checkout.steps.calendar.originAirports' | trans }}
								</h2>

								<p class="info mb-4">
									{{ 'checkout.steps.calendar.originAirportsInfo' | trans }}
								</p>

								<!-- Airports -->
								<AddAirportInput
								:trip-id="tripId"
								:exclusions="originAirports.map(airport => airport.iata_airport_code)"
                    			@addAirport="handleAddOriginAirport"
                    			ref="originAirportInput" />

								<!-- Airport cards -->
								<div class="airport-cards">
									<button
									class="btn airport-card"
									:class="{'airport-card-disabled': index > 2}"
									type="button"
									v-for="(airport, index) in originAirports"
									:key="'airport_' + index"
									@click="removeAirport(airport)">
										<!-- IATA airport code -->
										<span class="iata-code">
											{{ airport.iata_airport_code }}
										</span>

										<!-- Name -->
										<span class="name">
											{{ airport.name }}
										</span>

										<!-- Check icon -->
										<i class="far fa-trash"></i>
									</button>

									<!-- No origin airport -->
									<div class="alert alert-warning w-100" v-if="originAirports.length === 0">
										{{ 'checkout.steps.calendar.noOriginAirports' | trans }}
									</div>

									<!-- Too many origin airports -->
									<div class="alert alert-warning w-100" v-if="originAirports.length > 3">
										{{ 'checkout.steps.calendar.tooManyOriginAirports' | choice(originAirports.length - 3, { count: originAirports.length - 3}) }}
									</div>
								</div>
							</div>

							<!-- Calendar -->
							<h2>
								{{ 'checkout.steps.calendar.availableDates' | trans }}
							</h2>

							<!-- Filters -->
							<div class="d-flex filters mt-3 mb-n1">
									<!-- Nights -->
									<div class="nights-dropdown dropdown" :class="{ open: showDropdown === 'nights' }">
										<button class="btn dropdown-toggle" type="button" id="dropdownMenuButton" aria-haspopup="true" aria-expanded="false" @click="toggleDropdown('nights');">
											<span v-if="filters.nights">
												{{ filters.nights + 1 }}
												{{ 'checkout.steps.calendar.days' | choice(nights + 1) }}
											</span>

											<span v-if="! filters.nights">
												{{ 'checkout.steps.calendar.selectNights' | trans }}
											</span>
										</button>

										<div class="dropdown-menu" aria-labelledby="dropdownMenuButton" :class="{ show: showDropdown === 'nights' }">
											<button class="dropdown-item" type="button" @click="() => {
												filters.nights = null
												toggleDropdown('nights')
											}">
												<i class="fa fa-calendar-day font-weight-400 mr-1"></i>
												{{ 'checkout.steps.calendar.freeChoice' | trans }}
											</button>

											<button class="dropdown-item" type="button" :class="{ active: item.amount === filters.nights }" v-for="item in possibleNights" @click="() => {
												filters.nights = item.amount
												toggleDropdown('nights')
											}">
												{{ item.amount + 1 }}
												{{ 'checkout.steps.calendar.days' | choice(nights + 1) }}
											</button>
										</div>
									</div>
							</div>

							<svg class="loader my-3" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" v-if="isLoadingCalendar && availabilities.length === 0 && initialLoads < maxInitialLoads">
								<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
								<circle class="external-circle" cx="60" cy="60" r="50"></circle>
							</svg>

							<div class="alert alert-info mt-3" v-if="notAvailable">
								{{ 'checkout.steps.calendar.noAvailabilitiesFound' | trans }}
								<br />
								<button type="button" class="btn btn-outline-info btn-sm mt-2" @click.prevent="initCalendar">
									{{ 'checkout.steps.calendar.tryAgain' | trans }}
								</button>
							</div>

							<div class="calendar-view mt-3" v-if="availabilities.length > 0 && currentMonth !== null">
								<!-- Calendar info -->
								<div class="bg-light-teal px-4 py-3" v-if="calendarInfo">
									<p class="text-dark-primary m-0">
										<i class="fa fa-info-circle m1-"></i>
										{{ calendarInfo }}
									</p>
								</div>

								<!-- Calendar info buttons -->
								<a class="btn btn-block rounded-0 py-2 btn-mandatory-info-button mt-0" :class="['btn-' + button.color]" v-for="button in calendarInfoButtons" :href="button.url" target="_blank">
									{{ button.label }}
									<i class="fa fa-external-link-square ml-1"></i>
								</a>

								<div class="d-flex pt-3">
									<!-- Prev month (button) -->
									<div class="ml-3">
										<button type="button" class="btn btn-outline-dark calendar-control" @click="prevMonth()" :disabled="isLoading !== false || ! (parseInt(currentMonth.format('M'), 10) > parseInt(today.format('M'), 10) || parseInt(currentMonth.format('YYYY'), 10) > parseInt(today.format('YYYY'), 10))">
											<i class="fa fa-chevron-left"></i>
										</button>
									</div>

									<!-- Current month -->
									<div class="px-2 flex-grow-1">
										<span class="current-month">
											{{ currentMonth.format('MMMM YYYY') }}

											<i class="fa fa-spinner fa-spin" v-if="isLoadingCalendar"></i>
										</span>
									</div>

									<!-- Next month (button) -->
									<div class="mr-3">
										<button type="button" class="btn btn-outline-dark calendar-control" @click="nextMonth()" :disabled="isLoading !== false">
											<i class="fa fa-chevron-right"></i>
										</button>
									</div>
								</div>

								<!-- Seaons -->
								<div class="seasons px-2 pt-2 justify-content-center" v-if="currentSeasons && currentSeasons.length > 0">
									<span v-for="season in currentSeasons">
										<span class="cell-indicator" :class="'color-' + season.color"></span>

										<span class="d-flex flex-column">
											{{ season.name }}
											
											<small class="mt-n1" v-show="season.info">
												({{ season.info }})
											</small>
										</span>
									</span>
								</div>

								<div class="months">
									<div>
										<table :class="{ 'is-loading': (isLoading === 'loadCalendar' || isLoading === 'loadFilters') && availabilities.length > 0 }">
											<!-- Header -->
											<thead>
												<tr>
													<th>
														{{ 'general.shortWeekdayByNumber.1' | trans }}
													</th>
													<th>
														{{ 'general.shortWeekdayByNumber.2' | trans }}
													</th>
													<th>
														{{ 'general.shortWeekdayByNumber.3' | trans }}
													</th>
													<th>
														{{ 'general.shortWeekdayByNumber.4' | trans }}
													</th>
													<th>
														{{ 'general.shortWeekdayByNumber.5' | trans }}
													</th>
													<th class="weekend">
														{{ 'general.shortWeekdayByNumber.6' | trans }}
													</th>
													<th class="weekend">
														{{ 'general.shortWeekdayByNumber.0' | trans }}
													</th>
												</tr>
											</thead>

											<!-- Body -->
											<tbody>
												<tr v-for="(week, weekIndex) in currentCalendar">
													<!-- Fill up days -->
													<td v-for="index in week[0].date.format('E') - 1" class="space-filler">
														<span>
															{{ week[0].date.clone().subtract(week[0].date.format('E') - index, 'days').format('D') }}
														</span>
													</td>

													<td v-for="day in week"
													:class="{
														'available': (day.available && selectablePeriod === null) || (selectablePeriod !== null && selectablePeriod.contains(day.date)),
														'booked-out': day.booked_out && !day.available,
														'arrive-date': arriveDate !== null && day.date.format('YYYY-MM-DD') === arriveDate.format('YYYY-MM-DD'),
														'departure-date': departureDate !== null && day.date.format('YYYY-MM-DD') === departureDate.format('YYYY-MM-DD'),
														'travel-day': arriveDate !== null && travelPeriod !== null && travelPeriod.contains(day.date),
														'cheapest': typeof(day.cheapest) !== 'undefined' && day.cheapest === true && ! voucherCode && showCalendarPrices && (selectablePeriod === null || (selectablePeriod !== null && ! selectablePeriod.contains(day.date))),
														'additional-charge': voucherCode && ! day.cheapest && day.available && showCalendarPrices,
														'not-selectable': selectablePeriod !== null && ! selectablePeriod.contains(day.date) && day.date.format('YYYY-MM-DD') !== arriveDate.format('YYYY-MM-DD'),
														'selectable': (selectablePeriod !== null && selectablePeriod.contains(day.date)) || (arriveDate !== null && day.date.format('YYYY-MM-DD') === arriveDate.format('YYYY-MM-DD')),
													}"
														@click="() => {
														if (selectablePeriod === null) {
															setArriveDate(day)
														} else {
															setDepartureDate(day)
														}
													}">

														<!-- Close btn -->
														<div class="close-btn" v-if="arriveDate !== null && day.date.format('YYYY-MM-DD') === arriveDate.format('YYYY-MM-DD')" @click.stop="resetArriveDate()">
															<i class="wa-icon-close"></i>
														</div>

														<span :class="'color-' + day.color">
															<span class="day">
																{{ day.date.format('D') }}
															</span>

															<!-- From price -->
															<small class="price" v-if="day.available && showCalendarPrices && ! day.is_on_request && (arriveDate === null || (arriveDate !== null && day.date.format('YYYY-MM-DD') !== arriveDate.format('YYYY-MM-DD'))) && (departureDate === null || (departureDate !== null && day.date.format('YYYY-MM-DD') !== departureDate.format('YYYY-MM-DD'))) && (selectablePeriod === null || (selectablePeriod !== null && ! selectablePeriod.contains(day.date)))">
																{{ day.price | formatPrice  }}
																&euro;
															</small>

															<!-- Arrival -->
															<small class="price" v-if="arriveDate !== null && day.date.format('YYYY-MM-DD') === arriveDate.format('YYYY-MM-DD')">
																{{ 'checkout.steps.calendar.arrival' | trans }}
															</small>

															<!-- Total price -->
															<small class="price" v-if="day.available && showCalendarPrices && ! day.is_on_request && arriveDate !== null && ((selectablePeriod !== null && selectablePeriod.contains(day.date)) || (departureDate !== null && day.date.format('YYYY-MM-DD') === departureDate.format('YYYY-MM-DD'))) && day.date.format('YYYY-MM-DD') in currentDayPrices">
																{{ currentDayPrices[day.date.format('YYYY-MM-DD')] | formatPrice }}
																&euro;
															</small>

															<!-- Sold out -->
															<small class="sold-out" v-if="day.booked_out">
																{{ 'checkout.steps.calendar.soldOut' | trans }}
															</small>
														</span>
													</td>

													<!-- Fill up days -->
													<td v-for="index in 7 - week[week.length - 1].date.format('E')" class="space-filler" :class="{ 'travel-day': arriveDate !== null && travelPeriod !== null && travelPeriod.contains(week[week.length - 1].date.clone().add(index, 'days')) }">
														<span>
															{{ week[week.length - 1].date.clone().add(index, 'days').format('D') }}
														</span>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</div>

							<span class="per-person">
								&euro; {{ 'checkout.steps.calendar.pricePerPerson' | trans }}
							</span>

							<div class="row calendar-glossary px-4 mt-3" v-if="availabilities.length > 0 && currentMonth !== null && showCalendarPrices && voucherCode">
								<div class="col-xl-4 col-lg-6 mb-2">
									<span class="cell-indicator available">
										<div class="rect"></div>
										{{ 'checkout.steps.calendar.dateAvailable' | trans }}
									</span>
								</div>

								<div class="col-xl-4 col-lg-6 mb-2">
									<span class="cell-indicator additional-charge">
										<div class="rect"></div>
										{{ 'checkout.steps.calendar.dateAdditionalCharge' | trans }}
									</span>
								</div>
							</div>
						</div>

						<!-- Activities -->
						<div class="activities" v-if="isStepActive('activities')">
							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="currentStep != 'calendar' && currentStep != 'product'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<h2>
								{{ 'checkout.steps.activity.selectActivity' | trans }}
							</h2>

							<svg v-if="isLoading === 'loadActivities'" class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
								<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
								<circle class="external-circle" cx="60" cy="60" r="50"></circle>
							</svg>

							<div class="list-group mt-3">
								<!-- Optional activity -->
								<div class="form-check list-group-item" v-if="isOptionalActivity" :class="{ active: activityID == null }">
									<input type="radio" :value="null" v-model="activityID" id="activity_none" />
									<label for="activity_none">
										<div class="d-flex">
											<div class="flex-grow-1">
												<!-- Name -->
												{{ 'checkout.steps.activity.withoutActivity' | trans }}

												<small class="subtitle">
													{{ 'checkout.steps.activity.onlyHotel' | trans }}
												</small>
											</div>
										</div>
									</label>
								</div>

								<!-- Activity item -->
								<div class="form-check list-group-item" v-for="activity in possibleActivities" :class="{ active: activityID == activity.id }">
									<input type="radio" :value="activity.id" v-model="activityID" :id="'activity' + activity.id" />
									<label :for="'activity' + activity.id">
										<div class="d-flex">
											<!-- Image -->
											<div class="pr-3" v-if="activity.images.length > 0">
												<img :src="activity.images[0].url" class="rounded image" />
											</div>

											<div class="flex-grow-1">
												<!-- Name -->
												{{ activity.name }}
												<small class="subtitle" v-if="activity.description">
													{{ activity.description.substring(0, 50) + (activity.description.length > 50 ? '[...]' : '') }}
												</small>

												<!-- Included services -->
												<ul class="services included" v-if="activity.included_services.length > 0">
													<li v-for="includedService in activity.included_services">
														{{ includedService.name }}
													</li>
												</ul>

												<!-- Price -->
												<span class="price" v-if="!isVoucher && !activity.is_base_price_relative">
													<span v-if="activity.price > currentFromPrice">
														+ {{ activity.price - currentFromPrice | formatPrice }}
														&euro;
														<small>
															{{ 'checkout.perPerson' | trans }}
														</small>
													</span>

													<span v-if="activity.price <= currentFromPrice">
														{{ 'checkout.inclusive' | trans }}
													</span>
												</span>

												<span class="price" v-if="isVoucher && !activity.is_base_price_relative">
													<span>
														{{ activity.price | formatPrice }}
														&euro;
														<small>
															{{ 'checkout.perPerson' | trans }}
														</small>
													</span>
												</span>

												<span class="price" v-if="activity.is_base_price_relative">
													<span v-if="!activity.is_included">
														+ {{ activity.price | formatPrice }}
														&euro;
														<small>
															{{ 'checkout.perPerson' | trans }}
														</small>
													</span>

													<span v-if="activity.is_included">
														{{ 'checkout.inclusive' | trans }}
													</span>
												</span>
											</div>
										</div>
									</label>
								</div>
							</div>
						</div>

						<!-- Activity -->
						<div class="activity" v-if="isStepActive('activity')">
							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="currentStep != 'calendar' && currentStep != 'product'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<h2>
								{{ 'checkout.steps.activity.selectTickets' | trans }}
							</h2>

							<svg v-if="isLoading === 'loadActivity'" class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
								<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
								<circle class="external-circle" cx="60" cy="60" r="50"></circle>
							</svg>

							<div v-if="selectedActivity">
								<p class="info mb-4">
									{{ 'checkout.steps.activity.selectTicketsInfo' | trans }}
									<strong>
										{{ selectedActivity.name }}
									</strong>
								</p>

								<!-- Select date -->
								<div v-if="activityDates.length > 0">
									<h3 class="mb-3">
										{{ 'checkout.steps.activity.selectDate' | trans }}
									</h3>

									<!-- Date btn group -->
									<div class="form-group mb-0 dates" v-for="(datesGroup, groupIndex) in $lodash.chunk(activityDates, 4)">
										<div class="btn-group btn-group-toggle">
											<label class="btn btn-case" :for="'activityDate' + groupIndex + dateIndex" :class="{ active: activityDate === date.date, 'px-5': activityDates.length <= 3, 'px-4': activityDates.length <= 8, 'px-3': activityDates.length > 8, single: activityDates.length === 1 || activityDates.length % 4 === 1, cheapest: date.price <= currentFromPrice  }" v-for="(date, dateIndex) in datesGroup">
												<input type="radio" :value="date.date" :id="'activityDate' + groupIndex + dateIndex" autocomplete="off" v-model="activityDate">
												{{ $moment(date.date, 'YYYY-MM-DD').format('ddd, DD. MMM') }}
											</label>
										</div>
									</div>
								</div>

								<!-- Select time -->
								<div v-if="activityTimes.length > 0">
									<div class="divider"></div>

									<h3>
										{{ 'checkout.steps.activity.selectTime' | trans }}
									</h3>
									<!-- Time btn group -->
									<div class="form-group mt-3">
										<div class="btn-group btn-group-toggle">
											<label class="btn btn-case px-5" :for="'activityTime' + timeIndex" :class="{ active: activityTime === time, single: activityTimes.length === 1 }" v-for="(time, timeIndex) in activityTimes">
												<input type="radio" :value="time" :id="'activityTime' + timeIndex" autocomplete="off" v-model="activityTime">
												{{ $moment(time, 'HH:mm:ss').format('HH:mm') }}
											</label>
										</div>
									</div>
								</div>

								<!-- Select category -->
								<div v-if="activityCategories.length > 0">
									<div class="divider"></div>

									<h3>
										{{ 'checkout.steps.activity.selectCategory' | trans }}
									</h3>

									<div class="list-group mt-3">
										<div class="form-check list-group-item" v-for="category in activityCategories" :class="{ active: activityCategoryID == category.id, cheapest: priceForCategory(category) <= currentFromPrice }">
											<input type="radio" :value="category.id" v-model="activityCategoryID" :id="'activityCategory' + category.id" />
											<label :for="'activityCategory' + category.id">
												{{ category.name }}
												<small class="subtitle" v-if="category.subtitle">
													{{ category.subtitle }}
												</small>

												<!-- Price -->
												<span class="price" v-if="!isVoucher && !category.is_base_price_relative">
													<span v-if="priceForCategory(category) > currentFromPrice">
														+ {{ priceForCategory(category) - currentFromPrice | formatPrice }}
														&euro;
														<small>
															{{ 'checkout.perPerson' | trans }}
														</small>
													</span>

													<span v-if="priceForCategory(category) <= currentFromPrice">
														{{ 'checkout.inclusive' | trans }}
													</span>
												</span>

												<span class="price" v-if="isVoucher && !category.is_base_price_relative">
													<span>
														{{ priceForCategory(category) | formatPrice }}
														&euro;
														<small>
															{{ 'checkout.perPerson' | trans }}
														</small>
													</span>
												</span>

												<span class="price" v-if="category.is_base_price_relative">
													<span v-if="!category.is_included">
														+ {{ priceForCategory(category) | formatPrice }}
														&euro;
														<small>
															{{ 'checkout.perPerson' | trans }}
														</small>
													</span>

													<span v-if="category.is_included">
														{{ 'checkout.inclusive' | trans }}
													</span>
												</span>
											</label>
										</div>
									</div>
								</div>

								<!-- Ticket fee -->
								<div class="d-flex mt-4" v-if="ticketFee">
									<i class="fa fa-info-circle mr-2 text-primary" style="font-size: 20px;"></i>

									<div class="flex-grow-1">
										<strong class="d-block">
											{{ ticketFee.name }}

											<span class="ml-2 font-weight-400">
												{{ ticketFee.amount | formatPrice }}
												&euro;
											</span>
										</strong>

										<p class="text-muted" v-if="ticketFee.description">
											{{ ticketFee.description }}
										</p>
									</div>
								</div>
							</div>
						</div>

						<!-- Accommodation -->
						<div class="accommodation" v-if="isStepActive('accommodation')">
							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="currentStep != 'calendar' && currentStep != 'product'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<h2>
								{{ 'checkout.steps.accommodation.selectAccommodation' | trans }}
							</h2>

							<p class="info mb-4">
								{{ 'checkout.steps.accommodation.selectAccommodationInfo' | trans }}
							</p>

							<!-- Destinations -->
							<h3 v-if="accommodationDestinations.length > 1">
								{{ 'checkout.steps.accommodation.destinations.heading' | trans }}
							</h3>
							<div class="destinations" v-if="accommodationDestinations.length > 1">
								<!-- Arrow left -->
								<span class="arrow arrow-left" @click="scrollDestinations('left')">
									<i class="wa-icon-long-arrow-left"></i>
								</span>

								<!-- Arrow right -->
								<span class="arrow arrow-right" @click="scrollDestinations('right')">
									<i class="wa-icon-long-arrow-right"></i>
								</span>

								<div class="destination-items">
									<!-- All -->
									<div class="destination-card" @click="accommodationSelectedDestination = null" :class="{ active: accommodationSelectedDestination === null }">

										<!-- Name -->
										<span class="name">
											{{ 'checkout.steps.accommodation.destinations.all' | trans }}
										</span>

										<!-- Description -->
										<span class="description">
											{{ 'checkout.steps.accommodation.destinations.allInfo' | trans({ count: possibleAccommodations.length }) }}
										</span>
									</div>

									<!-- Destination cards -->
									<div class="destination-card" @click="accommodationSelectedDestination = destination" v-for="destination in accommodationDestinations" :class="{ active: accommodationSelectedDestination && accommodationSelectedDestination.id === destination.id }">
										<!-- Image -->
										<img :src="destination.image + '?width=140&height=100'" :srcset="destination.image + '?width=140&height=100 1x, ' + destination.image + '?width=280&height=200 2x'" v-if="destination.image" class="img-fluid" />

										<!-- Name -->
										<span class="name">
											{{ destination.name }}
										</span>

										<!-- Description -->
										<span class="description" v-if="destination.description">
											{{ destination.description }}
										</span>
									</div>
								</div>
							</div>

							<div class="divider mb-4" v-if="accommodationDestinations.length > 1"></div>

							<div class="d-flex flex-column flex-sm-row">
								<div class="mr-auto">
									<!-- Board types -->
									<div class="dropdown mb-3" v-if="accommodationBoardTypes.length > 0">
										<button class="btn btn-secondary dropdown-toggle" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
											{{ 'checkout.steps.accommodation.boardType' | trans }}:
											<span v-if="accommodationBoardType">
												{{ accommodationBoardType.name }}
											</span>
											<span v-if="! accommodationBoardType">
												{{ 'checkout.steps.accommodation.boardTypeAll' | trans }}
											</span>
										</button>
										<div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
											<a class="dropdown-item" href="#" @click.prevent="setAccommodationBoardType(null)">
												{{ 'checkout.steps.accommodation.boardTypeAll' | trans }}
											</a>

											<a class="dropdown-item" href="#" v-for="(boardType, boardTypeIndex) in accommodationBoardTypes" @click.prevent="setAccommodationBoardType(boardType)" :key="'board_type_' + boardTypeIndex">
												{{ boardType.name }}
											</a>
										</div>
									</div>

									<!-- Loading -->
									<div class="loading" style="clear: both;">
										<!-- Animation -->
										<svg v-if="isLoading === 'loadAccommodation'" class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
											<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
											<circle class="external-circle" cx="60" cy="60" r="50"></circle>
										</svg>

										<strong v-if="sortedAccommodations.length == 1">
											{{ 'checkout.steps.accommodation.loadingSingle' | trans({ total: sortedAccommodations.length }) }}
										</strong>
										<strong v-if="sortedAccommodations.length > 1 || sortedAccommodations.length === 0">
											{{ 'checkout.steps.accommodation.loadingMultiple' | trans({ total: sortedAccommodations.length }) }}
										</strong>

										<span v-if="isLoading === 'loadAccommodation'">
											{{ 'checkout.steps.accommodation.loadingInfo' | trans }}
										</span>
									</div>
								</div>

								<!-- Sorting options -->
								<div class="position-relative">
									<button class="btn btn-link btn-sm text-dark dropdown-toggle pt-0" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
										{{ 'checkout.steps.accommodation.sortingOptions.' + accommodationSorting | trans }}
									</button>
									<div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
										<a class="dropdown-item" href="#" v-for="option in accommodationSortingOptions" v-if="option !== 'distanceAsc' || (option === 'distanceAsc' && possibleActivities.length > 0)" @click.prevent="setAccommodationSorting(option)">
											{{ 'checkout.steps.accommodation.sortingOptions.' + option | trans }}
										</a>
									</div>
								</div>
							</div>

							<!-- List -->
							<div class="list-group mt-3">
								<!-- Loading placeholder -->
								<div class="form-check list-group-item text-center" v-if="this.possibleAccommodations.length === 0 && isLoading === 'loadAccommodation'">
									<div class="ph-item">
										<div class="ph-col-12">
											<div class="ph-row">
												<div class="ph-col-6 big"></div>
												<div class="ph-col-4 empty"></div>
												<div class="ph-col-2 big"></div>

												<div class="ph-col-4"></div>
												<div class="ph-col-8 empty"></div>
											</div>
										</div>
										<div class="ph-col-6">
											<div class="ph-row">
												<div class="ph-col-12 empty"></div>

												<div class="ph-col-8 big"></div>
												<div class="ph-col-4 empty"></div>

												<div class="ph-col-10"></div>
												<div class="ph-col-2 empty"></div>

												<div class="ph-col-8"></div>
												<div class="ph-col-4 empty"></div>

												<div class="ph-col-8"></div>
												<div class="ph-col-4 empty"></div>

												<div class="ph-col-12 empty"></div>

												<div class="ph-col-2 big"></div>
											</div>
										</div>
										<div class="ph-col-6">
											<div class="ph-picture"></div>
										</div>
									</div>
								</div>

								<!-- No hotels found -->
								<div class="list-group-item list-group-item-danger" v-if="sortedAccommodations.length === 0 && !isLoading">
									{{ 'checkout.steps.accommodation.noHotelsFound' | trans }}
								</div>

								<!-- Accommodation -->
								<div class="form-check list-group-item" v-for="accommodation in sortedAccommodations" :class="{ active: accommodationID == accommodation.id, 'is-recommended': accommodation.is_recommended }" v-if="(accommodationID == accommodation.id || !isHideOtherAccommodations) && accommodationHasBoardType(accommodation)">
									<input type="radio" :value="accommodation.id" v-model="accommodationID" :id="'accommodation' + accommodation.id" :disabled="isRunningRateCheck" />
									<label :for="'accommodation' + accommodation.id">
										<div class="row">
											<div class="col-md-5 image d-md-none">
												<img class="img-fluid" :src="$cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=320&height=140'" :srcset="$cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=320&height=140 1x, ' + $cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=640&height=280 2x'" />
											</div>

											<div class="col-md-7">
												<!-- Name -->
												<span class="name">
													<span class="d-block" v-if="accommodation.is_recommended">
														<span class="badge badge-info">
															{{ 'checkout.steps.accommodation.recommended' | trans }}
														</span>
													</span>

													{{ accommodation.name }}

													<span class="stars" v-if="accommodation.stars > 0">
														<i class="fa fa-star" v-for="i in accommodation.stars / 1"></i>
														<i class="fa fa-star-half" v-if="accommodation.stars%1 > 0"></i>
													</span>
												</span>

												<!-- Address -->
												<span class="location" v-if="accommodation.address">
													{{ accommodation.address }}
												</span>

												<!-- Location -->
												<span class="location" v-if="possibleActivities.length > 0">
													<i class="wa-icon-map-pointer"></i>
													{{ 'checkout.steps.accommodation.distanceToActivity' | trans({ distance: distanceInKm(accommodation.location, possibleActivities[0].location).replace('.', ','), activity: possibleActivities[0].name }) }}
												</span>

												<!-- Price -->
												<span class="price">
													<span>
														{{ accommodation.price | formatPrice }}
														&euro;
														<small>
															{{ 'checkout.perPerson' | trans }}
														</small>
													</span>
												</span>

												<!-- Select accommodation -->
												<button type="button" class="btn btn-primary btn-lg mt-3 select-accommodation" @click.prevent="accommodationID = accommodation.id" :disabled="isRunningRateCheck">
													<i class="wa-icon-check" v-if="accommodationID == accommodation.id"></i>
													{{ 'checkout.steps.accommodation.selectAccommodation' | trans }}
												</button>

												<!-- More infos -->
												<button type="button" class="btn btn-outline-primary btn-lg mt-3 accommodation-infos" @click.prevent="showAccommodationDetails('#accommodation-details-' + accommodation.id)">
													{{ 'checkout.steps.accommodation.moreInfos' | trans }}
												</button>
											</div>

											<div class="col-md-5 image d-none d-md-block">
												<div>
													<img class="img-fluid" :src="$cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=320&height=240'" :srcset="$cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=320&height=240 1x, ' + $cdn.default + 'hotels/' + accommodation.guid + '/' + accommodation.image + '?width=640&height=480 2x'" />
												</div>
											</div>
										</div>

										<!-- Rooms -->
										<div class="list-group rooms-list mt-3" v-if="accommodationID == accommodation.id">
											<div class="list-group-item list-group-header list-group-item-primary text-center">
												{{ 'checkout.steps.accommodation.availableRooms' | trans }}
											</div>

											<div class="form-check list-group-item" v-for="room in accommodation.rooms" :class="{ active: accommodationRoomID == room.id }" v-if="accommodationBoardType === null || (accommodationBoardType && room.available_board_types && room.available_board_types.indexOf(accommodationBoardType.id) >= 0)">

												<input type="radio" :value="room.id" v-model="accommodationRoomID" :id="'room' + room.id" :disabled="isRunningRateCheck" />
												<label :for="'room' + room.id">
													<svg v-if="isLoading === 'loadPersons'" class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
														<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
														<circle class="external-circle" cx="60" cy="60" r="50"></circle>
													</svg>

													<!-- Price -->
													<span class="price text-right d-none d-md-block">
														<!-- Cache price -->
														<span v-if="(rateCheckData !== null && accommodationRoomID != room.id) || rateCheckData === null">
															{{ room.price | formatPrice }}
														</span>

														<!-- Checked rate -->
														<span v-if="rateCheckData !== null && accommodationRoomID == room.id">
															{{ rateCheckData.per_person_price | formatPrice }}
														</span>

														&euro;
														<small>
															{{ 'checkout.perPerson' | trans }}
														</small>

														<small class="d-block text-right pb-3" v-if="isRunningRateCheck && accommodationRoomID == room.id">
															<svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
																<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
																<circle class="external-circle" cx="60" cy="60" r="50"></circle>
															</svg>

															{{ 'checkout.steps.accommodation.priceCheckRunning' | trans }}
														</small>

														<small class="d-block text-right pb-3 text-success" v-if="rateCheckData !== null && rateCheckData.approved === true && accommodationRoomID == room.id">
															<i class="fa fa-check"></i>
															{{ 'checkout.steps.accommodation.priceCheckConfirmed' | trans }}
														</small>
													</span>

													<!-- Name -->
													<span class="name">
														{{ room.name }}
													</span>

													<!-- Board type -->
													<strong class="d-block" v-if="room.board_type">
														<i :class="room.board_type.icon" v-if="room.board_type.icon"></i>

														{{ room.board_type.name }}
													</strong>

													<!-- First rate amenities -->
													<strong class="d-block" v-if="room.rates && room.rates.length > 0 && room.rates[0].amenities && room.rates[0].amenities.length > 0">
														{{  room.rates[0].amenities.map((amenity) => amenity.name).join(', ') }}
													</strong>

													<!-- Cancellation rules -->
													<div v-if="room.cancellation_rules" class="mt-2">
														<span class="refundable" v-if="room.is_refundable !== null && room.is_refundable">
															<i class="wa-icon-check-circle mr-1"></i>
															{{ 'checkout.summary.refundable' | trans }}

															<i class="fa fa-info-circle ml-1"
															data-toggle="tooltip"
															:title="'checkout.summary.refundableUntil' | trans({ date: $moment(room.refundable_until).format('DD.MM.YYYY'), time: $moment(room.refundable_until).format('HH:mm') })"
															v-if="room.refundable_until"></i>
														</span>
														<span class="non-refundable" v-if="room.is_refundable !== null && ! room.is_refundable">
															<i class="wa-icon-money-exchange mr-1"></i>
															{{ 'checkout.summary.nonRefundable' | trans }}
														</span>
													</div>

													<!-- Amount -->
													<span class="badge badge-primary" v-if="room.amount <= 5">
														{{ 'checkout.steps.accommodation.amountAvailable' | trans({ amount: room.amount }) }}
													</span>

													<!-- Price -->
													<span class="price text-right d-md-none">
														<!-- Without voucher -->
														<span v-if="!isVoucher">
															<!-- Cache price -->
															<span v-if="(rateCheckData !== null && accommodationRoomID != room.id) || rateCheckData === null">
																<span v-if="room.price > currentFromPrice">
																	<span v-if="currentFromPrice > 0">+</span>
																	{{ room.price - currentFromPrice | formatPrice }}
																	&euro;
																	<small>
																		{{ 'checkout.perPerson' | trans }}
																	</small>
																</span>

																<span v-if="room.price <= currentFromPrice">
																	{{ 'checkout.inclusive' | trans }}
																</span>
															</span>

															<!-- Checked rate -->
															<span v-if="rateCheckData !== null && accommodationRoomID == room.id">
																<span v-if="rateCheckData.per_person_price > currentFromPrice">
																	<span v-if="currentFromPrice > 0">+</span>
																	{{ rateCheckData.per_person_price - currentFromPrice | formatPrice }}
																	&euro;
																	<small>
																		{{ 'checkout.perPerson' | trans }}
																	</small>
																</span>

																<span v-if="rateCheckData.per_person_price <= currentFromPrice">
																	{{ 'checkout.inclusive' | trans }}
																</span>
															</span>
														</span>

														<!-- With voucher -->
														<span v-if="isVoucher">
															<span v-if="(rateCheckData !== null && accommodationRoomID != room.id) || rateCheckData === null">
																{{ room.price | formatPrice }}
															</span>

															<span v-if="rateCheckData !== null && accommodationRoomID == room.id">
																{{ rateCheckData.per_person_price | formatPrice }}
															</span>

															&euro;
															<small>
																{{ 'checkout.perPerson' | trans }}
															</small>
														</span>

														<small class="d-block text-right pb-3" v-if="isRunningRateCheck && accommodationRoomID == room.id">
															<svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
																<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
																<circle class="external-circle" cx="60" cy="60" r="50"></circle>
															</svg>
															{{ 'checkout.steps.accommodation.priceCheckRunning' | trans }}
														</small>

														<small class="d-block text-right pb-3 text-success" v-if="rateCheckData !== null && rateCheckData.approved === true && accommodationRoomID == room.id">
															<i class="fa fa-check"></i>
															{{ 'checkout.steps.accommodation.priceCheckConfirmed' | trans }}
														</small>
													</span>

													<!-- Check rate alert (approved) -->
													<div class="alert alert-primary mt-3" v-if="rateCheckData !== null && rateCheckData.approved === false && rateCheckData.available === true && accommodationRoomID == room.id">
														<i class="fal fa-info-square mr-2" style="font-size: 24px;"></i>
														{{ 'checkout.steps.accommodation.priceCheckChanged' | trans }}
														<strong>
															{{ rateCheckData.per_person_price | formatPrice }}
															&euro;
														</strong>

														{{ 'checkout.steps.accommodation.priceCheckPerPerson' | trans }}
													</div>

													<!-- Check rate alert (not available) -->
													<div class="alert alert-danger mt-3" v-if="rateCheckData !== null && rateCheckData.approved === false && rateCheckData.available === false && accommodationRoomID == room.id">
														<i class="fal fa-info-square mr-2" style="font-size: 24px;"></i>
														{{ 'checkout.steps.accommodation.priceCheckNotAvailable' | trans }}
													</div>
												</label>
											</div>
										</div>
									</label>
								</div>

								<!-- Show all hotels -->
								<button type="button" class="btn btn-outline-primary btn-lg mx-auto mt-3" @click.prevent="isHideOtherAccommodations = false" v-if="isHideOtherAccommodations && possibleAccommodations.length > 1">
									{{ 'checkout.steps.accommodation.showAllHotels' | choice(possibleAccommodations.length - 1) }}
								</button>
							</div>
						</div>

						<!-- Flights -->
						<div class="flights" v-if="isStepActive('flights')">
							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<h2>
								{{ 'checkout.steps.flights.selectFlight' | trans }}
							</h2>

							<flights-list
							ref="flightsList"
							:initial-origin-airports="originAirports"
							:is-loading="isLoading"></flights-list>
						</div>

						<!-- Extras -->
						<div class="extras" v-if="isStepActive('extras')">
							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="currentStep != 'calendar' && currentStep != 'product'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<h2>
								{{ 'checkout.steps.extras.title' | trans }}
							</h2>

							<p class="info mb-4">
								{{ 'checkout.steps.extras.subtitle' | trans }}
							</p>

							<div class="text-center" v-if="isLoading === 'loadExtras'">
								<svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
									<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
									<circle class="external-circle" cx="60" cy="60" r="50"></circle>
								</svg>
							</div>

							<div v-if="extras">
								<h3>
									<i class="fa fa-gift"></i>
									{{ 'checkout.steps.confirmation.shipping' | trans }}
								</h3>
								<p class="info mb-3">
									{{ 'checkout.steps.confirmation.shippingInfo' | trans }}
								</p>

								<div class="list-group mt-3">
									<div class="form-check list-group-item" :class="{ active: shipping === shippingOption.type }" v-for="(shippingOption, shippingIndex) in extras.shipping_options">
										<input type="radio" :value="shippingOption.type" v-model="shipping" :id="'shippingOption' + shippingIndex" />
										<label :for="'shippingOption' + shippingIndex">
											<!-- Price -->
											<span class="price text-right">
												<span v-if="shippingOption.price > 0">
													+
													{{ shippingOption.price | formatPrice }}
													&euro;
												</span>

												<span v-if="shippingOption.price === 0">
													{{ 'checkout.free' | trans }}
												</span>
											</span>

											<!-- Name -->
											{{ shippingOption.name }}

											<!-- Subtitle -->
											<small class="d-block" v-if="shippingOption.subtitle">
												{{ shippingOption.subtitle }}
											</small>

											<!-- Shipping info -->
											<small class="d-block text-primary" v-if="shipping === shippingOption.type && shippingOption.shipping_info">
												{{ shippingOption.shipping_info }}
											</small>

											<!-- Shipping fee -->
											<small class="d-block" v-if="shipping === shippingOption.type && shippingOption.shipping_fee > 0">
												({{ 'checkout.steps.confirmation.plus' | trans }}
												{{ shippingOption.shipping_fee | formatPrice }}
												&euro;
												{{ 'checkout.steps.confirmation.shippingFee' | trans }})
											</small>
										</label>
									</div>
								</div>

								<!-- Shipping variants -->
								<div class="shipping-variants mt-5" v-if="shippingVariants && shippingVariants.length > 0">
									<h3>
										{{ 'checkout.steps.confirmation.selectGiftBox' | trans }}
									</h3>

									<div class="list-group mt-3">
										<div class="form-check list-group-item" :class="{ active: shippingVariantID === variant.id }" v-for="(variant, variantIndex) in shippingVariants">
											<input type="radio" :value="variant.id" v-model="shippingVariantID" :id="'shippingVariant' + variantIndex" />
											<label :for="'shippingVariant' + variantIndex">
												<!-- Price -->
												<span class="price text-right">
													<span v-if="variant.price - currentShippingOption.price > 0">
														+
														{{ variant.price - currentShippingOption.price | formatPrice }}
														&euro;
													</span>
												</span>

												<!-- Image -->
												<img class="box" :src="$cdn.default + 'shipping-variants/' + variant.image + '?width=100'" :srcset="$cdn.default + 'shipping-variants/' + variant.image + '?width=100 1x,' + $cdn.default + 'shipping-variants/' + variant.image + '?width=200 2x'" v-if="variant.image" />

												<!-- Name -->
												{{ variant.name }}

												<!-- Shipping info -->
												<small class="d-block text-primary" v-if="shippingVariantID === variant.id && variant.shipping_info && currentShippingOption.shipping_info !== variant.shipping_info">
													{{ variant.shipping_info }}
												</small>

												<!-- Counter -->
												<small class="d-block text-primary" v-if="variant.has_counter">
													<i class="fa fa-boxes"></i>
													<span v-if="variant.stock >= 200">
														{{ 'checkout.steps.confirmation.shippingStock.default' | trans }}
													</span>
													<span v-if="variant.stock < 200">
														{{ 'checkout.steps.confirmation.shippingStock.counter' | trans({count: variant.stock}) }}
													</span>
												</small>

												<!-- Shipping fee -->
												<small class="d-block" v-if="shippingVariantID === variant.id && variant.shipping_fee > 0 && currentShippingOption.shipping_fee < variant.shipping_fee">
													({{ 'checkout.steps.confirmation.plus' | trans }}
													{{ variant.shipping_fee | formatPrice }}
													&euro;
													{{ 'checkout.steps.confirmation.shippingFee' | trans }})
												</small>
											</label>
										</div>
									</div>
								</div>
							</div>

							<p v-if="! isLoading">
								<small class="text-muted">
									{{ 'checkout.steps.extras.additionalInfo' | trans }}
								</small>
							</p>
						</div>

						<!-- Additional fields -->
						<div class="additional_fields" v-if="isStepActive('additional_fields')">
							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="currentStep != 'calendar' && currentStep != 'product'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<h2 v-if="isLoading !== 'loadAdditionalFields' && (! additionalFieldOptions || (additionalFieldOptions && ! additionalFieldOptions.title))">
								{{ 'checkout.steps.additionalFields.title' | trans }}
							</h2>

							<h2 v-if="isLoading !== 'loadAdditionalFields' && additionalFieldOptions && additionalFieldOptions.title">
								{{ additionalFieldOptions.title }}
							</h2>

							<p class="info mb-4" v-if="isLoading !== 'loadAdditionalFields' && (! additionalFieldOptions || (additionalFieldOptions && ! additionalFieldOptions.subtitle))">
								{{ 'checkout.steps.additionalFields.subtitle' | trans }}
							</p>

							<p class="info mb-4" v-if="isLoading !== 'loadAdditionalFields' && additionalFieldOptions && additionalFieldOptions.subtitle">
								{{ additionalFieldOptions.subtitle }}
							</p>

							<!-- Info -->
							<div v-html="additionalFieldOptions.info" v-if="additionalFieldOptions && additionalFieldOptions.info"></div>

							<!-- Add button for multiple usage -->
							<button type="button" class="btn btn-primary btn-lg mb-4" @click.prevent="addAdditionalValue" v-if="additionalFieldOptions && additionalFieldOptions.multiple_name && additionalFieldOptions.is_multiple">
								<i class="fa fa-plus mr-1"></i>
								{{ additionalFieldOptions.multiple_name }}
							</button>

							<!-- Errors -->
							<div class="alert alert-danger additional-field-errors" v-if="additionalFieldErrors && Object.keys(additionalFieldErrors).length > 0">
								<ul class="my-0" v-for="errorKey in Object.keys(additionalFieldErrors)">
									<li v-for="error in additionalFieldErrors[errorKey]">
										{{ error }}
									</li>
								</ul>
							</div>

							<!-- Additional fields -->
							<div class="card border my-2" v-for="(additionalValue, additionalValueIndex) in additionalValues">
								<h4 class="card-header" v-if="additionalFieldOptions && additionalFieldOptions.single_item_title">
									{{ additionalFieldOptions.single_item_title }}
									{{ additionalValueIndex + 1 }}
								</h4>

								<div class="card-body">
									<div class="form-group" v-for="additionalField in additionalFields">
										<!-- Label -->
										<label :for="'additional_field_' + additionalField.id + '_' + additionalValueIndex">
											{{ additionalField.label }}
											<span class="required" v-if="additionalField.is_required">*</span>
										</label>

										<!-- Type: text -->
										<input type="text" class="form-control" v-model="additionalValue[additionalField.name]" v-if="additionalField.type === 'text'" :placeholder="additionalField.placeholder" @change="() => { if (additionalField.coupon_campaign_id) { checkDiscountCode(additionalValue[additionalField.name]) } }" />

										<!-- Type: select -->
										<select type="text" class="custom-select" v-model="additionalValue[additionalField.name]" v-if="additionalField.type === 'select'" :placeholder="additionalField.placeholder">
											<!-- Placeholder -->
											<option disabled selected v-if="additionalField.placeholder">
												{{ additionalField.placeholder }}
											</option>

											<!-- Options -->
											<option :value="value.value" v-for="value in additionalField.values">
												{{ value.label }}
											</option>
										</select>

										<!-- Type: boolean -->
										<div class="custom-control custom-checkbox px-0" v-if="additionalField.type === 'boolean'">
											<input type="checkbox" class="custom-control-input" :id="'additional_field_checkbox_' + additionalField.id + '_' + additionalValueIndex" v-model="additionalValue[additionalField.name]" />

											<label class="custom-control-label" :for="'additional_field_checkbox_' + additionalField.id + '_' + additionalValueIndex">
												{{ additionalField.placeholder }}
											</label>
										</div>

										<!-- Description -->
										<small class="d-block mt-2 text-muted">
											{{ additionalField.description }}
										</small>
									</div>

									<!-- Remove additional value -->
									<button type="button" class="btn btn-outline-danger" style="font-size: 13px;" @click.prevent="removeAdditionalValue(additionalValueIndex)">
										<i class="fa fa-trash mr-1"></i>
										{{ additionalFieldOptions.single_item_title }}
										löschen
									</button>
								</div>
							</div>

							<div class="text-center" v-if="isLoading === 'loadAdditionalFields'">
								<svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
									<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
									<circle class="external-circle" cx="60" cy="60" r="50"></circle>
								</svg>
							</div>
						</div>

						<!-- Confirmation -->
						<div class="confirmation" v-if="isStepActive('confirmation')">
							<!-- Back button -->
							<button type="button" class="btn btn-default title-prev-button" @click="prevStep()" v-if="currentStep != 'calendar' && currentStep != 'product'" :title="'checkout.prevButton' | trans">
								<i class="wa-icon-long-arrow-left"></i>
							</button>

							<!-- Inline mobile summary -->
							<div class="summary inline d-md-none mt-0 mb-5">
								<h3 class="text-center">
									{{ 'checkout.summary.title' | trans }}
								</h3>

								<!-- Deal info -->
								<div class="deal-info">
									<div class="d-lg-none mb-3">
										<slot name="deal-image-top"></slot>
									</div>

									<div class="d-flex">
										<div class="w-100">
											<h3>
												<slot name="deal-name"></slot>
											</h3>

											<p>
												{{ adults + children.length }} {{ 'checkout.steps.calendar.persons' | choice(adults + children.length) }}
												<span v-if="nights !== null">
													&bull;
													{{ nights }} {{ 'checkout.steps.calendar.nights' | choice(nights) }}
												</span>
											</p>
										</div>
										<div class="flex-shrink-1 d-none d-lg-block" v-if="theme !== 'voucher'">
											<slot name="deal-image"></slot>
										</div>
										<div class="flex-shrink-1 d-none d-lg-block" style="max-width: 150px;" v-if="theme === 'voucher'">
											<img class="rounded" style="max-width: 100% !important;" :src="'https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/postal-' + design + '.jpg'" v-if="shipping === 'print_ticket'" />

											<img class="rounded" style="max-width: 100% !important;" :src="'https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/postal-deluxe.png'" v-if="shipping === 'gift_box'" />

											<img class="rounded" style="max-width: 100% !important;" :src="'https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/voucher-mail-' + design + '.jpg'" v-if="shipping === 'none'" />
										</div>
									</div>
								</div>

								<!-- Included services -->
								<div class="divider" v-if="theme !== 'voucher' && $slots['included-services']"></div>
								<button class="btn btn-link btn-lg collapsed included-services-toggler" type="button" data-toggle="collapse" data-target="#included_services" v-if="theme !== 'voucher' && $slots['included-services']">
									<i class="fa fa-chevron-right"></i>
									<i class="fa fa-chevron-down"></i>
									{{ 'checkout.summary.includedServices' | trans }}
								</button>
								<div class="collapse" id="included_services" v-if="theme !== 'voucher' && $slots['included-services']">
									<slot name="included-services"></slot>
								</div>

								<!-- Travel period -->
								<div class="divider" v-if="travelPeriod !== null"></div>
								<div class="travel-period" v-if="travelPeriod !== null">
									<div class="row">
										<div class="col">
											<span class="day">
												{{ arriveDate.format('DD') }}
											</span>
											<span class="month">
												{{ arriveDate.format('MMM') }}
												{{ arriveDate.format('YYYY') }}
											</span>
											<small class="weekday">
												{{ arriveDate.format('dddd') }}
											</small>
										</div>
										<div class="col">
											<small class="nights">
												{{ nights }} {{ 'checkout.summary.nights' | choice(nights) }}
											</small>
											<i class="wa-icon-long-arrow-right"></i>
										</div>
										<div class="col">
											<span class="day">
												{{ departureDate.format('DD') }}
											</span>
											<span class="month">
												{{ departureDate.format('MMM') }}
												{{ departureDate.format('YYYY') }}
											</span>
											<small class="weekday">
												{{ departureDate.format('dddd') }}
											</small>
										</div>
									</div>
								</div>

								<!-- Activities -->
								<div class="activity" v-for="activity in summary.activities">
									<div class="divider"></div>

									<div class="ticket" v-for="ticket in activity.tickets">
										<table>
											<tbody>
												<tr>
													<td>
														<span class="amount">
															{{ ticket.amount }}x
														</span>
													</td>
													<td>
														<strong class="name">
															{{ ticket.name }}
														</strong>
														<small class="subtitle">
															{{ ticket.subtitle }}
														</small>

														<div class="row date-time">
															<div class="col" v-if="ticket.date">
																<label>
																	{{ 'checkout.summary.ticketDate' | trans }}
																</label>
																{{ $moment(ticket.date, 'YYYY-MM-DD').format('dddd') }}, {{ $moment(ticket.date, 'YYYY-MM-DD').format('DD. MMMM YYYY') }}
															</div>
															<div class="col" v-if="ticket.time">
																<label>
																	{{ 'checkout.summary.ticketTime' | trans }}
																</label>
																{{ $moment(ticket.time, 'HH:mm:ss').format('HH:mm') }}
																{{ 'checkout.summary.clock' | trans }}
															</div>
															<div class="col" v-if="ticket.validity && ticket.time === null">
																<label>
																	{{ 'checkout.summary.validity' | trans }}
																</label>
																{{ ticket.validity }}
															</div>
														</div>

														<span class="age-group">
															{{ ticket.age_group }}
														</span>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>

								<!-- Rooms (Accommodations) -->
								<div class="divider" v-if="accommodationID !== null"></div>
								<div class="accommodation" v-if="accommodationID !== null" v-for="room in summary.rooms">
									<popover :name="'cancellationInfo' + room.id" :width="300" event="hover">
										{{ 'checkout.summary.refundableUntil' | trans({ date: $moment(room.refundable_until).format('DD.MM.YYYY'), time: $moment(room.refundable_until).format('HH:mm') }) }}
									</popover>

									<table>
										<tbody>
											<tr>
												<td>
													<strong class="name">
														{{ room.accommodation.name }}

														<span class="stars" v-if="room.stars > 0">
															<i class="fa fa-star" v-for="i in room.stars / 1"></i>
															<i class="fa fa-star-half" v-if="room.stars%1 > 0"></i>
														</span>
													</strong>
													<small class="subtitle">
														{{ room.address }}
													</small>

													<!-- Room -->
													<div class="row room">
														<div class="col">
															<label>
																{{ 'checkout.summary.room' | trans }}
															</label>
															{{ room.name }}
														</div>
													</div>

													<!-- Board type -->
													<div class="row room" v-if="room.board_type">
														<div class="col">
															<label>
																{{ 'checkout.summary.boardType' | trans }}
															</label>

															<i :class="room.board_type.icon" v-if="room.board_type.icon"></i>
															{{ room.board_type.name }}
														</div>
													</div>

													<!-- Amenities -->
													<div class="row room" v-if="room.amenities && room.amenities.length > 0">
														<div class="col">
															<label>
																{{ 'checkout.summary.extras' | trans }}
															</label>

															{{ room.amenities.map((amenity) => amenity.short_name || amenity.name).join(', ') }}
														</div>
													</div>

													<!-- Cancellation rules -->
													<div v-if="room.cancellation_rules" class="mt-2">
														<span class="refundable" v-if="room.is_refundable !== null && room.is_refundable" v-popover="{ name: 'cancellationInfo' + room.id, event: 'hover', width: 300 }">
															<i class="wa-icon-check-circle mr-1"></i>
															{{ 'checkout.summary.refundable' | trans }}

															<i class="fa fa-info-circle ml-1"
															v-if="room.refundable_until"></i>
														</span>
														<span class="non-refundable" v-if="room.is_refundable !== null && ! room.is_refundable">
															<i class="wa-icon-money-exchange mr-1"></i>
															{{ 'checkout.summary.nonRefundable' | trans }}
														</span>
													</div>
												</td>
											</tr>
										</tbody>
									</table>
								</div>

								<!-- Products -->
								<div class="product" v-for="product in summary.products">
									<div class="divider"></div>

									<table>
										<tbody>
											<tr>
												<!-- <td>
													<span class="quantity">
														{{ product.quantity }}x
													</span>
												</td> -->
												<td>
													<span class="price ml-2">
														{{ product.price | formatPrice }}
														&euro;
													</span>

													<strong class="name">
														{{ product.name }}
													</strong>
													<small class="subtitle" v-html="product.subtitle">
														{{ product.subtitle }}
													</small>

													<select class="custom-select custom-select-lg mt-2" v-model="productQuantity">
														<option value="1">1 {{ 'checkout.summary.vouchers' | choice(1) }}</option>
														<option value="2">2 {{ 'checkout.summary.vouchers' | choice(2) }}</option>
														<option value="3">3 {{ 'checkout.summary.vouchers' | choice(3) }}</option>
														<option value="4">4 {{ 'checkout.summary.vouchers' | choice(4) }}</option>
														<option value="5">5 {{ 'checkout.summary.vouchers' | choice(5) }}</option>
														<option value="6">6 {{ 'checkout.summary.vouchers' | choice(6) }}</option>
														<option value="7">7 {{ 'checkout.summary.vouchers' | choice(7) }}</option>
														<option value="8">8 {{ 'checkout.summary.vouchers' | choice(8) }}</option>
														<option value="9">9 {{ 'checkout.summary.vouchers' | choice(9) }}</option>
														<option value="10">10 {{ 'checkout.summary.vouchers' | choice(10) }}</option>
													</select>
												</td>
											</tr>
										</tbody>
									</table>
								</div>

								<div class="divider"></div>

								<!-- Subtotals -->
								<div class="subtotals mb-2" v-if="'total' in summary && (summary.rooms.length > 0 || summary.activities.length > 0 || summary.products.length > 0)">
									<div v-for="subtotal in summary.subtotals" v-if="'subtotals' in summary">
										<!-- Price -->
										<span class="price" v-if="subtotal.subtotal">
											{{ subtotal.subtotal | formatPrice }}
											&euro;
										</span>

										<i class="wa-icon-check-circle mr-1" v-if="subtotal.type === 'voucher'"></i>

										{{ 'checkout.summary.' + subtotal.type | trans }}

										<!-- Voucher code -->
										<small class="d-block text-muted" v-if="subtotal.type === 'voucher' && voucherCode" style="margin-left: 25px;">
											{{ 'checkout.summary.code' | trans }}:
											{{ voucherCode }}
										</small>

										<span v-if="subtotal.type === 'child'">
											({{ subtotal.age }} {{ 'checkout.summary.years' | choice(subtotal.age) }})
										</span>
									</div>

									<!-- Insurance -->
									<div v-if="summary.insurance">
										<span class="price">
											{{ summary.insurance.price | formatPrice }}
											&euro;
										</span>

										{{ summary.insurance.name }}
									</div>

									<!-- Shipping -->
									<div v-if="summary.shipping && ! displayOptions.isSumShipping">
										<span class="price">
											{{ summary.shipping.price | formatPrice }}
											&euro;
										</span>

										<span v-if="summary.shipping.quantity > 1">
											{{ summary.shipping.quantity }}x
										</span>

										{{ summary.shipping.name }}
									</div>

									<!-- Shipping fee -->
									<div v-if="summary.shipping_fee && ! displayOptions.isSumShipping">
										<span class="price">
											{{ summary.shipping_fee.price | formatPrice }}
											&euro;
										</span>

										<span v-if="summary.shipping.quantity > 1">
											{{ summary.shipping.quantity }}x
										</span>

										{{ summary.shipping_fee.name }}
									</div>

									<!-- Shipping + shipping fee -->
									<div v-if="summary.shipping && summary.shipping_fee && displayOptions.isSumShipping">
										<span class="price">
											{{ summary.shipping.price + summary.shipping_fee.price | formatPrice }}
											&euro;
										</span>

										<span v-if="summary.shipping.quantity > 1">
											{{ summary.shipping.quantity }}x
										</span>

										{{ 'checkout.summary.shippingType.' + shipping | trans }}
									</div>

									<!-- Discount -->
									<div v-if="summary.discount">
										<span class="price">
											{{ summary.discount.discount | formatPrice }}
											&euro;
										</span>

										{{ 'checkout.summary.discountCode' | trans }}:
										{{ summary.discount.code }}
									</div>

									<!-- Value coupons -->
									<div v-for="valueCoupon in summary.value_coupons">
										<span class="price">
											{{ valueCoupon.charge * -1 | formatPrice }}
											&euro;
										</span>

										{{ 'checkout.summary.valueCoupon' | trans }}:
										{{ valueCoupon.code }}
									</div>
								</div>

								<!-- Taxes and fees -->
								<div class="divider" v-if="taxesAndFees > 0"></div>
								<div class="taxes-and-fees" v-if="taxesAndFees > 0">
									<div>
										<span class="price">
											{{ summary.total - taxesAndFees | formatPrice }}
											&euro;
										</span>

										Reisepreis
									</div>

									<div style="cursor: pointer;" v-popover="{ name: 'taxesAndFees', event: 'hover', width: 300 }">
										<span class="price">
											{{ taxesAndFees | formatPrice }}
											&euro;
										</span>

										Steuern und Gebühren
										<i class="far fa-info-circle ml-1" ></i>
									</div>
								</div>

								<!-- Refundable -->
								<div v-if="typeof summary.trip !== 'undefined' && summary.is_refundable !== null">
									<div class="refundable" v-if="summary.is_refundable">
										<i class="wa-icon-insurance-basic-shield"></i>
										<div>
											<strong v-if="summary.refundable_type === 'full'">
												{{ 'checkout.summary.refundable' | trans }}
											</strong>
											<strong v-if="summary.refundable_type === 'partial'">
												{{ 'checkout.summary.partlyRefundable' | trans }}
											</strong>

											<small class="d-block text-muted" v-if="summary.refundable_until">
												{{ 'bis ' + $moment(summary.refundable_until).format('DD.MM.YYYY') + ' um ' + $moment(summary.refundable_until).format('HH:mm') + ' Uhr' }}
											</small>
										</div>
									</div>
									<div class="non-refundable" v-if="! summary.is_refundable">
										<i class="wa-icon-money-exchange mr-1"></i>
										{{ 'checkout.summary.nonRefundable' | trans }}
									</div>
								</div>

								<!-- Total -->
								<div class="total" v-if="summary.total || summary.allow_zero_price">
									<span class="price">
										{{ summary.total | formatPrice }}
										&euro;
									</span>

									{{ 'checkout.summary.total' | trans }}
								</div>

								<div class="text-center mb-3" v-if="isSummaryLoading">
									<svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
										<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
										<circle class="external-circle" cx="60" cy="60" r="50"></circle>
									</svg>
								</div>

								<button type="button" class="btn btn-primary btn-lg next-button" v-if="validNext === true" @click="nextStep()">
									{{ 'checkout.nextButton' | trans }}
								</button>

								<!-- Next infos -->
								<!-- Arrive date -->
								<p class="text-center text-info next-info" v-if="(arriveDate === null || departureDate === null) && productID === null">
									{{ 'checkout.nextInfos.arriveDate' | trans }}
								</p>
								<!-- Accommodation -->
								<p class="text-center text-info next-info" v-if="accommodationID === null && currentStep === 'accommodation' && productID === null">
									{{ 'checkout.nextInfos.accommodation' | trans }}
								</p>

								<!-- Fees info -->
								<div class="mt-3" v-if="feesInfo.length > 0">
									<small class="d-block text-muted" v-for="feeInfo in feesInfo">
										<i class="far fa-info-circle mr-1"></i>
										zzgl.
										{{ feeInfo.total | formatPrice }}
										&euro;
										{{ 'search.fees.types.' + feeInfo.type | trans }}
										vor Ort
									</small>
								</div>
							</div>

							<h2>
								{{ 'checkout.steps.confirmation.confirmAndPay' | trans }}
							</h2>

							<p class="info mb-4">
								{{ 'checkout.steps.confirmation.confirmAndPayInfo' | trans }}
							</p>

							<!-- Errors -->
							<div class="alert alert-danger" v-if="Object.keys(checkoutErrors).length > 0">
								<ul class="my-0" v-for="errorKey in Object.keys(checkoutErrors)">
									<li v-for="error in checkoutErrors[errorKey]">
										{{ error }}
									</li>
								</ul>
							</div>

							<div class="text-center" v-if="isLoading === 'loadConfirmation'">
								<svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
									<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
									<circle class="external-circle" cx="60" cy="60" r="50"></circle>
								</svg>
							</div>

							<!-- Price changed -->
							<div class="card border mt-3 mb-5" v-if="checkoutOptions !== null && !checkoutOptions.is_rooms_approved && ! isPriceChangeAccepted && ! checkoutOptions.is_sold_out">
								<div class="card-body">
									<strong class="d-block text-primary mb-2">
										Preisänderung!
									</strong>

									<h3 class="font-weight-700">
										Der Preis für deine Buchung hat sich geändert.
									</h3>

									<div class="d-flex flex-row">
										<div class="text-center d-flex flex-column pr-4 border-right">
											<span class="lead font-weight-700 pt-2">
												{{ checkoutOptions.approved_total | formatPrice }}
												&euro;
											</span>
											<small class="text-primary pb-2 font-weight-700">
												Neuer Preis
											</small>
										</div>

										<div class="text-center d-flex flex-column pl-4">
											<span class="lead pt-2" style="text-decoration: line-through;">
												{{ checkoutOptions.incorrect_total | formatPrice }}
												&euro;
											</span>
											<small class="text-danger pb-2 font-weight-700">
												Alter Preis
											</small>
										</div>
									</div>

									<p class="text-muted mt-3">
										<i class="fa fa-info-circle mr-1"></i>
										Bitte bestätige, dass du mit dem neuen Preis einverstanden bist.
									</p>

									<div class="bottom-control">
										<button class="btn btn-primary btn-lg book-button btn-block" @click="isPriceChangeAccepted = true">
											<i class="fa fa-check mr-1"></i>
											Bestätigen
										</button>
									</div>
								</div>
							</div>

							<!-- Sold out -->
							<div class="card border mt-3 mb-5" v-if="checkoutOptions !== null && !checkoutOptions.is_rooms_approved && ! isPriceChangeAccepted && checkoutOptions.is_sold_out">
								<div class="card-body">
									<strong class="d-block text-danger mb-2">
										<i class="fas fa-info-circle mr-1"></i>
										Ausverkauft!
									</strong>

									<h3 class="font-weight-700">
										Leider ist das Angebot nicht mehr verfügbar.
									</h3>
								</div>
							</div>

							<div v-if="checkoutOptions !== null && ! checkoutOptions.is_sold_out" :style="[checkoutOptions !== null && ! checkoutOptions.is_rooms_approved && ! isPriceChangeAccepted ? { 'filter': 'blur(2px)' } : null]" :disabled="checkoutOptions !== null && ! checkoutOptions.is_rooms_approved && ! isPriceChangeAccepted">

								<!-- Personal information -->
								<h3>
									<span v-if="! checkoutOptions.is_product">
										{{ 'checkout.steps.confirmation.firstTraveller' | trans }}
									</span>
									<span v-if="checkoutOptions.is_product">
										{{ 'checkout.steps.confirmation.buyer' | trans }}
									</span>
								</h3>

								<div class="personal-information">
									<div class="row" v-if="! checkoutOptions.is_product">
										<div class="col-md-6">
											<!-- Salutation -->
											<div class="form-group">
												<div class="input-group custom-input-group">
													<div class="input-group-prepend">
														<label class="input-group-text" for="salutation">
															<!-- Male icon -->
															<i class="fa fa-male" v-if="personalInformation.salutation == $options.filters.trans('checkout.steps.confirmation.salutationMale')" style="color: #4A90E2; font-size: 22px;"></i>
															<!-- Female icon -->
															<i class="fa fa-female" v-if="personalInformation.salutation == $options.filters.trans('checkout.steps.confirmation.salutationFemale')" style="color: #E24A89; font-size: 22px;"></i>
														</label>
													</div>

													<select v-model="personalInformation.salutation" id="salutation" class="custom-select">
														<option :value="'checkout.steps.confirmation.salutationFemale' | trans">
															{{ 'checkout.steps.confirmation.salutationFemale' | trans }}
														</option>
														<option :value="'checkout.steps.confirmation.salutationMale' | trans">
															{{ 'checkout.steps.confirmation.salutationMale' | trans }}
														</option>
													</select>
												</div>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-sm-6">
											<!-- Firstname -->
											<div class="form-group">
												<label for="firstname">
													{{ 'checkout.steps.confirmation.firstname' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="personalInformation.firstname" id="firstname" :class="{ 'is-invalid': 'personal_information.firstname' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'personal_information.firstname' in checkoutErrors">
													{{ checkoutErrors['personal_information.firstname'][0] }}
												</div>
											</div>
										</div>

										<div class="col-sm-6">
											<!-- Lastname -->
											<div class="form-group">
												<label for="lastname">
													{{ 'checkout.steps.confirmation.lastname' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="personalInformation.lastname" id="lastname" :class="{ 'is-invalid': 'personal_information.lastname' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'personal_information.lastname' in checkoutErrors">
													{{ checkoutErrors['personal_information.lastname'][0] }}
												</div>
											</div>
										</div>
									</div>

									<div class="row" v-if="! checkoutOptions.is_product">
										<div class="col-sm-12">
											<!-- Birthdate -->
											<div class="form-group">
												<label for="birthdate">
													{{ 'checkout.steps.confirmation.birthdate' | trans }}
													<span class="required">*</span>
												</label>
												<div class="input-group custom-input-group">
													<div class="input-group-prepend">
														<label class="input-group-text">
															<i class="fa fa-birthday-cake mr-2"></i>
														</label>
													</div>

													<masked-input
													type="text"
													class="form-control"
													:class="{ 'is-invalid': 'personal_information.birthdate' in checkoutErrors }"
													id="birthdate"
													v-model="personalInformation.birthdate"
													:mask="[/\d/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/, /\d/, /\d/]"
													:guide="true"
													:keepCharPositions="false"
													:showMask="false"
													:placeholder="'checkout.steps.confirmation.birthdatePlaceholder' | trans"
													placeholderChar=" "></masked-input>

													<div class="invalid-feedback" v-if="'personal_information.birthdate' in checkoutErrors">
														{{ checkoutErrors['personal_information.birthdate'][0] }}
													</div>

													<!--<input type="text" class="form-control" id="birthdate" v-model="personalInformation.birthdate" />-->
												</div>
											</div>
										</div>
									</div>

									<div class="divider" v-if="! checkoutOptions.is_product"></div>

									<!-- Contact information -->
									<h3 class="mb-3" v-if="! checkoutOptions.is_product">
										{{ 'checkout.steps.confirmation.contactInformation' | trans }}
									</h3>

									<div class="row">
										<div class="col-sm-12">
											<!-- Email address -->
											<div class="form-group">
												<label for="email">
													{{ 'checkout.steps.confirmation.email' | trans }}
													<span class="required">*</span>
												</label>
												<div class="input-group custom-input-group">
													<!-- <div class="input-group-prepend">
														<label class="input-group-text" for="email">
															@
														</label>
													</div> -->

													<input type="text" class="form-control" v-model="personalInformation.email" id="email" :class="{ 'is-invalid': 'personal_information.email' in checkoutErrors }" :placeholder="'checkout.steps.confirmation.emailPlaceholder' | trans" />

													<div class="invalid-feedback" v-if="'personal_information.email' in checkoutErrors">
														{{ checkoutErrors['personal_information.email'][0] }}
													</div>
												</div>
											</div>
										</div>
									</div>

									<div class="row" :class="{ 'mt-3': 'personal_information.email' in checkoutErrors }" v-if="! checkoutOptions.is_product">
										<div class="col-sm-12">
											<!-- Phone -->
											<div class="form-group">
												<label for="phone">
													{{ 'checkout.steps.confirmation.phone' | trans }}
													<span class="required">*</span>
												</label>

												<div class="input-group custom-input-group">
													<!-- <div class="input-group-prepend">
														<label class="input-group-text" for="phone">
															<i class="fa fa-phone"></i>
														</label>
													</div> -->

													<input type="text" class="form-control" v-model="personalInformation.phone" id="phone" :class="{ 'is-invalid': 'personal_information.phone' in checkoutErrors }" :placeholder="'checkout.steps.confirmation.phonePlaceholder' | trans" />

													<div class="invalid-feedback" v-if="'personal_information.phone' in checkoutErrors">
														{{ checkoutErrors['personal_information.phone'][0] }}
													</div>

													<!-- <div class="input-group-append d-none d-md-flex" v-if="!('personal_information.phone' in checkoutErrors)">
														<label class="input-group-text" for="phone">
															<span class="text-success">
																{{ 'checkout.steps.confirmation.noAdCalls' | trans }}
															</span>
														</label>
													</div> -->
												</div>

												<small class="d-block mt-2">
													<strong class="d-block text-success">
														<i class="fa fa-check"></i>
														{{ 'checkout.steps.confirmation.noPhoneAd' | trans }}
													</strong>

													<span class="text-muted">
														{{ 'checkout.steps.confirmation.noPhoneAdInfo' | trans }}
													</span>
												</small>
											</div>
										</div>
									</div>
								</div>

								<p>
									<span class="required">*</span>
									{{ 'checkout.steps.confirmation.requiredField' | trans }}
								</p>

								<!-- Accommodation guest -->
								<div class="divider" v-if="checkoutOptions.is_room"></div>
								<div v-if="checkoutOptions.is_room">
									<h3>
										{{ 'checkout.steps.confirmation.accommodationGuest' | trans }}
									</h3>
									<div class="accommodation-guest">
										<div class="form-check">
											<input class="form-check-input" v-model="isOtherAccommodationGuest" type="checkbox" value="" id="accommodationGuest">
											<label class="form-check-label" for="accommodationGuest">
												{{ 'checkout.steps.confirmation.accommodationGuestOther' | trans }}
											</label>
										</div>

										<!-- Inputs -->
										<div class="row" v-if="isOtherAccommodationGuest === true">
											<div class="col-md-3">
												<!-- Salutation -->
												<div class="form-group" style="margin-top: 29px;">
													<div class="input-group custom-input-group">
														<div class="input-group-prepend">
															<label class="input-group-text" for="accommodationGuestSalutation">
																<!-- Male icon -->
																<i class="fa fa-male" v-if="accommodationGuest.salutation == $options.filters.trans('checkout.steps.confirmation.salutationMale')" style="color: #4A90E2; font-size: 22px;"></i>
																<!-- Female icon -->
																<i class="fa fa-female" v-if="accommodationGuest.salutation == $options.filters.trans('checkout.steps.confirmation.salutationFemale')" style="color: #E24A89; font-size: 22px;"></i>
															</label>
														</div>

														<select v-model="accommodationGuest.salutation" id="accommodationGuestSalutation" class="custom-select">
															<option :value="'checkout.steps.confirmation.salutationFemale' | trans">
																{{ 'checkout.steps.confirmation.salutationFemale' | trans }}
															</option>
															<option :value="'checkout.steps.confirmation.salutationMale' | trans">
																{{ 'checkout.steps.confirmation.salutationMale' | trans }}
															</option>
														</select>
													</div>
												</div>
											</div>

											<div class="col-md-4">
												<!-- Firstname -->
												<div class="form-group">
													<label for="accommodationGuestFirstname">
														{{ 'checkout.steps.confirmation.firstname' | trans }}
														<span class="required">*</span>
													</label>
													<input type="text" class="form-control" v-model="accommodationGuest.firstname" id="accommodationGuestFirstname" :class="{ 'is-invalid': 'accommodation_guest.firstname' in checkoutErrors }" />

													<div class="invalid-feedback" v-if="'accommodation_guest.firstname' in checkoutErrors">
														{{ checkoutErrors['accommodation_guest.firstname'][0] }}
													</div>
												</div>
											</div>

											<div class="col-md-5">
												<!-- Lastname -->
												<div class="form-group">
													<label for="accommodationGuestLastname">
														{{ 'checkout.steps.confirmation.lastname' | trans }}
														<span class="required">*</span>
													</label>
													<input type="text" class="form-control" v-model="accommodationGuest.lastname" id="accommodationGuestLastname" :class="{ 'is-invalid': 'accommodation_guest.lastname' in checkoutErrors }" />

													<div class="invalid-feedback" v-if="'accommodation_guest.lastname' in checkoutErrors">
														{{ checkoutErrors['accommodation_guest.lastname'][0] }}
													</div>
												</div>
											</div>
										</div>
									</div>

									<!-- Accommodation comments -->
									<div class="alert alert-info mt-3" v-if="checkoutOptions.source_comments">
										<b class="d-block">
											{{ 'checkout.steps.confirmation.accommodationComments' | trans }}
										</b>

										{{ checkoutOptions.source_comments }}
									</div>
								</div>

								<!-- Travellers -->
								<div class="divider" v-if="travellersRequired"></div>
								<div class="travellers" v-if="travellersRequired">
									<h3>
										{{ 'checkout.steps.confirmation.travellers' | trans }}
									</h3>
									<p class="info mb-3">
										{{ 'checkout.steps.confirmation.travellersInfo' | trans }}
									</p>

									<!-- Adults + children -->
									<div v-for="(traveller, travellerIndex) in travellers">
										<h4 class="mt-4" v-if="traveller.type === 'adult'">
											{{ travellerIndex + 1 }}.
											{{ 'checkout.summary.adult' | trans }}
										</h4>
										<h4 class="mt-4" v-if="traveller.type === 'child'">
											{{ travellerIndex - adults + 1 }}.
											{{ 'checkout.summary.child' | trans }}

											<!-- Age -->
											<small v-if="traveller.birthdate !== null && traveller.birthdate.length === 10">
												({{ $moment().diff($moment(traveller.birthdate, 'DD.MM.YYYY').format('YYYY-MM-DD'), 'years') }}
												{{ 'checkout.summary.years' | choice($moment().diff($moment(traveller.birthdate, 'DD.MM.YYYY').format('YYYY-MM-DD'), 'years')) }})
											</small>
										</h4>

										<!-- Same as customer -->
										<div class="form-check" v-if="travellerIndex === 0 && !isOtherAccommodationGuest">
											<input type="checkbox" v-model="isSameFirstTraveller" id="sameFirstTraveller" class="form-check-input">
											<label for="sameFirstTraveller" class="form-check-label">
												{{ 'checkout.steps.confirmation.sameFirstTravellerAsCustomer' | trans }}
											</label>
										</div>

										<!-- First traveller info (same as customer) -->
										<p v-if="travellerIndex === 0 && isSameFirstTraveller && !isOtherAccommodationGuest && (personalInformation.salutation !== null && personalInformation.firstname !== null && personalInformation.lastname !== null)" style="margin-left: 35px;">
											{{ personalInformation.salutation }}
											{{ personalInformation.firstname }}
											{{ personalInformation.lastname }}

											<small class="ml-1">
												(<i class="fa fa-birthday-cake mr-1"></i> {{ personalInformation.birthdate }})
											</small>
										</p>

										<!-- First traveller info (same as hotel guest) -->
										<p v-if="travellerIndex === 0 && isOtherAccommodationGuest" style="margin-left: 20px;">
											<strong class="d-block">
												{{ 'checkout.steps.confirmation.sameFirstTravellerAsHotelGuest' | trans }}
											</strong>
											<span v-if="accommodationGuest.salutation !== null && accommodationGuest.firstname !== null && accommodationGuest.lastname !== null">
												{{ accommodationGuest.salutation }}
												{{ accommodationGuest.firstname }}
												{{ accommodationGuest.lastname }}
											</span>
											<span v-if="!(accommodationGuest.salutation !== null && accommodationGuest.firstname !== null && accommodationGuest.lastname !== null)">
												{{ 'checkout.steps.confirmation.pleaseFillOutHotelGuest' | trans }}
											</span>
										</p>

										<div class="row" v-if="travellerIndex > 0 || (travellerIndex === 0 && !isSameFirstTraveller && !isOtherAccommodationGuest)">
											<div class="col-md-3">
												<!-- Salutation -->
												<div class="form-group" style="margin-top: 29px;">
													<div class="input-group custom-input-group">
														<div class="input-group-prepend">
															<label class="input-group-text" :for="'travellerSalutation' + travellerIndex">
																<!-- Male icon -->
																<i class="fa fa-male" v-if="traveller.salutation == $options.filters.trans('checkout.steps.confirmation.salutationMale')" style="color: #4A90E2; font-size: 22px;"></i>
																<!-- Female icon -->
																<i class="fa fa-female" v-if="traveller.salutation == $options.filters.trans('checkout.steps.confirmation.salutationFemale')" style="color: #E24A89; font-size: 22px;"></i>
															</label>
														</div>

														<select v-model="traveller.salutation" :id="'travellerSalutation' + travellerIndex" class="custom-select" @change="isTravellersDirty = true">
															<option :value="'checkout.steps.confirmation.salutationFemale' | trans">
																{{ 'checkout.steps.confirmation.salutationFemale' | trans }}
															</option>
															<option :value="'checkout.steps.confirmation.salutationMale' | trans">
																{{ 'checkout.steps.confirmation.salutationMale' | trans }}
															</option>
														</select>
													</div>
												</div>
											</div>

											<div class="col-md-4">
												<!-- Firstname -->
												<div class="form-group">
													<label :for="'travellerFirstname' + travellerIndex">
														{{ 'checkout.steps.confirmation.firstname' | trans }}
														<span class="required">*</span>
													</label>
													<input type="text" class="form-control" v-model="traveller.firstname" :id="'travellerFirstname' + travellerIndex" :class="{ 'is-invalid': 'travellers.' + travellerIndex + '.firstname' in checkoutErrors }" @change="isTravellersDirty = true" />

													<div class="invalid-feedback" v-if="'travellers.' + travellerIndex + '.firstname' in checkoutErrors">
														{{ checkoutErrors['travellers.' + travellerIndex + '.firstname'][0] }}
													</div>
												</div>
											</div>

											<div class="col-md-5">
												<!-- Lastname -->
												<div class="form-group">
													<label :for="'travellerLastname' + travellerIndex">
														{{ 'checkout.steps.confirmation.lastname' | trans }}
														<span class="required">*</span>
													</label>
													<input type="text" class="form-control" v-model="traveller.lastname" :id="'travellerLastname' + travellerIndex" :class="{ 'is-invalid': 'travellers.' + travellerIndex + '.lastname' in checkoutErrors }" @change="isTravellersDirty = true" />

													<div class="invalid-feedback" v-if="'travellers.' + travellerIndex + '.lastname' in checkoutErrors">
														{{ checkoutErrors['travellers.' + travellerIndex + '.lastname'][0] }}
													</div>
												</div>
											</div>
										</div>

										<div class="row" v-if="travellerIndex > 0 || (travellerIndex === 0 && (!isSameFirstTraveller || isOtherAccommodationGuest))">
											<div class="col-sm-12">
												<!-- Birthdate -->
												<div class="form-group">
													<div class="input-group custom-input-group">
														<div class="input-group-prepend">
															<label class="input-group-text" for="birthdate">
																<i class="fa fa-birthday-cake mr-2"></i>
																{{ 'checkout.steps.confirmation.birthdate' | trans }}
																<span class="required">*</span>
															</label>
														</div>

														<masked-input
														type="text"
														class="form-control"
														:class="{ 'is-invalid': 'travellers.' + travellerIndex + '.birthdate' in checkoutErrors }"
														:id="'travellerBirthdate' + travellerIndex"
														v-model="traveller.birthdate"
														:mask="[/\d/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/, /\d/, /\d/]"
														:guide="true"
														:keepCharPositions="false"
														:showMask="false"
														placeholder="TT.MM.JJJJ"
														placeholderChar=" "></masked-input>

														<div class="invalid-feedback" v-if="'travellers.' + travellerIndex + '.birthdate' in checkoutErrors">
															{{ checkoutErrors['travellers.' + travellerIndex + '.birthdate'][0] }}
														</div>
													</div>
												</div>
											</div>
										</div>

										<!-- Additional fields -->
										<div class="form-group" v-for="additionalField in checkoutOptions.additional_fields">
											<!-- Label -->
											<label :for="'additional_field_' + additionalField.id + '_' + travellerIndex">
												{{ additionalField.label }}
												<span class="required" v-if="additionalField.is_required">*</span>
											</label>

											<!-- Type: text -->
											<input type="text" class="form-control" v-model="traveller.additional_fields[additionalField.name]" v-if="additionalField.type === 'text'" :placeholder="additionalField.placeholder" @change="() => { if (additionalField.coupon_campaign_id) { checkDiscountCode(traveller.additional_fields[additionalField.name]) } }" />

											<!-- Type: select -->
											<select type="text" class="custom-select" v-model="traveller.additional_fields[additionalField.name]" v-if="additionalField.type === 'select'" :placeholder="additionalField.placeholder">
												<!-- Placeholder -->
												<option disabled selected v-if="additionalField.placeholder">
													{{ additionalField.placeholder }}
												</option>

												<!-- Options -->
												<option :value="value.value" v-for="value in additionalField.values">
													{{ value.label }}
												</option>
											</select>

											<!-- Type: boolean -->
											<div class="custom-control custom-checkbox px-0" v-if="additionalField.type === 'boolean'">
												<input type="checkbox" class="custom-control-input" :id="'additional_field_checkbox_' + additionalField.id + '_' + travellerIndex" v-model="traveller.additional_fields[additionalField.name]" />

												<label class="custom-control-label" :for="'additional_field_checkbox_' + additionalField.id + '_' + travellerIndex">
													{{ additionalField.placeholder }}
												</label>
											</div>

											<!-- Description -->
											<small class="d-block mt-2 text-muted">
												{{ additionalField.description }}
											</small>
										</div>
									</div>
								</div>

								<!-- Insurances -->
								<div class="divider" v-if="!checkoutOptions.is_product && checkoutOptions.is_offer_insurances"></div>
								<div class="insurances" v-if="!checkoutOptions.is_product && checkoutOptions.is_offer_insurances">
									<img class="float-right" :src="$cdn.default + 'static/checkout/hansemerkur-logo.png?width=150'" :srcset="$cdn.default + 'static/checkout/hansemerkur-logo.png?width=150 1x, ' + $cdn.default + 'static/checkout/hansemerkur-logo.png?width=300 2x, ' + $cdn.default + 'static/checkout/hansemerkur-logo.png?width=450'" />

									<h3>
										{{ 'checkout.steps.confirmation.insurance' | trans }}
									</h3>
									<p class="info mb-3">
										{{ 'checkout.steps.confirmation.insuranceInfo' | trans }}
									</p>

									<div class="list-group mt-3">
										<!-- No insurance -->
										<div class="form-check list-group-item" :class="{ active: insurance === 'no' }">
											<input type="radio" value="no" v-model="insurance" id="noInsurance" />
											<label for="noInsurance">
												<i class="wa-icon-insurance-no-shield"></i>
												{{ 'checkout.steps.confirmation.noInsurances' | trans }}
											</label>
										</div>

										<!-- Basic insurance -->
										<div class="form-check list-group-item" :class="{ active: insurance === 'basic' }">
											<input type="radio" value="basic" v-model="insurance" id="basicInsurance" />
											<label for="basicInsurance">
												<i class="wa-icon-insurance-basic-shield"></i>
												Basic

												<ul>
													<li>
														Reisestornierung/-abbruch
														<small class="d-block">
															(Krankheit, Unfall, Todesfall, Arbeitslosigkeit, Verkehrsmittelverspätung)
														</small>
													</li>
												</ul>

												<small>
													<a :href="$cdn.default + 'assets/pdf/PIB_Ruecktrittsversicherung.pdf'" target="_blank">
														<i class="fa fa-info-circle"></i>
														Mehr Informationen
													</a>
												</small>

												<!-- Price -->
												<span class="price">
													+
													{{ checkoutOptions.insurances.basic | formatPrice }}
													&euro;
												</span>
											</label>
										</div>

										<!-- Plus insurance -->
										<div class="form-check list-group-item" :class="{ active: insurance === 'plus' }">
											<input type="radio" value="plus" v-model="insurance" id="plusInsurance" />
											<label for="plusInsurance">
												<i class="wa-icon-insurance-plus-shield"></i>
												Plus
												<ul>
													<li>
														Medizinische Kosten
													</li>
													<li>
														Rücktransport nach Deutschland
													</li>
													<li>
														Reisestornierung/-abbruch
														<small class="d-block">
															(Krankheit, Unfall, Todesfall, Arbeitslosigkeit, Verkehrsmittelverspätung)
														</small>
													</li>
													<li>
														Zahlungsmittel- & Dokumentenverlust
													</li>
													<li>
														Hilfeleistungen
													</li>
													<li>
														Verloren gegangenes Reisegepäck
													</li>
												</ul>

												<small>
													<a :href="$cdn.default + 'assets/pdf/PIB_5-Sterne-Premium-Schutz.pdf'" target="_blank">
														<i class="fa fa-info-circle"></i>
														Mehr Informationen
													</a>
												</small>

												<!-- Price -->
												<span class="price">
													+
													{{ checkoutOptions.insurances.plus | formatPrice }}
													&euro;
												</span>
											</label>
										</div>
									</div>
								</div>

								<!-- Discount and value coupon codes -->
								<div class="divider" v-if="(summary.total && summary.total > 0) || valueCoupons.length > 0"></div>
								<div class="discount-codes" v-if="(summary.total && summary.total > 0) || valueCoupons.length > 0">
									<h3>
										{{ 'checkout.steps.confirmation.discountCodesTitle' | trans }}
										<span class="text-muted font-weight-400">
											({{ 'checkout.steps.confirmation.optional' | trans }})
										</span>
									</h3>

									<!-- Discount form -->
									<div class="discount-form" v-if="summary.total && summary.total > 0">
										<div class="form-group">
											<div class="d-flex">
												<div class="flex-grow-1">
													<label for="discountCode" class="sr-only">
														{{ 'checkout.steps.confirmation.couponCode' | trans }}
													</label>
													<input type="text" class="form-control" v-model="discountCode" :placeholder="'checkout.steps.confirmation.couponCodePlaceholder' | trans" :class="{ 'is-invalid': discountCode !== null && ! discountCodeValid && discountCodeMessage !== null, 'is-valid': discountCode !== null && discountCodeValid && discountCodeMessage !== null }" />

													<div class="invalid-feedback" v-if="discountCode !== null && ! discountCodeValid && discountCodeMessage !== null">
														{{ discountCodeMessage }}
													</div>

													<div class="valid-feedback" v-if="discountCode !== null && discountCodeValid && discountCodeMessage !== null">
														{{ discountCodeMessage }}
													</div>
												</div>

												<div class="pl-2">
													<button type="button" class="btn btn-primary btn-lg" @click="checkDiscountCode()">
														{{ 'checkout.steps.confirmation.redeem' | trans }}
													</button>
												</div>
											</div>
										</div>
									</div>

									<!-- Value coupons -->
									<h4 class="mt-4" v-if="valueCoupons.length > 0">
										{{ 'checkout.steps.confirmation.myValueCoupons' | trans }}
									</h4>
									<div class="value-coupons" v-if="valueCoupons.length > 0">
										<div class="card border value-coupon-card" v-for="valueCoupon in valueCoupons">
											<div class="card-body">
												<!-- Balance -->
												<span class="badge float-right badge-lg ml-3" :class="{ 'badge-success': valueCoupon.balance > 0, 'badge-danger': valueCoupon.balance <= 0 }">
													{{ valueCoupon.balance | formatPrice }}
													&euro;
												</span>

												<span class="title">
													{{ 'checkout.steps.confirmation.valueCoupon' | trans }}
												</span>

												<!-- Code -->
												<span class="code">
													{{ valueCoupon.code }}
												</span>
											</div>
										</div>
									</div>

									<p class="mt-4 mb-n2" v-if="valueCoupons.length > 0">
										<strong>
											{{ 'checkout.steps.confirmation.toPay' | trans }}
										</strong>
										{{ summary.total | formatPrice }}
										&euro;
									</p>
								</div>

								<!-- Payment methods -->
								<div class="divider" v-if="summary.total > 0"></div>
								<div class="payment-methods" v-if="summary.total > 0">
									<h3>
										{{ 'checkout.steps.confirmation.paymentMethodsTitle' | trans }}
									</h3>

									<div class="list-group mt-3">
										<div class="form-check list-group-item" v-for="method in checkoutOptions.payment_methods" :class="{ active: method == paymentMethod }">
											<input type="radio" :value="method" v-model="paymentMethod" :id="'paymentMethod_' + method" />
											<label :for="'paymentMethod_' + method">
												<img :src="$cdn.default + 'assets/images/checkout/' + method + '-logo.svg?raw=true'" class="payment-logo" :class="method" />

												{{ 'checkout.steps.confirmation.paymentMethods.' + method | trans }}

												<small class="subtitle" v-if="paymentMethod == method">
													{{ 'checkout.steps.confirmation.paymentMethods.' + method + 'Info' | trans }}
												</small>

												<!-- Direct debit -->
												<div class="direct-debit-form" v-if="method == 'direct_debit' && paymentMethod == method">
													<h4>
														{{ 'checkout.steps.confirmation.directDebitData' | trans }}
													</h4>
													<p>
														<i class="wa-icon-lock"></i>
														{{ 'checkout.steps.confirmation.directDebitDataSecureInfo' | trans }}
													</p>

													<div class="row">
														<div class="col-md-6">
															<!-- Institut -->
															<div class="form-group">
																<label for="directDebitInstitut">
																	{{ 'checkout.steps.confirmation.institut' | trans }}
																	<span class="required">*</span>
																</label>
																<input type="text" v-model="directDebit.institut" class="form-control" id="directDebitInstitut" :class="{ 'is-invalid': 'direct_debit.institut' in checkoutErrors }" />

																<div class="invalid-feedback" v-if="'direct_debit.institut' in checkoutErrors">
																	{{ checkoutErrors['direct_debit.institut'][0] }}
																</div>
															</div>
														</div>

														<div class="col-md-6">
															<!-- Account holder -->
															<div class="form-group">
																<label for="directDebitAccountHolder">
																	{{ 'checkout.steps.confirmation.accountHolder' | trans }}
																	<span class="required">*</span>
																</label>
																<input type="text" v-model="directDebit.account_holder" class="form-control" id="directDebitAccountHolder" :class="{ 'is-invalid': 'direct_debit.account_holder' in checkoutErrors }" />

																<div class="invalid-feedback" v-if="'direct_debit.account_holder' in checkoutErrors">
																	{{ checkoutErrors['direct_debit.account_holder'][0] }}
																</div>
															</div>
														</div>
													</div>

													<div class="row">
														<div class="col-md-6">
															<!-- IBAN -->
															<div class="form-group">
																<label for="directDebitIban">
																	{{ 'checkout.steps.confirmation.iban' | trans }}
																	<span class="required">*</span>
																</label>
																<input type="text" v-model="directDebit.iban" class="form-control" id="directDebitIban" :class="{ 'is-invalid': 'direct_debit.iban' in checkoutErrors }" />

																<div class="invalid-feedback" v-if="'direct_debit.iban' in checkoutErrors">
																	{{ checkoutErrors['direct_debit.iban'][0] }}
																</div>
															</div>
														</div>

														<div class="col-md-6">
															<!-- BIC -->
															<div class="form-group">
																<label for="directDebitBic">
																	{{ 'checkout.steps.confirmation.bic' | trans }}
																	<span class="required">*</span>
																</label>
																<input type="text" v-model="directDebit.bic" class="form-control" id="directDebitBic" :class="{ 'is-invalid': 'direct_debit.bic' in checkoutErrors }" />

																<div class="invalid-feedback" v-if="'direct_debit.bic' in checkoutErrors">
																	{{ checkoutErrors['direct_debit.bic'][0] }}
																</div>
															</div>
														</div>
													</div>
												</div>

												<!-- Voucher -->
												<small class="d-block" v-if="method == 'voucher' && paymentMethod == method">
													{{ 'checkout.summary.code' | trans }}:
													<code>
														{{ voucherCode }}
													</code>
												</small>
											</label>
										</div>
									</div>
								</div>

								<!-- Invoice address -->
								<div class="divider"></div>
								<div class="invoice-address">
									<h3 class="mb-3">
										{{ 'checkout.steps.confirmation.invoiceAddress' | trans }}
									</h3>

									<div class="row">
										<div class="col-sm-12">
											<!-- Street house number -->
											<div class="form-group">
												<label for="streetHouseNumber">
													{{ 'checkout.steps.confirmation.streetHouseNumber' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="personalInformation.street_house_number" id="streetHouseNumber" :class="{ 'is-invalid': 'personal_information.street_house_number' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'personal_information.street_house_number' in checkoutErrors">
													{{ checkoutErrors['personal_information.street_house_number'][0] }}
												</div>
											</div>

											<!-- Toggle additional address -->
											<button type="button" class="btn btn-link px-1 btn-sm mt-n4" v-show="! checkoutOptions.is_additional_address_visible && ! personalInformation.additional_address" @click="checkoutOptions.is_additional_address_visible = true">
												<i class="fa fa-plus mr-1"></i>
												{{ 'checkout.steps.confirmation.showAdditionalAddress' | trans }}
											</button>

											<!-- Additional address -->
											<div class="form-group" v-show="checkoutOptions.is_additional_address_visible || personalInformation.additional_address">
												<label for="additionalAddress">
													{{ 'checkout.steps.confirmation.additionalAddress' | trans }}
													<span class="text-muted font-weight-400">
														({{ 'checkout.steps.confirmation.optional' | trans }})
													</span>
												</label>
												<input type="text" class="form-control" v-model="personalInformation.additional_address" id="additionalAddress" :class="{ 'is-invalid': 'personal_information.additional_address' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'personal_information.additional_address' in checkoutErrors">
													{{ checkoutErrors['personal_information.additional_address'][0] }}
												</div>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-sm-4">
											<!-- Zipcode -->
											<div class="form-group">
												<label for="zipcode">
													{{ 'checkout.steps.confirmation.zipcode' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="personalInformation.zipcode" id="zipcode" :class="{ 'is-invalid': 'personal_information.zipcode' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'personal_information.zipcode' in checkoutErrors">
													{{ checkoutErrors['personal_information.zipcode'][0] }}
												</div>
											</div>
										</div>

										<div class="col-sm-8">
											<!-- City -->
											<div class="form-group">
												<label for="city">
													{{ 'checkout.steps.confirmation.city' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="personalInformation.city" id="city" :class="{ 'is-invalid': 'personal_information.city' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'personal_information.city' in checkoutErrors">
													{{ checkoutErrors['personal_information.city'][0] }}
												</div>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-sm-12">
											<!-- COUNTRY -->
											<div class="form-group">
												<div class="input-group custom-input-group">
													<div class="input-group-prepend">
														<label class="input-group-text" for="country">
															<!-- Flag icon -->
															<i class="flag-icon" :class="'flag-icon-' + personalInformation.country"></i>
														</label>
													</div>

													<select v-model="personalInformation.country" id="country" class="custom-select" :class="{ 'is-invalid': 'personal_information.country' in checkoutErrors }">
														<option :value="country" v-for="country in checkoutCountries">
															{{ 'checkout.steps.confirmation.countries.' + country | trans }}
														</option>
													</select>

													<div class="invalid-feedback" v-if="'personal_information.country' in checkoutErrors">
														{{ checkoutErrors['personal_information.country'][0] }}
													</div>
												</div>
											</div>
										</div>
									</div>

									<!-- Checkbox for different shipping address -->
									<div class="form-check mt-3" v-if="shipping !== 'none'">
										<input class="form-check-input" v-model="isIdenticalInvoiceAndShippingAddress" type="checkbox" id="differentShippingAddress">
										<label class="form-check-label" for="differentShippingAddress">
											{{ 'checkout.steps.confirmation.otherShippingAddress' | trans }}
										</label>
									</div>
								</div>

								<!-- Shipping address -->
								<div class="shipping-address mt-4" v-if="!isIdenticalInvoiceAndShippingAddress">
									<h3 class="mb-3">
										{{ 'checkout.steps.confirmation.shippingAddress' | trans }}
									</h3>

									<div class="row">
										<div class="col-sm-12">
											<!-- Recipient -->
											<div class="form-group">
												<label for="recipient">
													{{ 'checkout.steps.confirmation.recipient' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="shippingAddress.recipient" id="recipient" :class="{ 'is-invalid': 'shipping_address.recipient' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'shipping_address.recipient' in checkoutErrors">
													{{ checkoutErrors['shipping_address.recipient'][0] }}
												</div>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-sm-12">
											<!-- Street house number -->
											<div class="form-group">
												<label for="shippingStreetHouseNumber">
													{{ 'checkout.steps.confirmation.streetHouseNumber' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="shippingAddress.street_house_number" id="shippingStreetHouseNumber" :class="{ 'is-invalid': 'shipping_address.street_house_number' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'shipping_address.street_house_number' in checkoutErrors">
													{{ checkoutErrors['shipping_address.street_house_number'][0] }}
												</div>
											</div>

											<!-- Toggle additional address -->
											<button type="button" class="btn btn-link px-1 btn-sm mt-n4" v-show="! checkoutOptions.is_additional_address_visible && !shippingAddress.additional_address" @click="checkoutOptions.is_additional_address_visible = true">
												<i class="fa fa-plus mr-1"></i>
												{{ 'checkout.steps.confirmation.showAdditionalAddress' | trans }}
											</button>

											<!-- Additional address -->
											<div class="form-group" v-show="checkoutOptions.is_additional_address_visible || shippingAddress.additional_address">
												<label for="shippingAdditionalAddress">
													{{ 'checkout.steps.confirmation.additionalAddress' | trans }}
													<span class="text-muted font-weight-400">
														({{ 'checkout.steps.confirmation.optional' | trans }})
													</span>
												</label>
												<input type="text" class="form-control" v-model="shippingAddress.additional_address" id="shippingAdditionalAddress" :class="{ 'is-invalid': 'shipping_address.additional_address' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'shipping_address.additional_address' in checkoutErrors">
													{{ checkoutErrors['shipping_address.additional_address'][0] }}
												</div>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-sm-4">
											<!-- Zipcode -->
											<div class="form-group">
												<label for="shippingZipcode">
													{{ 'checkout.steps.confirmation.zipcode' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="shippingAddress.zipcode" id="shippingZipcode" :class="{ 'is-invalid': 'shipping_address.zipcode' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'shipping_address.zipcode' in checkoutErrors">
													{{ checkoutErrors['shipping_address.zipcode'][0] }}
												</div>
											</div>
										</div>

										<div class="col-sm-8">
											<!-- City -->
											<div class="form-group">
												<label for="shippingCity">
													{{ 'checkout.steps.confirmation.city' | trans }}
													<span class="required">*</span>
												</label>
												<input type="text" class="form-control" v-model="shippingAddress.city" id="shippingCity" :class="{ 'is-invalid': 'shipping_address.city' in checkoutErrors }" />

												<div class="invalid-feedback" v-if="'shipping_address.city' in checkoutErrors">
													{{ checkoutErrors['shipping_address.city'][0] }}
												</div>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-sm-12">
											<!-- COUNTRY -->
											<div class="form-group">
												<div class="input-group custom-input-group">
													<div class="input-group-prepend">
														<label class="input-group-text" for="shippingCountry">
															<!-- Flag icon -->
															<i class="flag-icon" :class="'flag-icon-' + shippingAddress.country"></i>
														</label>
													</div>

													<select v-model="shippingAddress.country" id="shippingCountry" class="custom-select" :class="{ 'is-invalid': 'shipping_address.country' in checkoutErrors }">
														<option :value="country" v-for="country in checkoutCountries">
															{{ 'checkout.steps.confirmation.countries.' + country | trans }}
														</option>
													</select>

													<div class="invalid-feedback" v-if="'shipping_address.country' in checkoutErrors">
														{{ checkoutErrors['shipping_address.country'][0] }}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<!-- Final summary -->
								<div class="summary final inline d-md-none">
									<div class="subtotals border-0" v-if="'total' in summary && (summary.rooms.length > 0 || summary.activities.length > 0 || summary.products.length > 0)">

										<div v-for="product in summary.products" v-if="'products' in summary">
											<span class="price">
												{{ product.price | formatPrice }}
												&euro;
											</span>

											{{ productQuantity }}x {{ product.name }}
										</div>

										<div v-for="subtotal in summary.subtotals" v-if="'subtotals' in summary">
											<!-- Price -->
											<span class="price" v-if="subtotal.subtotal">
												{{ subtotal.subtotal | formatPrice }}
												&euro;
											</span>

											<i class="wa-icon-check-circle mr-1" v-if="subtotal.type === 'voucher'"></i>

											{{ 'checkout.summary.' + subtotal.type | trans }}

											<!-- Voucher code -->
											<small class="d-block text-muted" v-if="subtotal.type === 'voucher' && voucherCode" style="margin-left: 25px;">
												{{ 'checkout.summary.code' | trans }}: {{ voucherCode }}
											</small>

											<span v-if="subtotal.type === 'child'">
												({{ subtotal.age }} {{ 'checkout.summary.years' | choice(subtotal.age) }})
											</span>
										</div>

										<!-- Insurance -->
										<div v-if="summary.insurance">
											<span class="price">
												{{ summary.insurance.price | formatPrice }}
												&euro;
											</span>

											{{ summary.insurance.name }}
										</div>

										<!-- Shipping -->
										<div v-if="summary.shipping && ! displayOptions.isSumShipping">
											<span class="price">
												{{ summary.shipping.price | formatPrice }}
												&euro;
											</span>


											<span v-if="summary.shipping.quantity > 1">
												{{ summary.shipping.quantity }}x
											</span>

											{{ summary.shipping.name }}
										</div>

										<!-- Shipping fee -->
										<div v-if="summary.shipping_fee && ! displayOptions.isSumShipping">
											<span class="price">
												{{ summary.shipping_fee.price | formatPrice }}
												&euro;
											</span>

											<span v-if="summary.shipping.quantity > 1">
												{{ summary.shipping.quantity }}x
											</span>

											{{ summary.shipping_fee.name }}
										</div>

										<!-- Shipping + shipping fee -->
										<div v-if="summary.shipping && summary.shipping_fee && displayOptions.isSumShipping">
											<span class="price">
												{{ summary.shipping.price + summary.shipping_fee.price | formatPrice }}
												&euro;
											</span>

											<span v-if="summary.shipping.quantity > 1">
												{{ summary.shipping.quantity }}x
											</span>

											<span v-if="shipping === 'none'">Gutschein per E-Mail</span>
											<span v-if="shipping === 'print_ticket'">Gutschein per Post</span>
											<span v-if="shipping === 'gift_box'">Deluxe Gutscheinbox</span>
										</div>

										<!-- Discount -->
										<div v-if="summary.discount">
											<span class="price">
												{{ summary.discount.discount | formatPrice }}
												&euro;
											</span>

											{{ 'checkout.steps.confirmation.couponCode' | trans }}:
											{{ summary.discount.code }}
										</div>

										<!-- Value coupons -->
										<div v-for="valueCoupon in summary.value_coupons">
											<span class="price">
												{{ valueCoupon.charge * -1 | formatPrice }}
												&euro;
											</span>

											{{ 'checkout.steps.confirmation.valueCoupon' | trans }}:
											{{ valueCoupon.code }}
										</div>
									</div>

									<!-- Taxes and fees -->
									<div class="divider" v-if="taxesAndFees > 0"></div>
									<div class="taxes-and-fees" v-if="taxesAndFees > 0">
										<div>
											<span class="price">
												{{ summary.total - taxesAndFees | formatPrice }}
												&euro;
											</span>

											Reisepreis
										</div>

										<div style="cursor: pointer;" v-popover="{ name: 'taxesAndFees', event: 'hover', width: 300 }">
											<span class="price">
												{{ taxesAndFees | formatPrice }}
												&euro;
											</span>

											Steuern und Gebühren
											<i class="far fa-info-circle ml-1" ></i>
										</div>
									</div>

									<!-- Refundable -->
									<div v-if="typeof summary.trip !== 'undefined' && summary.is_refundable !== null">
										<div class="refundable" v-if="summary.is_refundable">
											<i class="wa-icon-insurance-basic-shield"></i>
											<div>
												<strong v-if="summary.refundable_type === 'full'">
													{{ 'checkout.summary.refundable' | trans }}
												</strong>
												<strong v-if="summary.refundable_type === 'partial'">
													{{ 'checkout.summary.partlyRefundable' | trans }}
												</strong>

												<small class="d-block text-muted" v-if="summary.refundable_until">
													{{ 'bis ' + $moment(summary.refundable_until).format('DD.MM.YYYY') + ' um ' + $moment(summary.refundable_until).format('HH:mm') + ' Uhr' }}
												</small>
											</div>
										</div>
										<div class="non-refundable" v-if="! summary.is_refundable">
											<i class="wa-icon-money-exchange mr-1"></i>
											{{ 'checkout.summary.nonRefundable' | trans }}
										</div>
									</div>

									<!-- Total -->
									<div class="total" v-if="summary.total || summary.allow_zero_price">
										<span class="price">
											{{ summary.total | formatPrice }}
											&euro;
										</span>

										{{ 'checkout.summary.total' | trans }}
									</div>

									<!-- Fees info -->
									<div class="mt-3" v-if="feesInfo.length > 0">
										<small class="d-block text-muted" v-for="feeInfo in feesInfo">
											<i class="far fa-info-circle mr-1"></i>
											zzgl.
											{{ feeInfo.total | formatPrice }}
											&euro;
											{{ 'search.fees.types.' + feeInfo.type | trans }}
											vor Ort
										</small>
									</div>
								</div>

								<!-- Terms of service -->
								<div class="terms-of-service mt-4">
									<div class="form-check">
										<input class="form-check-input" type="checkbox" value="" id="termsOfService" v-model="isTermsOfService" :class="{ 'is-invalid': 'is_terms_of_service' in checkoutErrors }">
										<label class="form-check-label" for="termsOfService">
											<span class="required">*</span>

											{{ 'checkout.steps.confirmation.acceptTermsOfServiceBefore' | trans }}

											<span v-for="(legalTerm, legalTermIndex) in checkoutOptions.legal_terms">
												<!-- And -->
												<span v-if="legalTermIndex > 0">
													{{ 'checkout.steps.confirmation.acceptTermsOfServiceAnd' | trans }}
												</span>

												<!-- Link -->
												<a :href="legalTerm.url" target="_blank">
													{{ legalTerm.name }}
												</a>

												<!-- Company -->
												<span v-if="legalTerm.company_name">
													{{ 'checkout.steps.confirmation.acceptTermsOfServicePart' | trans({ companyName: legalTerm.company_name }) }}
												</span>
											</span>

											{{ 'checkout.steps.confirmation.acceptTermsOfServiceAfter' | trans }}
										</label>

										<div class="invalid-feedback" v-if="'is_terms_of_service' in checkoutErrors">
											{{ checkoutErrors['is_terms_of_service'][0] }}
										</div>
									</div>
								</div>

								<!-- Confirm voucher redemption -->
								<div class="terms-of-service mt-4" v-if="checkoutOptions.is_voucher">
									<div class="form-check">
										<input class="form-check-input" type="checkbox" value="" id="confirmVoucherRedemption" v-model="isVoucherRedemptionConfirmed" :class="{ 'is-invalid': 'is_confirm_voucher_redemption' in checkoutErrors }">
										<label class="form-check-label" for="confirmVoucherRedemption">
											<span class="required">*</span>

											{{ 'checkout.steps.confirmation.confirmVoucherRedemption' | trans }}
										</label>

										<div class="invalid-feedback" v-if="'is_confirm_voucher_redemption' in checkoutErrors">
											{{ checkoutErrors['is_confirm_voucher_redemption'][0] }}
										</div>
									</div>
								</div>

								<!-- Confirm b2b package -->
								<div class="terms-of-service mt-4" v-if="checkoutOptions.is_package">
									<div class="form-check">
										<input class="form-check-input" type="checkbox" value="" id="confirmPackage" v-model="isPackageConfirmed" :class="{ 'is-invalid': 'is_package_confirmed' in checkoutErrors }">
										<label class="form-check-label" for="confirmPackage">
											<span class="required">*</span>

											{{ 'checkout.steps.confirmation.confirmPackage' | trans }}
										</label>

										<div class="invalid-feedback" v-if="'is_package_confirmed' in checkoutErrors">
											{{ checkoutErrors['is_package_confirmed'][0] }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Bottom control -->
					<div class="row bottom-control mt-5">
						<div class="col-sm-4">
							<button type="button" class="btn btn-light btn-lg prev-button px-5 d-none d-sm-block" v-if="(currentStep !== 'accommodation_preselection' && currentStep != 'product' && currentStep !== 'calendar') || (currentStep === 'calendar' && steps[0] === 'accommodation_preselection') || (currentStep != 'calendar' && steps[0] === 'calendar')" @click="prevStep()">
								{{ 'checkout.prevButton' | trans }}
							</button>
						</div>
						<div class="col-sm-8 text-right">
							<button type="button" class="btn btn-primary btn-lg next-button px-5" v-if="validNext === true" @click="nextStep()">
								<!-- Custom name for additional fields step -->
								<!-- TODO: Create dynamic property which has the current button name -->
								<span v-if="currentStep === 'additional_fields' && additionalFieldOptions && additionalFieldOptions.is_custom_skip_button === true && additionalValues.length === 0">
									{{ additionalFieldOptions.custom_skip_button_name }}
								</span>

								<!-- Default button name -->
								<span v-if="! (currentStep === 'additional_fields' && additionalFieldOptions && additionalFieldOptions.is_custom_skip_button === true && additionalValues.length === 0)">
									{{ 'checkout.nextButton' | trans }}
								</span>
							</button>

							<button type="button" class="btn btn-primary btn-lg book-button w-100" v-if="isStepActive('confirmation') && checkoutOptions !== null" @click="book()" :disabled="isLoading === 'book'">
								<span v-if="isLoading !== 'book'">
									<i class="wa-icon-long-arrow-right"></i>

									<span v-if="checkoutOptions.booking_mode === 'direct' && !checkoutOptions.is_product && !checkoutOptions.is_voucher">
										{{ 'checkout.bookButton' | trans }}
									</span>
									<span v-if="checkoutOptions.booking_mode === 'direct' && checkoutOptions.is_product && !checkoutOptions.is_voucher">
										{{ 'checkout.buyButton' | trans }}
									</span>
									<span v-if="checkoutOptions.is_voucher">
										{{ 'checkout.redeemVoucher' | trans }}
									</span>
									<span v-if="checkoutOptions.booking_mode === 'request' && !checkoutOptions.is_voucher">
										{{ 'checkout.requestButton' | trans }}
									</span>
								</span>

								<svg class="white-loader" v-if="isLoading === 'book'" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
									<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
									<circle class="external-circle" cx="60" cy="60" r="50"></circle>
								</svg>
							</button>

							<small class="d-block text-center text-primary mt-2 font-weight-500" v-if="isStepActive('confirmation') && checkoutOptions !== null">
								{{ 'checkout.steps.confirmation.bookSafe' | trans }}
							</small>

							<button type="button" class="btn btn-light btn-lg prev-button px-5 d-sm-none mt-4" v-if="(currentStep !== 'accommodation_preselection' && currentStep != 'product' && currentStep !== 'calendar') || (currentStep === 'calendar' && steps[0] === 'accommodation_preselection') || (currentStep != 'calendar' && steps[0] === 'calendar')" @click="prevStep()">
								{{ 'checkout.prevButton' | trans }}
							</button>

							<!-- Next infos -->
							<!-- Accommodation preselection -->
							<p class="text-muted next-info" v-if="currentStep === 'accommodation_preselection'">
								Wähle erst ein Hotel / Reiseziel aus.
							</p>

							<!-- Arrive date -->
							<p class="text-muted next-info" v-if="(arriveDate === null || departureDate === null) && productID === null && currentStep !== 'accommodation_preselection'">
								{{ 'checkout.nextInfos.arriveDate' | trans }}
							</p>

							<!-- Accommodation -->
							<p class="text-muted next-info" v-if="accommodationID === null && currentStep === 'accommodation' && productID === null">
								{{ 'checkout.nextInfos.accommodation' | trans }}
							</p>
						</div>
					</div>

					<p class="mt-5" v-if="tripId == 800 && ! isStepActive('confirmation')">
						<strong class="d-block">
							Hinweis zu Aufpreisen
						</strong>

						Wie entsteht ein Aufpreis? In einigen Fällen kommt es vor, dass Euch nur Hotels zur Verfügung stehen, die mit einem Aufpreis verbunden sind. Ein Grund dafür kann Euer Reisezeitraum sein, da Hotels ihre Preise an die Haupt- oder Nebensaison anpassen. In einigen Fällen können aber auch Messen oder Veranstaltungen in der Umgebung ein Grund für erhöhte Preise sein, sodass wir an bestimmten Terminen keine Einlösung zum Gutscheinpreis ermöglichen können. Auch Hotels aus einer höheren Sternekategorie sowie bereits ausgebuchte Termine können für zusätzliche Kosten sorgen.

						<small class="d-block mt-4">
							Auszug aus den Einlösebedingungen von der Verkaufsseite: "In beliebten Reisezeiträumen kann es durch mangelndem Kontingent zu geringer Verfügbarkeit oder durch hohe Nachfrage zu Aufpreisen bei der Buchung kommen."
						</small>
					</p>
				</div>

				<div class="col-md-5">
					<!-- Summary -->
					<div class="summary">
						<h2>
							{{ 'checkout.summary.title' | trans }}
						</h2>

						<!-- Deal info -->
						<div class="deal-info">
							<div class="d-lg-none mb-3">
								<slot name="deal-image-top"></slot>
							</div>

							<div class="d-flex">
								<div class="w-100">
									<h3>
										<slot name="deal-name"></slot>
									</h3>

									<p>
										{{ adults + children.length }} {{ 'checkout.steps.calendar.persons' | choice(adults + children.length) }}
										<span v-if="nights !== null">
											&bull;
											{{ nights }} {{ 'checkout.steps.calendar.nights' | choice(nights) }}
										</span>
									</p>
								</div>
								<div class="flex-shrink-1 d-none d-lg-block" v-if="theme !== 'voucher'">
									<slot name="deal-image"></slot>
								</div>

								<div class="flex-shrink-1 d-none d-lg-block" style="max-width: 150px;" v-if="theme === 'voucher'">
									<img class="rounded" style="max-width: 100% !important;" :src="'https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/postal-' + design + '.jpg'" v-if="shipping === 'print_ticket'" />

									<img class="rounded" style="max-width: 100% !important;" :src="'https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/postal-deluxe.png'" v-if="shipping === 'gift_box'" />

									<img class="rounded" style="max-width: 100% !important;" :src="'https://urlaubsguru-voucher-staging-cdn.s3.eu-central-1.amazonaws.com/shipping-variants/voucher-mail-' + design + '.jpg'" v-if="shipping === 'none'" />
								</div>
							</div>
						</div>

						<!-- Included services -->
						<div class="divider" v-if="theme !== 'voucher' && $slots['included-services']"></div>
						<button class="btn btn-link btn-lg collapsed included-services-toggler" type="button" data-toggle="collapse" data-target="#included_services" v-if="theme !== 'voucher' && $slots['included-services']">
							<i class="fa fa-chevron-right"></i>
							<i class="fa fa-chevron-down"></i>

							{{ 'checkout.includedServices' | trans }}
						</button>
						<div class="collapse" id="included_services" v-if="theme !== 'voucher' && $slots['included-services']">
							<slot name="included-services"></slot>
						</div>

						<!-- Travel period -->
						<div class="divider" v-if="travelPeriod !== null"></div>
						<div class="travel-period" v-if="travelPeriod !== null">
							<div class="row">
								<div class="col">
									<span class="day">
										{{ arriveDate.format('DD') }}
									</span>
									<span class="month">
										{{ arriveDate.format('MMM') }}
										{{ arriveDate.format('YYYY') }}
									</span>
									<small class="weekday">
										{{ arriveDate.format('dddd') }}
									</small>
								</div>
								<div class="col">
									<small class="nights">
										{{ nights }} {{ 'checkout.summary.nights' | choice(nights) }}
									</small>
									<i class="wa-icon-long-arrow-right"></i>
								</div>
								<div class="col">
									<span class="day">
										{{ departureDate.format('DD') }}
									</span>
									<span class="month">
										{{ departureDate.format('MMM') }}
										{{ departureDate.format('YYYY') }}
									</span>
									<small class="weekday">
										{{ departureDate.format('dddd') }}
									</small>
								</div>
							</div>
						</div>

						<!-- Activities -->
						<div class="activity" v-for="activity in summary.activities">
							<div class="divider"></div>

							<div class="ticket" v-for="ticket in activity.tickets">
								<table>
									<tbody>
										<tr>
											<td>
												<span class="amount">
													{{ ticket.amount }}x
												</span>
											</td>
											<td>
												<strong class="name">
													{{ ticket.name }}
												</strong>
												<small class="subtitle">
													{{ ticket.subtitle }}
												</small>

												<div class="row date-time">
													<div class="col" v-if="ticket.date">
														<label>
															{{ 'checkout.summary.ticketDate' | trans }}
														</label>
														{{ $moment(ticket.date, 'YYYY-MM-DD').format('dddd') }}, {{ $moment(ticket.date, 'YYYY-MM-DD').format('DD. MMMM YYYY') }}
													</div>
													<div class="col" v-if="ticket.time">
														<label>
															{{ 'checkout.summary.ticketTime' | trans }}
														</label>
														{{ $moment(ticket.time, 'HH:mm:ss').format('HH:mm') }}
														{{ 'checkout.summary.clock' | trans }}
													</div>
													<div class="col" v-if="ticket.validity && ticket.time === null">
														<label>
															{{ 'checkout.summary.validity' | trans }}
														</label>
														{{ ticket.validity }}
													</div>
												</div>

												<span class="age-group">
													{{ ticket.age_group }}
												</span>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<!-- Rooms (Accommodations) -->
						<div class="divider" v-if="accommodationID !== null"></div>
						<div class="accommodation" v-if="accommodationID !== null" v-for="room in summary.rooms">
							<popover :name="'cancellationInfo' + room.id" :width="300" event="hover">
								{{ 'checkout.summary.refundableUntil' | trans({ date: $moment(room.refundable_until).format('DD.MM.YYYY'), time: $moment(room.refundable_until).format('HH:mm') }) }}
							</popover>

							<table>
								<tbody>
									<tr>
										<td>
											<strong class="name">
												{{ room.accommodation.name }}

												<span class="stars" v-if="room.stars > 0">
													<i class="fa fa-star" v-for="i in room.stars / 1"></i>
													<i class="fa fa-star-half" v-if="room.stars%1 > 0"></i>
												</span>
											</strong>
											<small class="subtitle">
												{{ room.address }}
											</small>

											<!-- Room -->
											<div class="row room">
												<div class="col">
													<label>
														{{ 'checkout.summary.room' | trans }}
													</label>
													{{ room.name }}
												</div>
											</div>

											<!-- Board type -->
											<div class="row room" v-if="room.board_type">
												<div class="col">
													<label>
														{{ 'checkout.summary.boardType' | trans }}
													</label>

													<i :class="room.board_type.icon" v-if="room.board_type.icon"></i>
													{{ room.board_type.name }}
												</div>
											</div>

											<!-- Amenities -->
											<div class="row room" v-if="room.amenities && room.amenities.length > 0">
												<div class="col">
													<label>
														{{ 'checkout.summary.extras' | trans }}
													</label>

													{{ room.amenities.map((amenity) => amenity.short_name || amenity.name).join(', ') }}
												</div>
											</div>

											<!-- Cancellation rules -->
											<div v-if="room.cancellation_rules" class="mt-2">
												<span class="refundable" v-if="room.is_refundable !== null && room.is_refundable" v-popover="{ name: 'cancellationInfo' + room.id, event: 'hover', width: 300 }">
													<i class="wa-icon-check-circle mr-1"></i>
													{{ 'checkout.summary.refundable' | trans }}

													<i class="fa fa-info-circle ml-1"
													v-if="room.refundable_until"></i>
												</span>

												<span class="non-refundable" v-if="room.is_refundable !== null && ! room.is_refundable">
													<i class="wa-icon-money-exchange mr-1"></i>
													{{ 'checkout.summary.nonRefundable' | trans }}
												</span>
											</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<!-- Products -->
						<div class="product" v-for="product in summary.products">
							<div class="divider"></div>

							<table>
								<tbody>
									<tr>
										<td>
											<span class="quantity">
												{{ product.quantity }}x
											</span>
										</td>
										<td>
											<span class="price">
												{{ product.price | formatPrice }}
												&euro;
											</span>

											<strong class="name">
												{{ product.name }}
											</strong>
											<small class="subtitle" v-html="product.subtitle">
												{{ product.subtitle }}
											</small>
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<!-- Flights -->
						<div class="divider" v-if="summary.flights && summary.flights.length > 0"></div>
						<div class="flight" v-for="flight in summary.flights">
								<!-- Segment group -->
								<span class="segment-group"
								v-for="(segmentGroup, segmentGroupIndex) in flight.offer.segment_groups"
								:key="'current_flight_segment_group_' + segmentGroupIndex">

								<label :style="{ 'margin-top': segmentGroupIndex > 0 ? '15px' : '0' }">
									{{ 'checkout.steps.flights.segments.title.' + (segmentGroupIndex === 0 ? 'arrival' : (segmentGroupIndex === flight.offer.segment_groups.length - 1 ? 'departure' : 'connection' )) | trans }}
								</label>

									<!-- Segments -->
									<span class="segments"
									v-for="(segment, segmentIndex) in segmentGroup.segments"
									:key="'current_flight_segment_' + segmentGroupIndex + '_' + segmentIndex">
										<!-- Airline / Carrier logo -->
										<img class="carrier-logo"
										:src="$cdn.default + 'airlines/' + String(segment.carrier_code).toLowerCase() + '.png'" />

										<!-- Single segment -->
										<span class="segment">
											<!-- Airport -->
											<span class="iata-code">
												{{ segment.origin }}
											</span>

											<!-- Departure time -->
											<span class="time">
												{{ $moment(segment.departure_at).format('HH:mm') }}
											</span>
										</span>

										<!-- Icon -->
										<i class="fas fa-plane"></i>

										<!-- Single segment -->
										<span class="segment">
											<!-- Airport -->
											<span class="iata-code">
												{{ segment.destination }}
											</span>

											<!-- Arrival time -->
											<span class="time">
												{{ $moment(segment.arrival_at).format('HH:mm') }}
											</span>
										</span>
									</span>

									<!-- Meta -->
									<span class="meta">
										<!-- Duration -->
										<span class="duration">
											<!-- Days -->
											<span class="days" v-if="segmentGroup.duration.days > 0">
												{{ segmentGroup.duration.days }}
												{{ 'general.days' | choice(segmentGroup.duration.days) }}
											</span>

											<!-- Hours -->
											<span class="hours" v-if="segmentGroup.duration.hours > 0">
												{{ segmentGroup.duration.hours }}
												{{ 'general.hoursShort' | trans }}
											</span>

											<!-- Minutes -->
											<span class="minutes" v-if="segmentGroup.duration.minutes > 0">
												{{ segmentGroup.duration.minutes }}
												{{ 'general.minutesShort' | trans }}
											</span>
										</span>

										&bull;

										<!-- Stops descriptor -->
										<span class="stops-descriptor" :class="{ 'has-stops': segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0 }">
											<!-- With stops -->
											<span v-if="segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0">
												<i class="fas fa-info-circle"></i>
												{{ 'checkout.steps.flights.stopsDescriptor.stops' | choice(stopsDetails(segmentGroup).count, { count: stopsDetails(segmentGroup).count, codes: stopsDetails(segmentGroup).airports }) }}
											</span>

											<!-- Direct flight -->
											<span v-if="! (segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0)">
												{{ 'checkout.steps.flights.stopsDescriptor.direct' | trans }}
											</span>
										</span>

										<!-- Bags -->
										<span style="display: flex; flex-direction: row; gap: 20px; margin-top: 10px; color: #0E0E0E;">
											<span v-for="(flightTraveller, flightTravellerIndex) in flight.offer.price.travellers">
													<label style="color: #0E0E0E;">Gepäck ({{ flightTravellerIndex + 1 }}. Fluggast):</label>

													<!-- Small bag -->
													<span class="d-block small-bag-only" v-if="segmentGroup.segments.filter(segment => flight.dictionary[segment.operator_code].regulations && flight.dictionary[segment.operator_code].regulations.is_base_fare_small_bag_only === true).length > 0">
														- {{ 'checkout.steps.flights.smallBagOnlySummary' | trans }}
													</span>

													<!-- Bags -->
													<span v-for="segment in segmentGroup.segments">
														<span v-for="travellerSegment in flightTraveller.segments.filter((travellerSegment) => travellerSegment.id == segment.id)">
															<span class="d-block" v-for="bag in travellerSegment.bags">
																-
																{{ bag.quantity }}x
																{{ 'flights.bag_type.' + bag.bag_type | trans }}

																<!-- Route details for bag -->
																<span v-if="segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0">
																	({{ segment.origin }} - {{ segment.destination }})
																</span>
															</span>
														</span>
													</span>
											</span>
										</span>
									</span>
								</span>
						</div>

						<div class="divider"></div>

						<!-- Subtotals -->
						<div class="subtotals mb-2" v-if="'total' in summary && (summary.rooms.length > 0 || summary.activities.length > 0 || summary.products.length > 0)">
							<div v-for="subtotal in summary.subtotals" v-if="'subtotals' in summary">
								<!-- Price -->
								<span class="price" v-if="subtotal.subtotal && subtotal.subtotal !== null">
									{{ subtotal.subtotal | formatPrice }}
									&euro;
								</span>

								<!-- <i class="wa-icon-check-circle mr-1" v-if="subtotal.type === 'voucher'"></i> -->

								{{ 'checkout.summary.' + subtotal.type | trans }}

								<!-- Subtype -->
								<span v-if="subtotal.subType">
									({{ 'checkout.summary.' + subtotal.subType | trans }})
								</span>

								<!-- Voucher code -->
								<small class="d-block text-muted" v-if="subtotal.type === 'voucher' && voucherCode">
									{{ 'checkout.summary.code' | trans }}: {{ voucherCode }}
								</small>

								<span v-if="subtotal.type === 'child'">
									({{ subtotal.age }} {{ 'checkout.summary.years' | choice(subtotal.age) }})
								</span>
							</div>

							<!-- Insurance -->
							<div v-if="summary.insurance">
								<span class="price">
									{{ summary.insurance.price | formatPrice }}
									&euro;
								</span>

								{{ summary.insurance.name }}
							</div>

							<!-- Shipping -->
							<div v-if="summary.shipping && ! displayOptions.isSumShipping">
								<span class="price">
									{{ summary.shipping.price | formatPrice }}
									&euro;
								</span>

								<span v-if="summary.shipping.quantity > 1">
									{{ summary.shipping.quantity }}x
								</span>

								{{ summary.shipping.name }}
							</div>

							<!-- Shipping fee -->
							<div v-if="summary.shipping_fee && ! displayOptions.isSumShipping">
								<span class="price">
									{{ summary.shipping_fee.price | formatPrice }}
									&euro;
								</span>

								<span v-if="summary.shipping.quantity > 1">
									{{ summary.shipping.quantity }}x
								</span>

								{{ summary.shipping_fee.name }}
							</div>

							<!-- Shipping + shipping fee -->
							<div v-if="summary.shipping && summary.shipping_fee && displayOptions.isSumShipping">
								<span class="price">
									{{ summary.shipping.price + summary.shipping_fee.price | formatPrice }}
									&euro;
								</span>

								<span v-if="summary.shipping.quantity > 1">
									{{ summary.shipping.quantity }}x
								</span>

								<span v-if="shipping === 'none'">Gutschein per E-Mail</span>
								<span v-if="shipping === 'print_ticket'">Gutschein per Post</span>
								<span v-if="shipping === 'gift_box'">Deluxe Gutscheinbox</span>
							</div>

							<!-- Discount -->
							<div v-if="summary.discount">
								<span class="price">
									{{ summary.discount.discount | formatPrice }}
									&euro;
								</span>

								{{ 'checkout.summary.discountCode' | trans }}:
								{{ summary.discount.code }}
							</div>

							<!-- Value coupons -->
							<div v-for="valueCoupon in summary.value_coupons">
								<span class="price">
									{{ valueCoupon.charge * -1 | formatPrice }}
									&euro;
								</span>

								{{ 'checkout.summary.valueCoupon' | trans }}:
								{{ valueCoupon.code }}
							</div>
						</div>

						<!-- Taxes and fees -->
						<div class="divider" v-if="taxesAndFees > 0"></div>
						<div class="taxes-and-fees" v-if="taxesAndFees > 0">
							<div>
								<span class="price">
									{{ summary.total - taxesAndFees | formatPrice }}
									&euro;
								</span>

								Reisepreis
							</div>

							<div style="cursor: pointer;" v-popover="{ name: 'taxesAndFees', event: 'hover', width: 300
							 }">
								<span class="price">
									{{ taxesAndFees | formatPrice }}
									&euro;
								</span>

								Steuern und Gebühren
								<i class="far fa-info-circle ml-1" ></i>
							</div>
						</div>

						<!-- Refundable -->
								<div v-if="typeof summary.trip !== 'undefined' && summary.is_refundable !== null">
									<div class="refundable" v-if="summary.is_refundable">
										<i class="wa-icon-insurance-basic-shield"></i>
										<div>
											<strong v-if="summary.refundable_type === 'full'">
												{{ 'checkout.summary.refundable' | trans }}
											</strong>
											<strong v-if="summary.refundable_type === 'partial'">
												{{ 'checkout.summary.partlyRefundable' | trans }}
											</strong>

											<small class="d-block text-muted" v-if="summary.refundable_until">
												{{ 'bis ' + $moment(summary.refundable_until).format('DD.MM.YYYY') + ' um ' + $moment(summary.refundable_until).format('HH:mm') + ' Uhr' }}
											</small>
										</div>
									</div>
									<div class="non-refundable" v-if="! summary.is_refundable">
										<i class="wa-icon-money-exchange mr-1"></i>
										{{ 'checkout.summary.nonRefundable' | trans }}
									</div>
								</div>

						<!-- Total -->
						<div class="total" v-if="summary.total || summary.allow_zero_price">
							<span class="price">
								{{ summary.total | formatPrice }}
								&euro;
							</span>

							{{ 'checkout.summary.total' | trans }}
						</div>

						<div class="text-center mb-3" v-if="isSummaryLoading">
							<svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
								<circle class="internal-circle" cx="60" cy="60" r="30"></circle>
								<circle class="external-circle" cx="60" cy="60" r="50"></circle>
							</svg>
						</div>

						<button type="button" class="btn btn-primary btn-lg next-button" v-if="validNext === true" @click="nextStep()">
							{{ 'checkout.nextButton' | trans }}
						</button>

						<!-- Next infos -->
						<!-- Arrive date -->
						<p class="text-center text-info next-info" v-if="(arriveDate === null || departureDate === null) && productID === null">
							{{ 'checkout.nextInfos.arriveDate' | trans }}
						</p>
						<!-- Accommodation -->
						<p class="text-center text-info next-info" v-if="accommodationID === null && currentStep === 'accommodation' && productID === null">
							{{ 'checkout.nextInfos.accommodation' | trans }}
						</p>

						<!-- Fees info -->
						<div class="mt-3" v-if="feesInfo.length > 0">
							<small class="d-block text-muted" v-for="feeInfo in feesInfo">
								<i class="far fa-info-circle mr-1"></i>
								zzgl.
								{{ feeInfo.total | formatPrice }}
								&euro;
								{{ 'search.fees.types.' + feeInfo.type | trans }}
								vor Ort
							</small>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import MaskedInput from 'vue-text-mask'
	import FlightsList from './checkout/FlightsList.vue'
	import Lightbox from 'vue-pure-lightbox'
	import AddAirportInput from './checkout/AddAirportInput.vue'

	export default {
		props: [
		'mode',
		'analyticsPrefixId',
		'analyticsId',
		'analyticsBrand',
		'analyticsCategory',
		'analyticsCheckoutEvent',
		'theme',
		'termsTheme',
		'steps',
		'tripId',
		'tripPage',
		'supportRoute',
		'loadProductsEndpoint',
		'loadPersonsEndpoint',
		'checkPersonsEndpoint',
		'loadCalendarEndpoint',
		'loadActivityEndpoint',
		'loadAccommodationEndpoint',
		'checkRateEndpoint',
		'loadSummaryEndpoint',
		'loadConfirmationEndpoint',
		'bookEndpoint',
		'channelToken',
		'accommodationsCount'
		],
		components: {
			MaskedInput,
			FlightsList,
			Lightbox,
			AddAirportInput,
		},
		data: function() {
			return {
				initialSteps: [],

				// Enum
				enum: {
					steps: {
						accommodation_preselection: 'accommodation_preselection',
						product: 'product',
						calendar: 'calendar',
						activities: 'activities',
						activity: 'activity',
						accommodation: 'accommodation',
						transfer: 'transfer',
						extras: 'extras',
						additional_fields: 'additional_fields',
						confirmation: 'confirmation',
						finish: 'finish',
						flights: 'flights',
					}
				},

				// Helpers
				showDropdown: null,
				isError: false,
				isLoading: false,
				isLoadingCalendar: false,
				isSummaryLoading: false,
				calendarInfo: null,
				calendarInfoButtons: [],
				today: null,
				currentMonth: null,
				currentSeasons: [],
				initialLoads: 0,
				maxInitialLoads: 12,
				showCalendarPrices: true,
				accommodationSortingOptions: [
					'nameAsc',
					'priceAsc',
					'priceDesc',
					'distanceAsc',
				],
				accommodationSorting: 'priceAsc',
				checkoutCountries: [
					'de', 'at', 'ch', 'nl', 'fr', 'es', 'pl', 'lu', 'ru', 'dk', 'it', 'pt', 'cz', 'be', 'si',
				],
				checkoutErrors: {},
				firstPageLoad: true,
				firstAvailableFound: false,
				isVoucher: false,
				isRunningRateCheck: false,
				rateCheckData: null,

				// Steps
				stepsHistory: [],
				currentStep: null,
				arrowDownPositionLeft: 0,

				// Filters
				filters: {
					nights: null,
				},
				isChildren: false,
				isPersonsValid: true,
				isHidePersonSelection: false,
				adults: 2,
				children: [],
				tmpAdults: 2,
				tmpChildren: [],
				accommodationPreselectionData: [],
				accommodationPreselectionID: null,
				products: [],
				productID: null,
				productQuantity: 1,
				design: 'general',
				possibleNights: [],
				availabilities: [],
				arriveDate: null,
				departureDate: null,
				selectablePeriod: null,
				possibleActivities: [],
				isOptionalActivity: false,
				activityID: null,
				activityDate: null,
				activityTime: null,
				activityCategoryID: null,
				possibleAccommodations: [],
				accommodationID: null,
				accommodationRoomID: null,
				accommodationRateKey: null,
				accommodationBoardTypes: [],
				accommodationBoardType: null,
				accommodationDestinations: [],
				accommodationSelectedDestination: null,
				accommodationPackage: false,
				accommodationMember: false,
				isHideOtherAccommodations: false,
				originAirports: [],
				selectedFlight: null,
				summary: [],
				extras: null,
				additionalFields: [],
				additionalFieldOptions: null,
				additionalValues: [],
				isValidAdditionalValues: false,
				additionalFieldErrors: {},
				checkoutOptions: null,
				isPriceChangeAccepted: false,
				personalInformation: {
					salutation: 'Frau',
					firstname: null,
					lastname: null,
					email: null,
					phone: null,
					birthdate: null,
					street_house_number: null,
					additional_address: null,
					zipcode: null,
					city: null,
					country: 'de'
				},
				isOtherAccommodationGuest: false,
				accommodationGuest: {
					salutation: 'Frau',
					firstname: null,
					lastname: null
				},
				isIdenticalInvoiceAndShippingAddress: true,
				insurance: 'no',
				shipping: 'none',
				shippingVariantID: null,
				shippingAddress: {
					recipient: null,
					street_house_number: null,
					additional_address: null,
					zipcode: null,
					city: null,
					country: 'de',
				},
				discountCode: null,
				discountCodeMessage: null,
				discountCodeValid: false,
				valueCoupons: [],
				paymentMethod: null,
				isTermsOfService: false,
				isVoucherRedemptionConfirmed: false,
				isPackageConfirmed: false,
				directDebit: {
					institut: null,
					account_holder: null,
					iban: null,
					bic: null
				},
				isSameFirstTraveller: true,
				travellers: [],
				isTravellersDirty: false,

				// Display options
				displayOptions: {
					isSumShipping: false,
				},

				// Analytics
				analyticsStepsFired: [],
			}
		},
		mounted: function() {
			this.initialSteps = [...this.steps]

			// Set locale
			this.personalInformation.salutation = this.$options.filters.trans('checkout.steps.confirmation.salutationFemale')
			this.personalInformation.country = document.documentElement.lang || 'de'
			this.accommodationGuest.salutation = this.$options.filters.trans('checkout.steps.confirmation.salutationFemale')
			this.shippingAddress.country = document.documentElement.lang || 'de'

			var self = this
			window.onpopstate = (event) => {
				event.preventDefault();
				self.prevStep()
			}

			// Init params from url
			this.refreshParams();

			// Set current step
			if (! this.currentStep) {
				this.currentStep = this.steps[0];
			}

			// this.today = this.$moment().subtract(2, 'years').subtract(6, 'months');
			this.today = this.$moment();
			this.currentMonth = this.$moment((this.today.format('YYYY-MM') + '-01'), 'YYYY-MM-DD');

			// Package
			let params = this.$route.query
			if (typeof(params.package) !== 'undefined' && params.package === 'true') {
				this.accommodationPackage = true
			}
			// Member
			if (typeof(params.member) !== 'undefined' && params.member === 'true') {
				this.accommodationMember = true
			}

			// Set arrow down left position
			var self = this;
			setTimeout(function() {
				self.setArrowDownPositionLeft();
			}, 150);

			if (this.currentStep == 'calendar') {
				this.loadPersons();
			} else if (this.currentStep == 'product') {
				this.loadProducts();
			} else if (this.currentStep == 'accommodation_preselection') {
				this.loadAccommodationPreselection();
			}

			// Add step for voucher shop
			if (this.theme === 'voucher') {
				this.steps.push('finish');
			}

			// Voucher setup
			if (this.theme === 'voucher' && this.steps.indexOf('product') !== -1) {
				this.shipping = 'print_ticket';
				this.displayOptions.isSumShipping = true;
			}
		},
		computed: {
			notAvailable() {
				let availableIndex = _.findIndex(this.availabilities, { available: true })

				return (this.availabilities.length > 0 && availableIndex === -1) || (this.availabilities.length === 0 && this.initialLoads >= this.maxInitialLoads)
			},
			nights() {
				if (! this.arriveDate || ! this.departureDate) {
					return null
				}

				return this.departureDate.diff(this.arriveDate, 'days')
			},
			travelPeriod() {
				if (! this.arriveDate || ! this.departureDate) {
					return null
				}

				return this.$moment().range(this.arriveDate.clone(), this.departureDate.clone());
			},
			isAnyOnRequest: function () {
				for (var i = 0; i < this.availabilities.length; i++) {
					if (this.availabilities[i].is_on_request) return true;
				}
				return false;
			},

			currentCalendar: function() {
				let from = this.currentMonth.startOf('month').clone()
				let to = this.currentMonth.endOf('month').clone()

				// Extend to selectable period
				if (this.selectablePeriod && to.isBefore(this.selectablePeriod.end)) {
					to = this.selectablePeriod.end.clone()
				}

				// Extend to departure date
				if (this.departureDate && to.isBefore(this.departureDate)) {
					to = this.departureDate.clone()
				}

				// Build range
				let range = moment().range(from, to)

				let calendar = []
				for (let day of range.by('days')) {
					calendar.push({
						date: day.clone(),
					})
				}

				// Filter availabilities
				let availabilities = this.availabilities.filter((availability) => range.contains(availability.date))

				// Apply filters
				// Nights filter
				if (this.filters.nights) {
					availabilities = availabilities.map((availability) => {
						let price = _.find(availability.price_per_days, { days: this.filters.nights + 1 })
						return {
							...availability,
							available: availability.available && this.filters.nights + 1 >= availability.min_days && this.filters.nights + 1 <= availability.max_days,
							price: price ? price.price : availability.price,
						}
					})
				}

				calendar = calendar.map((day) => {
					let found = null

					// Try to find availability
					availabilities.forEach((availability) => {
						if (availability.date.isSame(day.date)) {
							found = availability
						}
					})

					return found || day
				})

				// Group by iso week
				return _.groupBy(calendar, function(availability) {
					return availability.date.isoWeek();
				});
			},
			// departureDate: function() {
			// 	if (this.arriveDate === null) return null;

			// 	return this.arriveDate.clone().add(this.nights, 'days');
			// },
			selectedActivity: function () {
				var activity = _.find(this.possibleActivities, { 'id': this.activityID });

				return activity !== undefined ? activity : null;
			},
			activityDates: function() {
				var activity = _.find(this.possibleActivities, { 'id': this.activityID });

				if (activity)

				// With categories
				if (activity !== undefined && activity.categories && activity.categories.length > 0) {
					var dates = _.map(activity.categories, function(category) {
						return _.map(category.dates, function(date) {
							return {
								date: date.date,
								price: date.price
							};
						});
					});

					return _.orderBy(
						_.uniqBy(_.sortBy(_.flattenDepth(dates, 1), ['price']), 'date'),
						['date'],
						['asc']
					);
				}

				// Without categories
				if (activity !== undefined && activity.dates && activity.dates.length > 0) {
					var dates = _.map(activity.dates, function(date) {
						return {
							date: date.date,
							price: date.price
						};
					});

					return _.orderBy(
						_.uniqBy(_.sortBy(dates, ['price']), 'date'),
						['date'],
						['asc']
					);
				}

				return [];
			},
			activityTimes: function() {
				var self = this;
				var activity = _.find(this.possibleActivities, { id: this.activityID })

				// With categories
				if (activity !== undefined && activity.categories && activity.categories.length > 0) {
					var times = _.map(activity.categories, function(category) {
						return _.map(category.dates, function(date) {
							if (date.date == self.activityDate) {
								return _.map(date.times, function(time) {
									return time.time;
								});
							} else {
								return [];
							}
						});
					});

					return _.uniq(_.flattenDeep(times));
				}

				// Without categories
				if (activity !== undefined && activity.dates && activity.dates.length > 0) {
					var times = _.map(activity.dates, function(date) {
						if (date.date == self.activityDate) {
							return _.map(date.times, function(time) {
								return time.time;
							});
						} else {
							return [];
						}
					});

					return _.uniq(_.flattenDeep(times));
				}

				return []
			},
			activityCategories: function() {
				var self = this;
				var activity = _.find(this.possibleActivities, { id: this.activityID })

				// Check if date and time are available
				if (activity !== undefined && activity.categories && activity.categories.length > 0) {
					var categories = _.filter(activity.categories, function(category) {
						// Check for date
						for (var d = 0; d < category.dates.length; d++) {
							if (category.dates[d].date == self.activityDate) {

								// Only check date if there are no times
								if (self.activityTimes.length === 0) {
									return true;
								} else {
									// Check for time
									for (var t = 0; t < category.dates[d].times.length; t++) {
										if (category.dates[d].times[t].time == self.activityTime) {
											return true;
										}
									}
								}
							}
						}

						return false;
					});

					categories = _.flatten(categories);
					categories = _.orderBy(categories, ['sort', 'price', 'name'], ['asc', 'desc', 'asc']);

					return categories;
				}

				return [];
			},
			ticketFee() {
				let fee = null

				var activity = _.find(this.possibleActivities, { 'id': this.activityID });

				if (activity !== undefined) {
					var category = _.find(activity.categories, { id: this.activityCategoryID })

					if (category !== undefined) {
						fee = category.fee
					}
				}

				return fee
			},
			feesInfo() {
				if (this.summary && this.summary.rooms) {
					return [].concat.apply([], this.summary.rooms.map((room) => room.fees));
				}

				return [];
			},
			taxesAndFees() {
				if (this.summary && this.summary.rooms) {
					return this.summary.rooms.map((room) => room.taxes_and_fees).reduce((a, b) => a + b, 0)
				}

				return 0.0;
			},
			validNext: function() {
				return (
					// Product
					(this.productID !== null && this.currentStep == 'product') ||

					// Calendar
					(this.arriveDate !== null && this.departureDate !== null && this.currentStep == 'calendar' && (this.steps.indexOf('flights') === -1 || (this.steps.indexOf('flights') !== -1 && this.originAirports.length > 0))) ||

					// Activities
					(this.arriveDate !== null && (this.activityID !== null || (this.activityID === null && this.isOptionalActivity)) && this.currentStep == 'activities') ||

					// Activity
					(this.arriveDate !== null && this.activityID !== null && (this.activityCategories.length === 0 || (this.activityCategories.length > 0 && this.activityCategoryID !== null)) && (this.activityDates.length === 0 || (this.activityDates.length > 0 && this.activityDate !== null)) && (this.activityTimes.length === 0 || (this.activityTimes.length > 0 && this.activityTime !== null)) && this.currentStep == 'activity') ||

					// Flight
					(this.currentStep === 'flights' && this.selectedFlight && this.isLoading === false && this.isError === false) ||

					// Extras
					(this.currentStep == 'extras') ||

					// Additional fields
					(this.currentStep == 'additional_fields') ||

					// Accommodation
					(this.arriveDate !== null && (this.accommodationID !== null && this.accommodationRoomID !== null && this.isRunningRateCheck === false && this.currentStep == 'accommodation') && (this.rateCheckData === null || (this.rateCheckData !== null && this.rateCheckData.available === true)))
					);
			},
			sortedAccommodations: function () {
				var accommodations = this.possibleAccommodations;

				switch (this.accommodationSorting) {
					case 'nameAsc':
					var fields = ['is_recommended', 'name', 'price'];
					var orders = ['desc', 'asc', 'asc'];
					break;

					case 'priceAsc':
					var fields = ['is_recommended', 'price', 'name'];
					var orders = ['desc', 'asc', 'asc'];
					break;

					case 'priceDesc':
					var fields = ['is_recommended', 'price', 'name'];
					var orders = ['desc', 'desc', 'asc'];
					break;

					case 'distanceAsc':
					var fields = ['is_recommended', 'distance', 'name', 'price'];
					var orders = ['desc', 'asc', 'asc', 'asc'];
					break;
				}

				// Filter by destination
				if (this.accommodationDestinations.length > 0 && this.accommodationSelectedDestination !== null) {
					accommodations = accommodations.filter((accommodation) => this.accommodationSelectedDestination.hotels.indexOf(accommodation.id) !== -1)
				}

				return _.orderBy(accommodations, fields, orders);
			},
			isPersonalization: function() {
				if (typeof(this.summary.activities) !== undefined) {
					for (var i = 0; i < this.summary.activities.length; i++) {
						for (var t = 0; t < this.summary.activities[i].tickets.length; t++) {
							if (this.summary.activities[i].tickets[t].is_personalization === true) {
								return true;
							}
						}
					}
				}

				if (this.insurance !== 'no') return true;

				return false;
			},
			currentFromPrice: function() {
				if (this.arriveDate !== null) {

					// Activity step
					if ((this.currentStep == 'activity' || this.currentStep == 'activities') || this.possibleActivities.length === 0) {
						for (var i = 0; i < this.availabilities.length; i++) {
							if (this.availabilities[i].date.isSame(this.arriveDate)) {
								let exactPrice = _.find(this.availabilities[i].price_per_days, {
									days: this.nights + 1,
								})
								if (exactPrice) {
									return exactPrice.price
								}

								return this.availabilities[i].price;
							}
						}
					}

					// Accommodation step
					if (this.currentStep == 'accommodation' && this.possibleActivities.length > 0) {
						for (var i = 0; i < this.activityCategories.length; i++) {
							if (this.activityCategories[i].id == this.activityCategoryID) {
								return this.activityCategories[i].price;
							}
						}
					}
				}

				return 0.0;
			},
			currentDayPrices: function() {
				if (this.arriveDate !== null) {
					let prices = {};

					for (var i = 0; i < this.availabilities.length; i++) {
						if (this.availabilities[i].date.isSame(this.arriveDate)) {
							for (var y = 0; y < this.availabilities[i].price_per_days.length; y++) {
								const currentDate = this.availabilities[i].date.clone()

								prices[currentDate.add(this.availabilities[i].price_per_days[y].days - 1, 'days').format('YYYY-MM-DD')] = this.availabilities[i].price_per_days[y].price
							}
						}
					}

					return prices
				}

				return {};
			},
			travellersRequired: function() {
				return (
					this.currentStep === this.enum.steps.confirmation &&
					(
						this.insurance !== 'no' ||
						(
							typeof(this.checkoutOptions.travellers_required) !== 'undefined' &&
							this.checkoutOptions.travellers_required === true
							)
						)
					);
			},

			// Voucher code
			voucherCode: function() {
				return $cookies.get('wa_voucherCode')
			},

			accommodationLocation: function() {
				if (this.accommodationID !== null) {
					for (var i = 0; i < this.possibleAccommodations.length; i++) {
						if (this.possibleAccommodations[i].id == this.accommodationID) {
							return this.possibleAccommodations[i].location;
						}
					}
				}

				return null;
			},

			// Shipping
			currentShippingOption: function () {
				if (this.extras === null || ! Array.isArray(this.extras.shipping_options)) return null;

				for (var i = 0; i < this.extras.shipping_options.length; i++) {
					if (this.extras.shipping_options[i].type === this.shipping) {
						return this.extras.shipping_options[i];
					}
				}

				return null;
			},
			shippingVariants: function () {
				if (this.extras === null || ! Array.isArray(this.extras.shipping_options)) return null;

				for (var i = 0; i < this.extras.shipping_options.length; i++) {
					if (this.extras.shipping_options[i].type === this.shipping) {
						return this.extras.shipping_options[i].variants;
					}
				}

				return null;
			}
		},
		watch: {
			currentStep: function() {
				// Load persons
				if (this.currentStep === this.enum.steps.calendar && this.steps[0] === this.enum.steps.accommodation_preselection && this.stepsHistory.length > 0 && this.stepsHistory[this.stepsHistory.length - 1] === this.enum.steps.accommodation_preselection) {
					this.loadPersons()
				}

				// Accommodation preselection
				if (this.currentStep === this.enum.steps.accommodation_preselection) {
					this.accommodationPreselectionID = null
					this.today = this.$moment();
					this.currentMonth = this.$moment((this.today.format('YYYY-MM') + '-01'), 'YYYY-MM-DD');
				}

				// Activities
				if (this.currentStep === this.enum.steps.activities) {
					// Reset date + time
					this.activityCategoryID = null;
					this.activityDate = null;
					this.activityTime = null;

					this.loadActivities();
				}

				// Activity
				if (this.currentStep === this.enum.steps.activity) {
					// Reset accommodation
					// BUT: only when the activity is before the hotel selection
					const accommodationIndex = this.steps.indexOf('accommodation')
					const activityIndex = this.steps.indexOf('activity')
					if (accommodationIndex !== -1 && activityIndex !== -1 && activityIndex < accommodationIndex) {
						this.accommodationID = null;
						this.accommodationRoomID = null;
					}

					this.loadActivity();
				}

				// Accommodation
				if (this.currentStep === this.enum.steps.accommodation) {
					this.loadAccommodation();
					this.loadSummary();
				}

				// Flights
				// Prevent reload when going back from 'extras' step
				if (this.currentStep === this.enum.steps.flights && this.stepsHistory[this.stepsHistory.length - 1] !== this.enum.steps.extras) {
					var self = this;
					setTimeout(() => {
						self.$refs.flightsList.loadFlights();
						self.loadSummary();
					}, 1000);
				}

				// Extras
				if (this.currentStep === this.enum.steps.extras) {
					this.loadExtras();
				}

				// Additional fields
				if (this.currentStep === this.enum.steps.additional_fields) {
					this.loadAdditionalFields();
				}

				// Confirmation
				if (this.currentStep === this.enum.steps.confirmation) {
					this.loadConfirmation();

					// Fire UG Tracking pixel
					if (typeof fbq === 'function') {
						fbq('track', 'AddToCart', {
							value: this.summary.total,
							currency: 'EUR',
							content_ids: this.tripId,
						});
					}
				}

				// If current step changes, set the arrow down left position
				var self = this;
				setTimeout(function() {
					self.setArrowDownPositionLeft();
				}, 150);

				this.refreshParams();

				// Push last step to history
				this.stepsHistory.push(this.currentStep)
			},
			accommodationPreselectionID() {
				// Reset availabilities
				this.availabilities = [];
			},
			isError: function() {
				window.scrollTo(0,0);
			},
			// '$route': function(to, from) {
			// 	this.firstPageLoad = true;
			// 	this.refreshParams();
			// },
			productID: function() {
				this.refreshParams();
				this.loadSummary();
			},
			productQuantity: function() {
				this.refreshParams();
				this.loadSummary();
			},
			currentMonth: function() {
				this.refreshParams();
			},
			adults: function () {
				this.refreshParams();
			},
			children: function () {
				this.refreshParams();
			},
			arriveDate: function () {
				this.refreshParams();

				// Reset accommodation selection when step is calendar
				// if (this.currentStep === this.enum.steps.calendar) {
				// 	this.accommodationID = null;
				// 	this.accommodationRoomID = null;
				// }
			},
			activityDate: function() {
				// Check for times
				if (this.activityTimes.length > 0) {
					this.activityTime = this.activityTimes[0];
				} else {
					this.loadSummary();
				}

				this.refreshParams();
			},
			activityTime: function() {
				this.loadSummary();
				this.refreshParams();
			},
			activityID: function() {
				this.toggleActivityStep()

				this.loadSummary();
				this.refreshParams();
			},
			activityCategoryID: function() {
				this.loadSummary();
				this.refreshParams();
			},
			accommodationRoomID: function() {
				this.rateCheckData = null;

				var room = null;
				for (var i = 0; i < this.possibleAccommodations.length; i++) {
					for (var r = 0; r < this.possibleAccommodations[i].rooms.length; r++) {
						if (this.possibleAccommodations[i].rooms[r].id == this.accommodationRoomID) {
							this.accommodationID = this.possibleAccommodations[i].id;
							room = this.possibleAccommodations[i].rooms[r];
							break;
						}
					}
				}

				// Determine if it's necessary to recheck room rate
				if (room !== null && room.is_approve_rate === true) {
					// Check to approve rate
					this.checkRate(room);
				}

				this.loadSummary();
				this.refreshParams();
			},
			accommodationID: function(value) {
				// Hide all other accommodations
				if (value !== null) this.isHideOtherAccommodations = true;

				// Jump to selection
				if (value !== null && $('div.checkout div.accommodation div.list-group').length > 0) {

					setTimeout(function() {
						$('html,body').stop().animate({
							scrollTop: $('div.checkout div.accommodation div.list-group').offset().top - 30
						}, 500, 'swing');
					}, 100);
				}

				// Check if accommodationRoomID is related to this accommodation
				for (var i = 0; i < this.possibleAccommodations.length; i++) {
					if (this.possibleAccommodations[i].id == this.accommodationID) {
						var isRelated = false;
						for (var r = 0; r < this.possibleAccommodations[i].rooms.length; r++) {
							if (this.possibleAccommodations[i].rooms[r].id == this.accommodationRoomID) {
								isRelated = true;
								break;
							}
						}

						// If not, set to first room
						if (!isRelated) {
							this.accommodationRoomID = this.possibleAccommodations[i].rooms[0].id;
						}
					}
				}
			},
			accommodationBoardType: function() {
				this.loadSummary()
				this.refreshParams();
			},
			selectedFlight: {
				handler(val) {
					this.loadSummary()
				},
				deep: true
			},
			valueCoupons: {
				handler(val) {
					this.loadSummary()
				},
				deep: true
			},
			isOtherAccommodationGuest: function() {
				setTimeout(function() {
					$('input#accommodationGuestFirstname').focus();
				}, 100);

				// Replace or insert first traveller by hotel guest
				if (this.isOtherAccommodationGuest === true) {
					this.travellers[0] = {
						...this.travellers[0],
						salutation: this.accommodationGuest.salutation,
						firstname: this.accommodationGuest.firstname,
						lastname: this.accommodationGuest.lastname,
					}
				}
			},
			insurance: function() {
				this.loadSummary();
				this.refreshParams();

				// Travellers
				this.buildTravellers();

				// Prefill first adult with first traveller
				if (this.isSameFirstTraveller === true) {
					this.travellers[0].salutation = this.personalInformation.salutation;
					this.travellers[0].firstname = this.personalInformation.firstname;
					this.travellers[0].lastname = this.personalInformation.lastname;
					this.travellers[0].birthdate = this.personalInformation.birthdate;
				}

				// Prefill first adult with accommodation guest
				if (this.isOtherAccommodationGuest === true) {
					this.travellers[0] = {
						...this.travellers[0],
						salutation: this.accommodationGuest.salutation,
						firstname: this.accommodationGuest.firstname,
						lastname: this.accommodationGuest.lastname,
					}
				}
			},
			shipping: function() {
				// Set shipping variant id
				if (this.currentShippingOption) {
					if (this.currentShippingOption.variants.length === 0) {
						this.shippingVariantID = null;
					} else {
						this.shippingVariantID = this.currentShippingOption.variants[0].id;
					}
				}

				this.loadSummary();
				this.refreshParams();
			},
			shippingVariantID: function() {
				this.loadSummary();
				this.refreshParams();
			},
			isIdenticalInvoiceAndShippingAddress: function() {
				if (this.isIdenticalInvoiceAndShippingAddress === true) {
					this.shippingAddress = {
						recipient: null,
						street_house_number: null,
						additional_address: null,
						zipcode: null,
						city: null,
						country: 'de',
					};
				}
			},
			personalInformation: {
				handler(val) {
					// Prefill first adult with first traveller
					// if (this.travellers.length > 0 && this.isTravellersDirty === false) {
					if (this.travellers.length > 0 && this.isSameFirstTraveller === true) {
						this.travellers[0].salutation = this.personalInformation.salutation;
						this.travellers[0].firstname = this.personalInformation.firstname;
						this.travellers[0].lastname = this.personalInformation.lastname;
						this.travellers[0].birthdate = this.personalInformation.birthdate;
					}
				},
				deep: true
			},
			isSameFirstTraveller: function() {
					// Prefill first adult with first traveller
					if (this.isSameFirstTraveller === true) {
						this.travellers[0].salutation = this.personalInformation.salutation;
						this.travellers[0].firstname = this.personalInformation.firstname;
						this.travellers[0].lastname = this.personalInformation.lastname;
						this.travellers[0].birthdate = this.personalInformation.birthdate;
					}
			},
			accommodationGuest: {
				handler(val) {
					// Prefill first adult with accommodation guest
					if (this.isOtherAccommodationGuest === true) {
						this.travellers[0] = {
							...this.travellers[0],
							salutation: this.accommodationGuest.salutation,
							firstname: this.accommodationGuest.firstname,
							lastname: this.accommodationGuest.lastname,
						}
					}
				},
				deep: true
			},
			filters: {
				handler(val) {
					// Automatically adjust length of stay when nights are selected
					if (this.filters.nights && this.arriveDate) {
						this.departureDate = this.arriveDate.clone().add(this.filters.nights, 'days')
					}
					if (this.filters.nights) {
						this.jumpToFirstMonth()
					}
				},
				deep: true
			},
		},
		methods: {
			stopsDetails(segmentGroup) {
                const stopSegments = [...segmentGroup.segments]
                stopSegments.pop() // Remove the final segment

                return {
                    count: segmentGroup.segments.length - 1,
                    airports: stopSegments.map(segment => segment.destination),
                }
            },

			removeAirport(airport) {
				const existingOriginAirportCodes = this.originAirports.map(airport => airport.iata_airport_code)
				const index = existingOriginAirportCodes.indexOf(airport.iata_airport_code)

				this.originAirports.splice(index, 1)
			},
			scrollDestinations(direction) {
				const scrollContainer = $('div.destination-items')
				scrollContainer.animate({
					scrollLeft: scrollContainer.scrollLeft() + (direction === 'left' ? -160 : 160)
				}, 300);
			},
			jumpToFirstMonth() {
				var firstAvailableDate = null;
				var isCurrentMonthAvailable = false;
				for (var i = 0; i < this.availabilities.length; i++) {
					if (this.isAvailable(this.availabilities[i])) {
						if (! firstAvailableDate) {
							firstAvailableDate = this.availabilities[i].date.clone()
						}
						if (this.availabilities[i].date.format('YYYY-MM') === this.currentMonth.format('YYYY-MM')) {
							isCurrentMonthAvailable = true;
						}
					}
				}

				if (! isCurrentMonthAvailable && firstAvailableDate) {
					this.currentMonth = firstAvailableDate.startOf('month').clone();
				}
			},

			isAvailable(day) {
				if (! day.available) {
					return false
				}

				// Filters
				// Nights
				if (this.filters.nights !== null && ! (this.filters.nights + 1 >= day.min_days && this.filters.nights + 1 <= day.max_days)) {
					return false
				}

				return true
			},

			// Steps
			isStepCompleted: function(step) {
				var currentIndex = this.steps.indexOf(this.currentStep);

				return currentIndex > this.steps.indexOf(step);
			},
			isStepActive: function(step) {
				var currentIndex = this.steps.indexOf(this.currentStep);

				return currentIndex === this.steps.indexOf(step);
			},
			nextStep: function() {
				// Check additional fields
				if (this.currentStep === 'additional_fields' && ! this.isValidAdditionalValues) {
					this.checkAdditionalFields()
					return;
				}

				var nextIndex = this.steps.indexOf(this.currentStep) + 1;

				if (nextIndex < this.steps.length) {
					this.currentStep = this.steps[nextIndex];
				}

				if ($(window).scrollTop() > 120) {
					setTimeout(function() {
						// Prevent bug
						if ($('nav.checkout-nav').length === 0) {
							$('html,body').stop().animate({
								scrollTop: 30,
							}, 500, 'swing');

							return;
						}

						$('html,body').stop().animate({
							scrollTop: $('nav.checkout-nav').offset().top - 30
						}, 500, 'swing');
					}, 100);
				}
			},
			prevStep: function() {
				// Back to trip
				if (this.steps[0] !== 'accommodation_preselection' && (this.currentStep === 'calendar' || this.currentStep === 'product') || (this.steps[0] === 'accommodation_preselection' && this.currentStep === 'accommodation_preselection')) {
					window.location.href = this.tripPage;

					return;
				}

				// Back to standalone page
				if (this.mode === 'standalone' && this.currentStep === 'confirmation') {
					window.history.back()

					return;
				}


				var prevIndex = this.steps.indexOf(this.currentStep) - 1;

				if (prevIndex >= 0) {
					this.currentStep = this.steps[prevIndex];
				}

				if ($(window).scrollTop() > 120) {
					setTimeout(function() {
						$('html,body').stop().animate({
							scrollTop: $('nav.checkout-nav').offset().top - 30
						}, 500, 'swing');
					}, 100);
				}
			},

			// Arrow down
			setArrowDownPositionLeft: function() {
				var activeElement = $('div.checkout nav.checkout-nav ul.steps li.is-active');

				// Prevent bug
				if (! activeElement || typeof activeElement === 'undefined' || (activeElement && activeElement.length === 0)) {
					return;
				}

				var left = activeElement.offset().left + (activeElement.width() / 2);

				this.arrowDownPositionLeft = left;
			},

			// Dropdowns
			toggleDropdown: function(dropdownName) {
				this.showDropdown = this.showDropdown === dropdownName ? null : dropdownName;
			},

			// Products
			loadProducts: function() {
				var self = this;
				this.isLoading = 'loadProducts';

				axios.get(this.loadProductsEndpoint, {
					params: {
						trip_id: this.tripId,
						voucher_code: this.voucherCode
					}
				}).then(function (response) {
					self.products = response.data.data;

					// Set first product
					if (self.productID === null) {
						self.productID = self.products[0].products[0].id;
					}

					self.isLoading = false;
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},

			// Accommodation preselection
			loadAccommodationPreselection() {
				var self = this;
				this.isLoading = 'loadAccommodationPreselection';

				axios.get(route('api.checkout.accommodationPreselection'), {
					params: {
						trip_id: this.tripId,
						voucher_code: this.voucherCode
					}
				}).then(function (response) {
					self.accommodationPreselectionData = response.data.data;

					// Set first accommodation
					if (self.accommodationPreselectionID === null) {
						self.accommodationPreselectionID = self.accommodationPreselectionData[0].id;
					}

					self.isLoading = false;
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},
			setPreselectedAccommodation(accommodation) {
				this.accommodationPreselectionID = accommodation.id

				this.nextStep()
			},

			// Persons
			loadPersons: function() {
				var self = this;
				this.isLoading = 'loadPersons';
				this.isPersonsValid = true;

				axios.get(this.loadPersonsEndpoint, {
					params: {
						trip_id: this.tripId,
						voucher_code: this.voucherCode
					}
				}).then(function (response) {
					self.isHidePersonSelection = response.data.is_hide_person_selection
					self.isChildren = response.data.data.children;
					if (response.data.data.min_selection_persons !== null && response.data.data.min_selection_persons > 2) {
						self.adults = response.data.data.min_selection_persons;
					}
					self.isLoading = false;
					self.loadFilters()
					self.loadCalendar();
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},
			increaseChildren: function() {
				this.children.push({
					id: this.children.length,
					birthdate: {
						day: null,
						month: null,
						year: null
					}
				});
				setTimeout(function() {
					$('div.checkout div.calendar div.children-age').last().find('div.birthdate input.day').focus();
				}, 200)
			},
			decreaseChildren: function() {
				this.children.pop();
			},
			calculateChildAge: function(child) {
				return this.$moment().diff(this.getChildBirthdateString(child), 'years', false);
			},
			getChildBirthdateString: function(child) {
				
				// Fix birthdate issue
				if (typeof(child.birthdate) === 'undefined' && typeof(child.year) !== 'undefined') {
					child = {birthdate: child};
				}
				
				var year = child.birthdate.year;
				var month = parseInt(child.birthdate.month, 10) < 10 ? '0' + parseInt(child.birthdate.month, 10) : '' + child.birthdate.month;
				var day = parseInt(child.birthdate.day, 10) < 10 ? '0' + parseInt(child.birthdate.day, 10) : '' + child.birthdate.day;
				var birthdateString = year + '-' + month + '-' + day;

				return birthdateString;
			},
			clearBirthdateInput: function(event) {
				event.target.value = '';
			},
			validateEnterBirthdate: function(event) {
				var value = event.target.value;
				var newValue = parseInt('' + event.target.value + event.key, 10);

				if (event.key === 'Backspace') return true;

				// Max length
				switch (event.target.className) {
					case 'day':
						if (value.length > 2 || newValue > 31) {
							event.preventDefault();
						} else if (value.length === 2) {
							event.target.value = '';
						}
						break;

					case 'month':
						if (value.length > 2 || newValue > 12) {
							event.preventDefault();
						} else if (value.length === 2) {
							event.target.value = '';
						}
						break;

					case 'year':
						if (value.length > 4) {
							event.preventDefault();
						} else if (value.length === 4) {
							event.target.value = '';
						}
						break;
				}
			},
			jumpEnterBirthdate: function(event) {
				if (event.target.className !== 'year' && event.target.value.length === 2) {
					event.target.nextElementSibling.focus();
				}
			},
			buildPersons: function() {
				var persons = [];
				persons.push({
					type: 'adult',
					birthdate: '1990-01-01',
					amount: this.adults
				});

				for (var i = 0; i < this.children.length; i++) {
					var child = this.children[i];
					persons.push({
						type: 'child',
						birthdate: this.getChildBirthdateString(child),
						amount: 1
					});
				}

				return persons;
			},
			applyPersons: function(dropdownName) {
				var self = this;
				this.isLoading = 'applyPersons';
				this.isPersonsValid = true;

				axios.post(this.checkPersonsEndpoint, {
					trip_id: this.tripId,
					persons: this.buildPersons(),
					voucher_code: this.voucherCode
				}).then(function (response) {
					self.isPersonsValid = response.data.data.valid;
					self.isLoading = false;

					// Valid persons
					if (response.data.data.valid === true) {
						self.tmpAdults = JSON.parse(JSON.stringify(self.adults));
						self.tmpChildren = JSON.parse(JSON.stringify(self.children));

						self.toggleDropdown(dropdownName);

						// Reset availabilities
						self.availabilities = []

						self.resetArriveDate();

						// Refresh calendar
						self.loadCalendar();

						// Refresh filters
						self.loadFilters()

						// Init calendar
						// if (self.availabilities.length === 0) {
						// 	self.initCalendar();
						// }
					}
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},
			togglePersonsDropdown: function(dropdownName) {
				if (this.showDropdown === dropdownName) {
					this.adults = JSON.parse(JSON.stringify(this.tmpAdults));
					this.children = JSON.parse(JSON.stringify(this.tmpChildren));
				} else {
					this.tmpAdults = JSON.parse(JSON.stringify(this.adults));
					this.tmpChildren = JSON.parse(JSON.stringify(this.children));
				}
				this.isPersonsValid = true;

				this.toggleDropdown(dropdownName);
			},

			// Filters
			loadFilters: function() {
				var self = this;
				this.isLoading = 'loadFilters';

				axios.post(route('api.checkout.filters'), {
					trip_id: this.tripId,
					persons: this.buildPersons(),
					date_from: this.currentMonth.clone().format('YYYY-MM-DD'),
					date_to: this.currentMonth.clone().add(1, 'years').format('YYYY-MM-DD'),
					voucher_code: this.voucherCode
				}).then(function (response) {
					self.possibleNights = response.data.data.nights;

					// Only set when not still 
					if (self.originAirports.length === 0) {
						self.originAirports = response.data.data.origin_airports
					}

					// Check if possible nights contains current nights
					if (self.nights !== null) {
						var currentNightsFound = false;
						for (var i = 0; i < self.possibleNights.length; i++) {
							if (self.possibleNights[i].amount == self.nights) {
								currentNightsFound = true;

								// Refresh calendar
								// self.loadCalendar();
							}
						}

						if (!currentNightsFound) self.nights = self.possibleNights[0].amount;
					} else {
						// self.nights = self.possibleNights[0].amount;
						// self.loadCalendar()
					}

					self.isLoading = false;
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},

			// Calendar
			loadCalendar: function() {
				var self = this;
				this.isLoadingCalendar = true

				axios.post(this.loadCalendarEndpoint, {
					trip_id: this.tripId,
					persons: this.buildPersons(),
					voucher_code: this.voucherCode,
					year: this.currentMonth.format('YYYY'),
					month: this.currentMonth.format('M'),
					accommodation_preselection_id: this.accommodationPreselectionID,
				}).then(function (response) {
					// Calendar
					var availabilities = response.data.data;

					// Calendar info
					self.calendarInfo = response.data.info;
					self.calendarInfoButtons = response.data.info_buttons;

					// Seasons
					var seasons = response.data.seasons;
					for (var i = 0; i < seasons.length; i++) {
						for (var k = 0; k < seasons[i].periods.length; k++) {
							seasons[i].periods[k].date_from = self.$moment(seasons[i].periods[k].date_from, 'YYYY-MM-DD');
							seasons[i].periods[k].date_to = self.$moment(seasons[i].periods[k].date_to, 'YYYY-MM-DD');
						}
					}
					self.currentSeasons = seasons;

					// Show calendar prices
					self.showCalendarPrices = ! response.data.is_hide_prices;

					// Parse dates
					for (var i = 0; i < availabilities.length; i++) {
						availabilities[i].date = self.$moment(availabilities[i].date, 'YYYY-MM-DD');
					}

					// Set first month
					var availableFound = false;
					for (var i = 0; i < availabilities.length; i++) {
						if (availabilities[i].available === true) {
							availableFound = true;
							self.firstAvailableFound = true;
							break;
						}
					}

					// Check arrive date
					var arriveDate = false;
					if (self.arriveDate !== null) {
						for (var i = 0; i < availabilities.length; i++) {
							if (availabilities[i].date.format('YYYY-MM-DD') === self.arriveDate.format('YYYY-MM-DD') && availabilities[i].available) {
								self.setArriveDate(availabilities[i]);
								arriveDate = true;
							}
						}
					}
					if (!arriveDate) self.resetArriveDate();

					self.isLoadingCalendar = false;

					if (! availableFound && ! self.firstAvailableFound) {
						if (self.initialLoads <= self.maxInitialLoads) {
							self.nextMonth();
						} else {
							self.isLoadingCalendar = false;
						}
						self.initialLoads++;
						return
					}

					let existingAvailabilities = self.availabilities
					let slicedAvailabilities = availabilities.slice()

					// Delete existing dates which are available in response
					existingAvailabilities = existingAvailabilities.filter((availability) => {
						let shouldDelete = false
						slicedAvailabilities.forEach((day) => {
							if (day.date.isSame(availability.date)) {
								shouldDelete = true
							}
						})

						return ! shouldDelete
					})

					// Update availabilities after sorting them
					self.availabilities = [...existingAvailabilities, ...slicedAvailabilities].sort((a, b) => {
						return a.date.format('YYYYMMDD') - b.date.format('YYYYMMDD')
					})

					// Update current month
					if (self.initialLoads === 0) {
						// self.jumpToFirstMonth()
					}
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},
			setArriveDate: function(day) {
				if (day.available) {
					this.arriveDate = day.date.clone();
					// this.nights = null;
					if (this.filters.nights) {
						this.departureDate = this.arriveDate.clone().add(this.filters.nights, 'days')
						this.selectablePeriod = null
					} else {
						this.departureDate = null;

						this.selectablePeriod = this.$moment().range(
							day.date.clone().add(day.min_days - 1, 'days'),
							day.date.clone().add(day.max_days - 1, 'days')
							);
					}
				}
			},
			setDepartureDate: function(day) {
				if (this.selectablePeriod.contains(day.date)) {
					this.departureDate = day.date.clone();
					this.selectablePeriod = null;
				}
			},
			resetArriveDate: function() {
				this.arriveDate = null;
				this.departureDate = null;
				this.selectablePeriod = null;

				//this.$forceUpdate();
			},
			nextMonth: function() {
				this.resetArriveDate();
				this.currentMonth = this.currentMonth.clone().add('1', 'months');

				this.checkReload()
				//this.$forceUpdate();
			},
			prevMonth: function() {
				this.resetArriveDate();
				this.currentMonth = this.currentMonth.clone().subtract('1', 'months');

				this.checkReload()
			},
			checkReload: function() {
				// Check if need to load calendar data
				let currentMonth = this.currentMonth
				let foundDates = this.availabilities.filter((day) => day.date.isSame(currentMonth, 'month'))
				if (foundDates.length == 0) {
					this.loadCalendar()
				}
			},
			initCalendar: function() {
				this.initialLoads = 0;
				this.resetArriveDate();

				this.today = this.$moment()
				this.currentMonth = this.$moment((this.today.format('YYYY-MM') + '-01'), 'YYYY-MM-DD');
				this.loadCalendar()
			},

			// Google Analytics: Measure checkout
			measureAnalytics() {
				// Check if dataLayer is defined
				if (typeof dataLayer === 'undefined') return;

				var self = this

				// Determine checkout step
				var step = null
				switch (this.currentStep) {
					case this.enum.steps.product: step = 1; break;
					case this.enum.steps.calendar: step = 1; break;
					case this.enum.steps.activities: step = 2; break;
					case this.enum.steps.activity: step = 2; break;
					case this.enum.steps.accommodation: step = 3; break;
					case this.enum.steps.flights: step = 4; break;
					case this.enum.steps.confirmation: step = 5; break;
				}

				if (!self.summary.trip) return;

				// Skip first step
				if (step === 1) return;

				// ID + Price
				let firstSubtotal = null
				for (var i = 0; i < self.summary.subtotals.length; i++) {
					if (self.summary.subtotals[i].subtotal) {
						firstSubtotal = self.summary.subtotals[i].subtotal
						break;
					}
				}
				// let gaPrice = self.summary.subtotals.length > 0 ? self.summary.subtotals[0].subtotal : self.currentFromPrice
				let gaPrice = firstSubtotal ? firstSubtotal : self.currentFromPrice
				let gaProductID = self.tripId
				let gaProductName = self.summary.trip.name
				if (self.summary.products && self.summary.products.length > 0) {
					gaProductID = self.summary.products[0].id
					gaPrice = self.summary.products[0].price
					gaProductName = self.summary.products[0].name + ' - ' + self.summary.products[0].subtitle
				}

				// Clear product name
				gaProductName = gaProductName.replace(/<\/?[^>]+(>|$)/g, "");

				// Skip for zero prices
				if (! this.voucherCode && gaPrice <= 0) return;

				// Check if step was already fired
				if (this.analyticsStepsFired.indexOf(step) !== -1) return;

				// Build data
				// Reference: https://developers.google.com/tag-manager/enhanced-ecommerce#checkout
				let data = {
					'event': this.analyticsCheckoutEvent,
					'ecommerce': {
						'checkout': {
							'actionField': {
								'step': step
							},
							'products': [{
								'name': gaProductName,
								'id': (this.analyticsPrefixId ? this.analyticsPrefixId : '') + (this.analyticsId ? this.analyticsId : gaProductID),
								'price': gaPrice,
								'revenue': gaPrice,
								'tax': 0,
								'shipping': 0,
								'quantity': self.productQuantity,
								'brand': this.analyticsBrand ? this.analyticsBrand : '',
								'category': this.analyticsCategory ? this.analyticsCategory : '',
								'quantity': 1,
								'affiliation': 'watado-trips',
							}]
						}
					}
				};

				// Clear previous ecommerce object
                dataLayer.push({
                    ecommerce: null,
                })

				// Push data
				dataLayer.push(data);

				// Push to fired steps
				this.analyticsStepsFired.push(step)

				// Log
				console.info('Analytics data pushed.', data)
			},

			// Activities
			loadActivities: function() {
				var self = this;
				this.isLoading = 'loadActivities';

				axios.post(this.loadActivityEndpoint, {
					trip_id: this.tripId,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate.format('YYYY-MM-DD'),
					departure_date: this.departureDate.format('YYYY-MM-DD'),
					voucher_code: this.voucherCode,
				}).then(function (response) {
					self.possibleActivities = response.data.data.activities;
					self.isOptionalActivity = response.data.data.options.is_optional_activity;

					// Set initial activity
					if (self.possibleActivities.length > 0 && ! response.data.data.options.is_optional_activity) {
						// Check if current activity exists
						var found = undefined;
						if (self.activityID !== null) {
							found = _.find(self.possibleActivities, { id: self.activityID })
						}

						// Not found
						if (found === undefined) {
							self.activityID = self.possibleActivities[0].id;
						}
					}

					self.toggleActivityStep()

					self.isLoading = false;
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},

			// Activity
			loadActivity: function() {
				var self = this;
				this.isLoading = 'loadActivity';

				axios.post(this.loadActivityEndpoint, {
					trip_id: this.tripId,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate.format('YYYY-MM-DD'),
					departure_date: this.departureDate.format('YYYY-MM-DD'),
					voucher_code: this.voucherCode,
					activity_id: this.activityID,
				}).then(function (response) {
					self.possibleActivities = response.data.data.activities;

					// Set first category, date and time
					for (var i = 0; i < self.possibleActivities.length; i++) {
						if (i === 0) {
							// Activity
							var activity = self.possibleActivities[0];
							self.activityID = activity.id;

							// With categories
							if (activity.categories.length > 0) {
								// Find cheapest category
								var categoryIndex = 0;
								for (var c = 0; c < activity.categories.length; c++) {
									if (activity.categories[c].price < activity.categories[categoryIndex].price) {
										categoryIndex = c;
									}
								}
								self.activityCategoryID = activity.categories[categoryIndex].id;

								// Dates
								if (activity.categories[categoryIndex].dates.length > 0) {
									// Find cheapest date
									var dateIndex = 0;
									for (var d = 0; d < activity.categories[categoryIndex].dates.length; d++) {
										if (activity.categories[categoryIndex].dates[d].price < activity.categories[categoryIndex].dates[dateIndex].price) {
											dateIndex = d;
										}
									}

									self.activityDate = activity.categories[categoryIndex].dates[dateIndex].date;

									// Times
									if (activity.categories[categoryIndex].dates[dateIndex].times.length > 0) {
										// Find cheapest time
										var timeIndex = 0;
										for (var t = 0; t < activity.categories[categoryIndex].dates[dateIndex].times.length; t++) {
											if (activity.categories[categoryIndex].dates[dateIndex].times[t].price < activity.categories[categoryIndex].dates[dateIndex].times[timeIndex].price) {
												timeIndex = t;
											}
										}

										self.activityTime = activity.categories[categoryIndex].dates[dateIndex].times[timeIndex].time;
									}
								}
							}

							// Without categories
							if (activity.dates && activity.dates.length > 0) {
								// Dates
								if (activity.dates.length > 0) {
									// Find cheapest date
									var dateIndex = 0;
									for (var d = 0; d < activity.dates.length; d++) {
										if (activity.dates[d].price < activity.dates[dateIndex].price) {
											dateIndex = d;
										}
									}

									self.activityDate = activity.dates[dateIndex].date;

									// Times
									if (activity.dates[dateIndex].times.length > 0) {
										// Find cheapest time
										var timeIndex = 0;
										for (var t = 0; t < activity.dates[dateIndex].times.length; t++) {
											if (activity.dates[dateIndex].times[t].price < activity.dates[dateIndex].times[timeIndex].price) {
												timeIndex = t;
											}
										}

										self.activityTime = activity.dates[dateIndex].times[timeIndex].time;
									}
								}
							}
						}
					}

					self.isLoading = false;
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},

			priceForCategory: function(category) {
				for (var i = 0; i < category.dates.length; i++) {
					if (category.dates[i].date == this.activityDate) {
						// Check for times
						for (var t = 0; t < category.dates[i].times.length; t++) {
							if (category.dates[i].times[t].time == this.activityTime) {
								return category.dates[i].times[t].price >= 0 ? category.dates[i].times[t].price : 0;
							}
						}

						return category.dates[i].price >= 0 ? category.dates[i].price : 0;
					}
				}
			},

			// Accommodation
			loadAccommodation: function() {
				var self = this;
				this.isLoading = 'loadAccommodation';
				self.possibleAccommodations = [];
				self.isHideOtherAccommodations = false;

				if (! this.arriveDate || ! this.departureDate) {
					return;
				}

				axios.post(this.loadAccommodationEndpoint, {
					channel_token: this.channelToken,
					trip_id: this.tripId,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate.format('YYYY-MM-DD'),
					departure_date: this.departureDate.format('YYYY-MM-DD'),
					activity_id: this.activityID,
					activity_category_id: this.activityCategoryID,
					activity_date: this.activityDate,
					activity_time: this.activityTime,
					voucher_code: this.voucherCode,
					accommodation_preselection_id: this.accommodationPreselectionID,
				}).then(function (response) {
					// Board types
					self.accommodationBoardTypes = response.data.data.board_types;
					var found = false;
					if (self.accommodationBoardType) {
						for (var i = 0; i < self.accommodationBoardTypes.length; i++) {
							if (self.accommodationBoardTypes[i].id === self.accommodationBoardType.id) {
								found = true;
							}
						}
					}
					if (! found && self.accommodationBoardTypes.length > 0) {
						self.accommodationBoardType = null;
					}

					// Destinations
					self.accommodationDestinations = response.data.data.destinations;

					// Accommodations
					for (var i = 0; i < response.data.data.accommodations.length; i++) {
						// Calculate distance
						if (self.possibleActivities.length > 0) {
							response.data.data.accommodations[i]['distance'] = self.distanceInKm(response.data.data.accommodations[i].location, self.possibleActivities[0].location);
						}

						self.possibleAccommodations.push(response.data.data.accommodations[i]);
					}

					// Clear "accommodationID" and "accommodationRoomID" if not found
					var accommodationFound = false;
					var accommodationRoomFound = false;
					for (var i = 0; i < self.possibleAccommodations.length; i++) {
						if (self.possibleAccommodations[i].id === self.accommodationID) {
							accommodationFound = true;

							for (var r = 0; r < self.possibleAccommodations[i].rooms.length; r++) {
								if (self.possibleAccommodations[i].rooms[r].id === self.accommodationRoomID && ((this.accommodationBoardType && self.possibleAccommodations[i].rooms[r].available_board_types.indexOf(this.accommodationBoardType.id) !== -1) || ! this.accommodationBoardType)) {
									accommodationRoomFound = true;
								}
							}
						}
					}

					if (!accommodationFound) {
						self.accommodationID = null;
						self.accommodationRoomID = null;
					}
					if (!accommodationRoomFound) {
						self.accommodationRoomID = null;
					}

					if (response.data.has_backfill === false) {
						self.isLoading = false;
					}
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},
			setAccommodationSorting: function (option) {
				this.accommodationSorting = option;
			},
			setAccommodationBoardType: function (boardType) {
				this.accommodationBoardType = boardType;
			},
			showAccommodationDetails: function (target) {
				$(target).modal('show');
			},
			accommodationHasBoardType: function (accommodation) {
				return true;

				if (this.accommodationBoardType) {
					var found = false;
					for (var i = 0; i < accommodation.rooms.length; i++) {
						if (accommodation.rooms[i].available_board_types.indexOf(this.accommodationBoardType.id) >= 0) {
							found = true;
						}
					}

					if (! found) {
						return false;
					}
				}

				return true;
			},
			checkRate: function(room) {
				this.isRunningRateCheck = true;
				this.rateCheckData = null;

				var self = this;

				axios.post(this.checkRateEndpoint, {
					trip_id: this.tripId,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate !== null ? this.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.departureDate !== null ? this.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.activityID,
					activity_category_id: this.activityCategoryID,
					activity_date: this.activityDate,
					activity_time: this.activityTime,
					accommodation_id: this.accommodationID,
					accommodation_room_id: this.accommodationRoomID,
					accommodation_package: this.accommodationPackage,
					accommodation_member: this.accommodationMember,
					insurance: this.insurance,
					voucher_code: this.voucherCode,
					current_price: room.price,
				}).then(function (response) {
					self.rateCheckData = response.data.data;

					// Update room price
					room.price = response.data.data.per_person_price;

					self.isRunningRateCheck = false;
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isRunningRateCheck = false;
				});
			},

			handleAddOriginAirport(airport) {
				// Only add when the airport doesn't exist
				if (this.originAirports.map(item => item.id).indexOf(airport.id) === -1) {
					this.originAirports.push(airport)
				}
			},

			// Summary
			loadSummary: function () {
				var self = this;
				this.isSummaryLoading = true;

				// Check if this checkout has the step "flights"
				const hasFlightsStep = this.steps.indexOf('flights') !== -1;
				const isFlightOfferRequired = ['flights', 'confirmation', 'extras', 'additional_fields'].indexOf(this.currentStep) !== -1;
				if (hasFlightsStep && isFlightOfferRequired && !!(this.selectedFlight?.token || null) === false) {
					this.isSummaryLoading = false;
					return;
				}

				axios.post(this.loadSummaryEndpoint, {
					trip_id: this.tripId,
					step: this.currentStep,
					product_id: this.productID,
					quantity: this.productQuantity,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate !== null ? this.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.departureDate !== null ? this.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.activityID,
					activity_category_id: this.activityCategoryID,
					activity_date: this.activityDate,
					activity_time: this.activityTime,
					accommodation_id: this.accommodationID,
					accommodation_room_id: this.accommodationRoomID,
					accommodation_rate_key: this.accommodationRateKey,
					accommodation_package: this.accommodationPackage,
					accommodation_member: this.accommodationMember,
					accommodation_board_type_id: this.accommodationBoardType ? this.accommodationBoardType.id : null,
					flight_offer_token: this.selectedFlight?.token || null,
					insurance: this.insurance,
					shipping: this.shipping,
					shipping_variant_id: this.shippingVariantID,
					voucher_code: this.voucherCode,
					discount_code: this.discountCode,
					value_coupons: this.valueCoupons.map((coupon) => coupon.code),
				}).then(function (response) {
					var oldSummary = Object.assign({}, self.summary);

					self.summary = response.data.data;

					// Grab automatically applied discount code
					if (response.data.data.discount && response.data.data.discount.code) {
						self.discountCode = response.data.data.discount.code;
					}

					// Reload confirmation page on voucher redemptions
					if (self.currentStep === 'confirmation') {
						self.loadConfirmation();
					}

					// Is voucher
					if (typeof(response.data.data.voucher) !== 'undefined') {
						self.isVoucher = true;
					}

					self.isSummaryLoading = false;

					// Measure analytics
					self.measureAnalytics();

				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isSummaryLoading = false;
				});
			},

			// Load extras
			loadExtras() {
				var self = this;
				this.isLoading = 'loadExtras';

				axios.post(route('api.checkout.extras'), {
					trip_id: this.tripId,
					step: this.currentStep,
					product_id: this.productID,
					quantity: this.productQuantity,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate !== null ? this.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.departureDate !== null ? this.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.activityID,
					activity_category_id: this.activityCategoryID,
					activity_date: this.activityDate,
					activity_time: this.activityTime,
					accommodation_id: this.accommodationID,
					accommodation_room_id: this.accommodationRoomID,
					insurance: this.insurance,
					shipping: this.shipping,
					shipping_variant_id: this.shippingVariantID,
					voucher_code: this.voucherCode,
					discount_code: this.discountCode,
				}).then(function (response) {
					self.extras = response.data.data;

					// Set shipping variant id
					if (self.currentShippingOption) {
						if (self.currentShippingOption.variants.length === 0) {
							self.shippingVariantID = null;
						} else {
							self.shippingVariantID = self.currentShippingOption.variants[0].id;
						}
					}

					// Default shipping
					var foundShippingOption = false;
					if (! (self.extras === null || ! Array.isArray(self.extras.shipping_options))) {
						for (var i = 0; i < self.extras.shipping_options.length; i++) {
							if (self.extras.shipping_options[i].type === self.shipping) {
								foundShippingOption = true;
							}
						}
					}
					if (!foundShippingOption) {
						self.shipping = 'none';
					}

					self.isLoading = false;

				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},

			// Load additional fields
			loadAdditionalFields() {
				var self = this;
				this.isLoading = 'loadAdditionalFields';

				axios.post(route('api.checkout.additionalFields'), {
					trip_id: this.tripId,
				}).then(function (response) {
					self.additionalFields = response.data.data;
					self.additionalFieldOptions = response.data.options;

					// Non multiple
					if (response.data.options.is_multiple === false) {

						// Define additional fields
						let additionalFields = {}

						self.additionalFields.forEach(additionalField => {
							let defaultValue = null

							// Define default value
							switch (additionalField.type) {
								case 'text': defaultValue = ''; break;
								case 'select': defaultValue = additionalField.values[0].value; break;
								case 'boolean': defaultValue = false; break;
							}

							additionalFields[additionalField.name] = defaultValue
						})

						// Set default value
						self.additionalValues = [Object.assign({}, additionalFields)]
					}

					self.isLoading = false;

				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},

			// Check additional fields
			checkAdditionalFields() {
				var self = this;

				this.isValidAdditionalValues = false

				axios.post(route('api.checkout.checkAdditionalFields'), {
					trip_id: this.tripId,
					additional_values: this.additionalValues,
				}).then(function (response) {
					self.additionalFieldErrors = {}
				    self.isValidAdditionalValues = true
				    self.nextStep()

				    setTimeout(function () {
				    	self.isValidAdditionalValues = false
				    }, 300)
				}).catch(function (error) {
				    self.additionalFieldErrors = error.response.data.errors;
					self.completeAdditionalFieldErrors();

					// Scroll top
					setTimeout(function() {
						$('html,body').stop().animate({
							scrollTop: $('div.additional_fields').offset().top - 30
						}, 500, 'swing');
					}, 250);
				});
			},

			// Confirmation
			loadConfirmation: function () {
				var self = this;
				this.isLoading = 'loadConfirmation';
				this.isPriceChangeAccepted = false;

				axios.post(this.loadConfirmationEndpoint, {
					trip_id: this.tripId,
					step: this.currentStep,
					product_id: this.productID,
					quantity: this.productQuantity,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate !== null ? this.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.departureDate !== null ? this.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.activityID,
					activity_category_id: this.activityCategoryID,
					activity_date: this.activityDate,
					activity_time: this.activityTime,
					accommodation_id: this.accommodationID,
					accommodation_room_id: this.accommodationRoomID,
					accommodation_rate_key: this.accommodationRateKey,
					accommodation_package: this.accommodationPackage,
					accommodation_member: this.accommodationMember,
					insurance: this.insurance,
					shipping: this.shipping,
					shipping_variant_id: this.shippingVariantID,
					voucher_code: this.voucherCode,
					discount_code: this.discountCode,
				}).then(function (response) {
					self.checkoutOptions = response.data.data;

					// Salutation fix
					if (self.checkoutOptions.is_product) {
						self.personalInformation.salutation = null;
					}

					// Travellers
					self.buildTravellers();

					// Payment methods
					if (! self.checkoutOptions.payment_methods.includes(self.paymentMethod)) {
						self.paymentMethod = self.checkoutOptions.payment_methods[0];
					}

					self.isLoading = false;

					// Measure analytics
					self.measureAnalytics();

				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.isLoading = false;
				});
			},

			book: function () {
				// Demo
				if (this.checkoutOptions.is_demo) {
					$('div#demo-checkout-modal').modal('show');
					return;
				}

				var self = this;
				this.isLoading = 'book';

				// Fix: Replace or insert first traveller with hotel guest
				var _travellers = this.travellers.slice();
				if (this.isOtherAccommodationGuest) {
					_travellers[0] = {
						..._travellers[0],
						salutation: this.accommodationGuest.salutation,
						firstname: this.accommodationGuest.firstname,
						lastname: this.accommodationGuest.lastname,
					}

					// Check for filled birthdate
					if (_travellers.length > 0 && typeof _travellers[0].birthdate === 'undefined') {
						_travellers[0] = {
							..._travellers[0],
							birthdate: this.personalInformation.birthdate,
						}
					}
				}

				// Book
				axios.post(this.bookEndpoint, {
					channel_token: this.channelToken,
					token: this.checkoutOptions.token,
					trip_id: this.tripId,
					product_id: this.productID,
					quantity: this.productQuantity,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate !== null ? this.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.departureDate !== null ? this.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.activityID,
					activity_category_id: this.activityCategoryID,
					activity_date: this.activityDate,
					activity_time: this.activityTime,
					accommodation_id: this.accommodationID,
					accommodation_room_id: this.accommodationRoomID,
					accommodation_rate_key: this.accommodationRateKey,
					accommodation_package: this.accommodationPackage,
					accommodation_member: this.accommodationMember,
					flight_offer_token: this.selectedFlight?.token || null,
					personal_information: this.personalInformation,
					is_other_accommodation_guest: this.isOtherAccommodationGuest,
					accommodation_guest: this.accommodationGuest,
					insurance: this.insurance,
					shipping: this.shipping,
					shipping_variant_id: this.shippingVariantID,
					is_different_shipping_address: ! this.isIdenticalInvoiceAndShippingAddress,
					shipping_address: this.shippingAddress,
					payment_method: this.paymentMethod,
					is_terms_of_service: this.isTermsOfService,
					is_confirm_voucher_redemption: this.isVoucherRedemptionConfirmed,
					is_package_confirmed: this.isPackageConfirmed,
					direct_debit: this.directDebit,
					travellers: _travellers,
					voucher_code: this.voucherCode,
					discount_code: this.discountCode,
					value_coupons: this.valueCoupons.map((coupon) => coupon.code),
					additional_values: this.additionalValues,
					design: this.design,
				}).then(function (response) {
					self.checkoutErrors = {};

					// Redirect user
					if (response.data.success === true) {
						window.location.href = response.data.redirect_url;
					}

					// self.isLoading = false;
				}).catch(function (error) {
					self.checkoutErrors = error.response.data.errors;
					self.completeCheckoutErrors();

					if ($(window).scrollTop() > 120) {
						setTimeout(function() {
							$('html,body').stop().animate({
								scrollTop: $('nav.checkout-nav').offset().top - 30
							}, 500, 'swing');
						}, 100);
					}

					self.isLoading = false;
				});
			},

			randomInteger: function(min, max) {
				return Math.floor(Math.random() * (max - min) ) + min;
			},
			distanceInKm: function(location1, location2) {
				var R = 6371;
				var dLat = this.deg2rad(location2.latitude - location1.latitude);
				var dLon = this.deg2rad(location2.longitude - location1.longitude);
				var a =
				Math.sin(dLat/2) * Math.sin(dLat/2) +
				Math.cos(this.deg2rad(location1.latitude)) * Math.cos(this.deg2rad(location2.latitude)) *
				Math.sin(dLon/2) * Math.sin(dLon/2)
				;
				var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
				var d = R * c;
				return d.toFixed(1);
			},
			deg2rad: function (deg) {
				return deg * (Math.PI/180)
			},

			refreshParams: function () {
				var self = this
				let params = this.$route.query

				// Product
				if (this.$route.query.product === undefined) {
					if (this.productID !== null) {
						Object.assign({}, params, {
							product: this.productID,
						})
					}
				} else {
					if (this.firstPageLoad) {
						this.productID = this.$route.query.product;
					} else {
						Object.assign({}, params, {
							product: this.product,
						})
					}
				}

				// Quantity
				if (this.$route.query.quantity === undefined) {
					if (this.productQuantity !== null && this.productID !== null) {
						Object.assign({}, params, {
							quantity: this.productQuantity,
						})
					}
				} else {
					if (this.firstPageLoad) {
						this.productQuantity = this.$route.query.quantity;
					} else {
						Object.assign({}, params, {
							quantity: this.productQuantity,
						})
					}
				}

				// Step
				if (this.$route.query.step === undefined) {
					if (this.productQuantity !== null && this.productID !== null) {
						Object.assign({}, params, {
							step: this.currentStep,
						})
					}
				} else {
					if (this.firstPageLoad) {
						this.currentStep = this.$route.query.step;
					} else {
						Object.assign({}, params, {
							step: this.currentStep,
						})
					}
				}

				// Arrive date + Departure date
				if (this.$route.query.arrive_date === undefined && this.$route.query.departure_date === undefined) {
					if (this.arriveDate !== null && this.departureDate !== null) {
						this.$router.push({
							query: Object.assign({}, this.$route.query, {
								arrive_date: this.arriveDate.format('YYYY-MM-DD'),
								departure_date: this.departureDate.format('YYYY-MM-DD'),
							})
						});
					}
				} else if (this.$route.query.arrive_date !== undefined && this.$route.query.departure_date !== undefined) {
					if (this.firstPageLoad) {
						this.arriveDate = this.$moment(this.$route.query.arrive_date, 'YYYY-MM-DD');
						this.departureDate = this.$moment(this.$route.query.departure_date, 'YYYY-MM-DD');
					} else {
						if (this.arriveDate !== null && this.departureDate !== null) {
							this.$router.push({
								query: Object.assign({}, this.$route.query, {
									arrive_date: this.arriveDate.format('YYYY-MM-DD'),
									departure_date: this.departureDate.format('YYYY-MM-DD'),
								})
							});
						}
					}
				}

				// Adults
				if (this.$route.query.adults === undefined) {
					this.$router.push({
						query: Object.assign({}, this.$route.query, { adults: this.adults })
					});
				} else {
					if (this.firstPageLoad) {
						this.adults = parseInt(this.$route.query.adults, 10);
					} else {
						this.$router.push({
							query: Object.assign({}, this.$route.query, { adults: this.adults })
						});
					}
				}

				// Children
				if (this.$route.query.children === undefined) {
					if (this.children.length > 0) {
						this.$router.push({
							query: Object.assign({}, this.$route.query, {
								children: this.children.map((child) => self.getChildBirthdateString(child)).join(',')
							})
						});
					}
				} else {
					if (this.firstPageLoad) {
						this.children = this.$route.query.children.split(',').map((child) => ({
							birthdate: {
								day: moment(child).format('DD'),
								month: moment(child).format('MM'),
								year: moment(child).format('YYYY'),
							},
						}));
					} else {
						if (this.children.length > 0) {
							this.$router.push({
								query: Object.assign({}, this.$route.query, {
									children: this.children.map((child) => self.getChildBirthdateString(child)).join(',')
								})
							});
						}
					}
				}
				/*
				// Nights
				if (this.$route.query.nights === undefined) {
					if (this.nights !== null) {
						this.$router.push({
							query: Object.assign({}, this.$route.query, { nights: this.nights })
						});
					}
				} else {
					if (this.firstPageLoad) {
						this.nights = this.$route.query.nights;
					} else {
						if (this.nights !== null) {
							this.$router.push({
								query: Object.assign({}, this.$route.query, { nights: this.nights })
							});
						}
					}
				}

				// Current month
				if (this.$route.query.calendar_month === undefined) {
					if (this.currentMonth !== null) {
						this.$router.push({
							query: Object.assign({}, this.$route.query, { calendar_month: this.currentMonth.format('YYYY-MM-DD') })
						});
					}
				} else {
					if (this.firstPageLoad) {
						this.currentMonth = this.$moment(this.$route.query.calendar_month, 'YYYY-MM-DD');
					} else {
						if (this.currentMonth !== null) {
							this.$router.push({
								query: Object.assign({}, this.$route.query, { calendar_month: this.currentMonth.format('YYYY-MM-DD') })
							});
						}
					}
				}

				// Activity date
				if (this.$route.query.activity_date === undefined) {
					if (this.activityDate !== null) {
						this.$router.push({
							query: Object.assign({}, this.$route.query, { activity_date: this.activityDate })
						});
					}
				} else {
					if (this.firstPageLoad) {
						this.activityDate = this.$route.query.activity_date;
					} else {
						if (this.activityDate !== null) {
							this.$router.push({
								query: Object.assign({}, this.$route.query, { activity_date: this.activityDate })
							});
						}
					}
				}

				// Activity time
				if (this.$route.query.activity_time === undefined) {
					if (this.activityTime !== null) {
						this.$router.push({
							query: Object.assign({}, this.$route.query, { activity_time: this.activityTime })
						});
					}
				} else {
					if (this.firstPageLoad) {
						this.activityTime = this.$route.query.activity_time;
					} else {
						if (this.activityTime !== null) {
							this.$router.push({
								query: Object.assign({}, this.$route.query, { activity_time: this.activityTime })
							});
						}
					}
				}

				// Activity category id
				if (this.$route.query.activity_category_id === undefined) {
					if (this.activityCategoryID !== null) {
						this.$router.push({
							query: Object.assign({}, this.$route.query, { activity_category_id: this.activityCategoryID })
						});
					}
				} else {
					if (this.firstPageLoad) {
						this.activityCategoryID = this.$route.query.activity_category_id;
					} else {
						if (this.activityCategoryID !== null) {
							this.$router.push({
								query: Object.assign({}, this.$route.query, { activity_category_id: this.activityCategoryID })
							});
						}
					}
				}
				*/

				// Accommodation id
				if (this.$route.query.accommodation_id === undefined) {
					if (this.accommodationID !== null) {
						Object.assign({}, params, {
							accommodation_id: this.accommodationID,
						})
					}
				} else {
					if (this.firstPageLoad) {
						this.accommodationID = this.$route.query.accommodation_id;
					} else {
						if (this.accommodationID !== null) {
							Object.assign({}, params, {
								accommodation_id: this.accommodationID,
							})
						}
					}
				}

				// Accommodation room id
				if (this.$route.query.accommodation_room_id === undefined) {
					if (this.accommodationRoomID !== null) {
						Object.assign({}, params, {
							accommodation_room_id: this.accommodationRoomID,
						})
					}
				} else {
					if (this.firstPageLoad) {
						this.accommodationRoomID = this.$route.query.accommodation_room_id;
					} else {
						if (this.accommodationRoomID !== null) {
							Object.assign({}, params, {
								accommodation_room_id: this.accommodationRoomID,
							})
						}
					}
				}

				// Accommodation rate key
				if (this.$route.query.accommodation_rate_key === undefined) {
					if (this.accommodationRateKey !== null) {
						Object.assign({}, params, {
							accommodation_rate_key: this.accommodationRateKey,
						})
					}
				} else {
					if (this.firstPageLoad) {
						this.accommodationRateKey = this.$route.query.accommodation_rate_key;
					} else {
						if (this.accommodationRateKey !== null) {
							Object.assign({}, params, {
								accommodation_rate_key: this.accommodationRateKey,
							})
						}
					}
				}

				this.$router.push({
					query: params,
				}).catch(err => {
					if (err.name !== 'NavigationDuplicated' && ! err.message.includes('Avoided redundant navigation to current location')) {
						// console.log(err);
					}
				});

				this.firstPageLoad = false;
			},

			// Build travellers
			buildTravellers: function() {
				var oldTravellers = this.travellers.slice(); // Use slice to make a copy without reference

				// Filter old empty travellers
				oldTravellers = oldTravellers.filter(person => person.firstname && person.lastname)

				// Build travellers
				this.travellers = [];
				if (this.travellersRequired) {
					// Adults
					for (var i = 0; i < this.adults; i++) {
						this.travellers.push({
							type: 'adult',
							salutation: this.$options.filters.trans('checkout.steps.confirmation.salutationFemale'),
							firstname: null,
							lastname: null,
							birthdate: null
						});
					}

					// Children
					for (var i = 0; i < this.children.length; i++) {
						var birthdate = this.children[i].birthdate;

						this.travellers.push({
							type: 'child',
							salutation: this.$options.filters.trans('checkout.steps.confirmation.salutationFemale'),
							firstname: null,
							lastname: null,
							birthdate: birthdate.day + '.' + birthdate.month + '.' + birthdate.year
						});
					}
				}

				// Additional fields
				if (this.checkoutOptions.additional_fields.length > 0) {
					// Define additional fields
					let additionalFields = {}

					this.checkoutOptions.additional_fields.forEach(additionalField => {
						let defaultValue = null

						// Define default value
						switch (additionalField.type) {
							case 'text': defaultValue = ''; break;
							case 'select': defaultValue = additionalField.values[0].value; break;
							case 'boolean': defaultValue = false; break;
						}

						additionalFields[additionalField.name] = defaultValue
					})

					this.travellers = this.travellers.map((traveller) => {
						return {
							...traveller,
							additional_fields: Object.assign({}, additionalFields),
						}
					})
				}

				// Replace or insert first traveller by hotel guest
				if (this.isOtherAccommodationGuest === true) {
					this.travellers[0] = {
						...this.travellers[0],
						salutation: this.accommodationGuest.salutation,
						firstname: this.accommodationGuest.firstname,
						lastname: this.accommodationGuest.lastname,
					}
				}

				// Merge with old travellers
				this.travellers = _.merge(this.travellers, oldTravellers);
			},

			// Mobile summary
			toggleMobileSummary: function() {
				$('div.checkout').toggleClass('show-summary');
			},

			// Check discount code
			checkDiscountCode: function(code) {
				var self = this;

				if (code) {
					this.discountCode = code
				}

				axios.post(route('api.checkout.checkDiscountCode'), {
					trip_id: this.tripId,
					step: this.currentStep,
					product_id: this.productID,
					quantity: this.productQuantity,
					persons: this.buildPersons(),
					arrive_date: this.arriveDate !== null ? this.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.departureDate !== null ? this.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.activityID,
					activity_category_id: this.activityCategoryID,
					activity_date: this.activityDate,
					activity_time: this.activityTime,
					accommodation_id: this.accommodationID,
					accommodation_room_id: this.accommodationRoomID,
					insurance: this.insurance,
					shipping: this.shipping,
					voucher_code: this.voucherCode,
					discount_code: this.discountCode,
				}).then(function (response) {
					self.discountCodeMessage = response.data.message;
					self.discountCodeValid = response.data.success;

					// Add value coupon
					if (response.data.type === 'value_coupon' && response.data.data && response.data.data.balance > 0) {
						// Check if already exists
						var found = _.findIndex(self.valueCoupons, { id: response.data.data.id })
						if (found === -1) {
							// Push voucher and set active
							self.valueCoupons.push({
								...response.data.data,
							});

							// Hide discount form
							setTimeout(() => {
								self.discountCode = null
							}, 1000)
						}
					}

					setTimeout(() => {
						self.loadSummary();
					}, 1000);
				}).catch(function (error) {
				    console.log(error);
				    self.$toast.error('Error. Please check the console.');
				});
			},

			// Toggle activity steps
			toggleActivityStep() {
				let index = this.steps.indexOf('activity')
				let activitiesIndex = this.steps.indexOf('activities')

				// Toggle steps
				if (this.activityID === null && this.isOptionalActivity && index !== -1) {
					this.steps.splice(index, 1)
				} else if (this.activityID !== null && this.isOptionalActivity && this.initialSteps.indexOf('activity') !== -1 && index === -1) {
					this.steps.splice(activitiesIndex + 1, 0, 'activity')
				}
			},

			// Checks if there are errors for additional fields and corrects
			// the error message with the label of that additional field.
			completeCheckoutErrors() {
				if (this.checkoutOptions && this.checkoutOptions.additional_fields && this.checkoutOptions.additional_fields.length > 0 && Object.keys(this.checkoutErrors).length > 0) {
					Object.keys(this.checkoutErrors).forEach(key => {
						this.checkoutOptions.additional_fields.forEach(additionalField => {
							let regex = new RegExp('travellers.\\d*.additional_fields.' + additionalField.name, 'gi')

							if (regex.test(key)) {
								this.checkoutErrors[key]['0'] = String(this.checkoutErrors[key]['0']).replace(regex, additionalField.label)
							}
						})
					})
				}
			},

			addAdditionalValue() {
				// Define additional fields
				let additionalFields = {}

				this.additionalFields.forEach(additionalField => {
					let defaultValue = null

					// Define default value
					switch (additionalField.type) {
						case 'text': defaultValue = ''; break;
						case 'select': defaultValue = additionalField.values[0].value; break;
						case 'boolean': defaultValue = false; break;
					}

					additionalFields[additionalField.name] = defaultValue
				})

				// Set default value
				this.additionalValues.push(Object.assign({}, additionalFields))
			},
			removeAdditionalValue(index) {
				this.additionalValues.splice(index, 1)
			},

			// Checks if there are errors for additional fields and corrects
			// the error message with the label of that additional field.
			completeAdditionalFieldErrors() {
				if (this.additionalFields && this.additionalFields.length > 0 && this.additionalFieldErrors && Object.keys(this.additionalFieldErrors).length > 0) {
					Object.keys(this.additionalFieldErrors).forEach(key => {
						this.additionalFields.forEach(additionalField => {
							let regex = new RegExp('additional_values.\\d*.' + additionalField.name, 'gi')

							if (regex.test(key)) {
								this.additionalFieldErrors[key]['0'] = String(this.additionalFieldErrors[key]['0']).replace(regex, additionalField.label)
							}
						})
					})
				}
			},
		}
	}
</script>
<style>
	.lightbox {
		z-index: 99;
	}
	.lightbox__image img {
	    width: 100%;
	    height: auto!important;
	    max-width: 600px;
	    margin: 0 auto;
	    display: block;
	}

	a.lightbox__thumbnail img {
		max-width: 200px !important;
	}
</style>
